import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useSwapScroll } from './swap-scroll'
import { Tooltip } from './Tooltip'

// Build-time constant injected by Vite (apps/web/vite.config.ts). Declared
// inline so this file typechecks regardless of the consuming package's
// tsconfig — sibling env.d.ts files aren't always in scope.
declare const __APP_VERSION__: string
import {
  Settings, Loader2, Bug, LogOut, Key, Mail, Plus, X, Star,
  User as UserIcon, Building2, Users, Palette, HardDrive, Info,
  Folder, File, ChevronDown, ArrowUp, ArrowDown, Minus, Lock, ShieldBan, Search,
  GripVertical, Pencil, Trash2, Check, ChevronRight, ChevronLeft, UsersRound, UserSearch,
  Network, Link2, CircleDollarSign, SlidersHorizontal, CreditCard, History, Clock, Plug, Code2,
} from 'lucide-react'
import { request } from '@papan/api-client'
import { isSystemTenant, isValidEmailAddress, normalizeEmailAddress, classifyProofCode, PROOF_CODE_LENGTH, proofRefusalKind, typedCodeSurvivesFailedSend } from '@papan/types'
import { PasswordInput } from './PasswordInput'
import { ScrollSpy } from './settingsChrome'
import { useAnchoredPosition } from './useAnchoredPosition'
import { useMyCapabilities } from './useMyCapabilities'
import { keepsMemberManagement } from './selfRoleChange'
import { CapabilitiesUnavailableNotice } from './CapabilitiesUnavailableNotice'
import { ConnectedAppsPage } from './ConnectedAppsPage'
import { useTranslation } from '@papan/i18n'
import { usePageTitle } from '@papan/brand/react'
import { useTheme, getThemeClass } from './ThemeProvider'

import { useToast } from './Toast'
import { useConfirm } from './ConfirmModal'
import { useKeybookAvailable } from './keybookAvailability'
import type { ThemeMode } from './useSettings'

const themeModes: { value: ThemeMode; labelKey: string }[] = [
  { value: 'dark', labelKey: 'settings.theme.dark' },
  { value: 'dim', labelKey: 'settings.theme.dim' },
  { value: 'light', labelKey: 'settings.theme.light' },
]

const isDev = import.meta.env.DEV

// ---------------------------------------------------------------------------
// Page identifiers
// ---------------------------------------------------------------------------

// Two of these — 'permissions' and 'language' — no longer render anything, and
// are kept deliberately: they are still reachable URLs, and `RETIRED_SETTINGS`
// is typed `SettingsPage[]` so that a key which stops existing here fails to
// COMPILE rather than silently stopping being redirected. Deleting them from the
// union would drop those URLs to `default:`, which draws Profile under an
// address bar that still names the removed page.
type SettingsPage =
  | 'profile'
  | 'emails'
  | 'password'
  | 'secure-storage'
  | 'blocked'
  // The apps a person connected over MCP (Claude…), each acting as them; per person, shown in every org.
  | 'connected-apps'
  | 'usage'
  | 'network'
  | 'org-general'
  | 'permissions'
  | 'members'
  | 'teams'
  | 'links'
  // A tenant's OWN spend. These three came back from the Usage & Billing shelf
  // when it became platform-only: an org's budget is org configuration, and it
  // belongs beside members and teams rather than in a cross-tenant surface.
  | 'budget'
  | 'limits'
  | 'subscription'
  | 'theme'
  // Editor preferences (#987): format on save.
  | 'editor'
  | 'language'
  | 'storage'
  // Dedicated version-retention settings page (#590 Task 9) — split out of
  // the flat "keep up to N" control that used to live on `'storage'` (#452
  // Task A8): the hierarchical per-kind/per-extension ladder plus the
  // per-file overrides list needed more room than a Storage-page card. Both
  // org AND personal (see `!ORG_ONLY_SETTINGS`, same mirror as `'storage'`),
  // hidden in an identity-only tenant (`IDENTITY_ONLY_HIDDEN_SETTINGS` below)
  // — no drive there means nothing to retain.
  | 'version-retention'
  // Settings → Organization → File activity (#118, Task B8): who created /
  // updated / moved / deleted / restored what, gated `view_file_audit:read`.
  // Org-only — see `ORG_ONLY_SETTINGS` below.
  | 'file-audit'
  | 'about'
  | 'devtools'

/**
 * Settings pages an identity-only tenant does not have (§8, 0175).
 *
 * §8's list verbatim: *"hide Secure Storage/Keybook, Links, Network, and
 * Storage settings"*. Typed as `SettingsPage[]` so a page renamed in the union
 * above fails to COMPILE here rather than silently stopping being hidden.
 *
 * ── Billing and Spending are here now, and that reverses an earlier stance ──
 *
 * This list used to hold four entries, and the paragraph here argued that
 * Billing and Spending must NEVER join them: *"they report what an account was
 * charged … hiding a billing page can hide a real charge."* That reasoning was
 * correct when it was written and is retired by
 * 2026-08-13_IDENTITY_ONLY_BILLING_AND_SETTINGS.md §1 D1.
 *
 * What changed is the premise, not the principle. An identity-only tenant has
 * no drive, no workspace and no way to spend anything, the billing WRITES are
 * gated for it (`identityOnlyWriteGate('billing.write')` on `/api/billing/*`),
 * and personal tenants hold no live subscriptions on this deployment. With
 * nothing acquirable and nothing owed, there is no charge left to hide — so
 * these pages report an empty state about a tenant that cannot transact, which
 * reads as broken rather than as informative.
 *
 * **The premise is load-bearing. If personal tenants ever hold live
 * subscriptions, `'subscription'` comes back off this list** — otherwise a
 * customer is charged monthly with no cancel button, no card-update link and no
 * invoice, and the recovery path is the platform console rather than anything
 * they can reach. Do not restore the old paragraph without changing that fact
 * first; it still reads as sound, which is exactly the risk.
 *
 * ── Four keys, not two ──────────────────────────────────────────────────
 *
 * `'usage'` is an ALIAS rendering the same page as `'budget'` (see the `case`
 * below), and `'limits'` has no sidebar item on a personal drive but both
 * shells pass `limitsPage` unconditionally — so hiding the two visible items
 * would leave two live URLs onto the same surfaces. These four are also exactly
 * what `parseBudgetBillingPath` rewrites `/billing/*` onto, so the legacy doors
 * close with them.
 *
 * Only ever applied to a PERSONAL tenant — organizations short-circuit to
 * 'full' — which is why these sit inside the `!isOrg` block of the sidebar.
 */
export const IDENTITY_ONLY_HIDDEN_SETTINGS: SettingsPage[] = [
  'secure-storage', 'links', 'network', 'storage', 'version-retention',
  'subscription', 'budget', 'usage', 'limits',
]

/**
 * Pages that only mean anything inside an ORGANIZATION.
 *
 * They rendered unguarded, so `/~/settings/members` resolved against a personal
 * tenant and drew a members editor for a tenant that has none. Nothing leaked —
 * it is the caller's own tenant — but in identity-only mode it is reachable,
 * meaningless and writable, and none of the mode's server gates cover it (they
 * sit on files/bin/quarantine/workspaces/keybook/share-links, not `memberships`
 * or `teams`).
 */
export const ORG_ONLY_SETTINGS: SettingsPage[] = [
  'org-general', 'members', 'teams', 'file-audit',
]

/**
 * Pages that no longer exist for anyone.
 *
 * Both are TEMPORARY removals with their reasoning at the deletion sites:
 * `'permissions'` moved to the platform console (§8.4), `'language'` is gone
 * while every user is pinned to English (§8.5).
 *
 * They are listed rather than simply deleted so the URL corrects itself. A
 * removed `case` falls through to `default:`, which renders Profile while the
 * address bar still says `/settings/permissions` — a page that "loads" as
 * something else. Routing them through the same redirect keeps the URL honest,
 * which is the whole reason the identity-only list drives a redirect and not
 * just a hidden nav item.
 */
export const RETIRED_SETTINGS: SettingsPage[] = [
  'permissions', 'language',
]

// ---------------------------------------------------------------------------
// Sub-components (unchanged)
// ---------------------------------------------------------------------------

/**
 * Set OR change a password — and which of those it is must be KNOWN, not guessed.
 *
 * `hasPassword` is `null` while the answer is still in flight, and that third
 * state is load-bearing rather than tidiness. Treating "not yet known" as
 * `false` renders the set-a-first-password form to someone who already has one,
 * which is exactly the bug this fixes (#568): they type a new password twice,
 * submit, and are told "user already has a password" with no field to fix it.
 * CLAUDE.md records the same trap on the capability side — `can()` returns false
 * for "denied" AND for "still fetching", and a page gated on it alone refuses its
 * own admin on every cold load. Same shape, so the same rule: wait for a resolved
 * answer before deciding what to render.
 */
function PasswordSection({
  hasPassword,
  onSetPassword,
  onChangePassword,
  onForgotPassword,
  onRetryHasPassword,
}: {
  /** `null` = not resolved yet. NEVER coerce this to a boolean. */
  hasPassword?: boolean | null
  onSetPassword?: (newPassword: string) => Promise<{ error?: string }>
  onChangePassword?: (currentPassword: string, newPassword: string) => Promise<{ error?: string }>
  onForgotPassword?: () => void
  /** Re-ask for `hasPassword` after a failed fetch. See `pending` below. */
  onRetryHasPassword?: () => void
}) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  if (!onSetPassword) return null

  const changing = hasPassword === true
  /**
   * A shell that renders the change form without wiring `onChangePassword` used
   * to fall through to `onSetPassword` — collecting a current password,
   * discarding it, and reproducing #568 exactly: "user already has a password",
   * with a filled-in current-password box on screen. The prop is optional, so
   * typecheck cannot object, and dropping it left all seven wiring tests green.
   * Refuse loudly instead — which the `!onSetPassword` line above does NOT do:
   * it returns null, so the section simply vanishes and the user is left with an
   * empty card under a "Password" heading. That fails closed too, and is left
   * alone here because it is the shape every other section on this page uses,
   * but it is not the precedent this comment used to claim it was.
   */
  const misconfigured = changing && !onChangePassword

  const handleSubmit = async () => {
    setMessage(null)
    if (changing && !currentPassword) {
      setMessage({ type: 'error', text: 'Enter your current password' })
      return
    }
    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' })
      return
    }
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' })
      return
    }
    setSaving(true)
    try {
      // One call, chosen from a known fact. The old code called `setPassword`
      // and on ANY error retried `changePassword({ currentPassword: '' })` —
      // which cannot verify — then reported the FIRST call's error, so the user
      // read "user already has a password" and never saw why the retry failed.
      const result = changing
        ? await onChangePassword!(currentPassword, newPassword)
        : await onSetPassword(newPassword)
      if (result.error) {
        setMessage({ type: 'error', text: result.error })
      } else {
        setMessage({
          type: 'success',
          // A COMPLETED eviction, and true when this renders: the change moved the
          // account's session marker before it answered, so every other device's
          // session (cache cookie included) is refused from its next request and
          // its open connections are closed (#601). This is the one line a user
          // who suspects a compromise reads — never weaken the enforcement
          // without weakening the claim.
          text: changing
            ? 'Password changed. Your other devices have been signed out.'
            : 'Password set successfully',
        })
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
      }
    } catch {
      setMessage({ type: 'error', text: changing ? 'Failed to change password' : 'Failed to set password' })
    } finally {
      setSaving(false)
    }
  }

  // Not resolved yet — render the fields disabled rather than pick a branch.
  //
  // It DOES shift when it resolves to `true`: the current-password group, the
  // divider and the eviction note appear above the button, ~110-150px of
  // downward movement at the moment that button stops reading "Loading..." and
  // becomes clickable. A previous version of this comment claimed the form was
  // "the same height either way", which stopped being true when the layout was
  // rewritten. Left as-is rather than reserving the space: a fixed-height
  // skeleton would have to guess at the taller branch, and the window is a
  // single fetch. Worth revisiting if it ever becomes a mis-tap in practice.
  //
  // `onRetryHasPassword` matters because the desktop shell keeps decks mounted
  // for the life of the tab: a single failed fetch at boot (a rolling restart, a
  // blip) otherwise leaves this page permanently disabled, reading "Loading…"
  // with no error and no way out but a full reload — the "correct-looking, no
  // error, only a reload fixes it" shape CLAUDE.md calls the worst symptom
  // available, on the very page this change exists to un-dead-end.
  const pending = hasPassword === null || hasPassword === undefined

  // ONE expression for "cannot submit", so the button and the Enter key cannot
  // drift apart — Enter used to bypass every condition the button checked.
  // NOTE what is NOT here: `changing && !currentPassword`. Including it disabled
  // the button, and a disabled <button> dispatches no click — so `handleSubmit`'s
  // "Enter your current password" guard became unreachable and the user got a
  // greyed button, a silent Enter, and no explanation. Let the submit through and
  // let the guard say why.
  const submitBlocked = saving || pending || misconfigured || !newPassword

  const FIELD = 'w-full themed-input border rounded-md px-3 py-2 text-sm outline-none focus:border-blue-500'
  const LABEL = 'block text-xs font-medium themed-text-secondary mb-1.5'

  /*
   * LABELS, not placeholders. A placeholder disappears the moment you type, so
   * three near-identical boxes become unlabelled exactly when they are filled —
   * and a screen reader has nothing to announce at all. This is the change that
   * matters most on this screen.
   *
   * WIDTH IS CAPPED. Full-width stretched these to ~985px on a desktop: a line
   * length no password needs, and it made three stacked inputs read as one
   * undifferentiated wall.
   *
   * GROUPED IN TWO, with a rule between. "Prove it is you" and "choose the new
   * one" are different questions; running them together as a flat stack is why
   * the form felt like a pile of boxes rather than two steps.
   */
  return (
    <div className="space-y-5">
      {changing && (
        <div className="max-w-sm">
          <label htmlFor="pw-current" className={LABEL}>Current password</label>
          <PasswordInput
            id="pw-current"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            className={FIELD}
          />
          {onForgotPassword && (
            // UNDER THE FIELD IT RESCUES, not beside the submit button. The need
            // arises the moment someone fails to recall the password — while
            // they are looking at that input, not after filling in two more —
            // and it is where every sign-in form puts it, so it is found without
            // being read for. Beside the primary button it was a tertiary escape
            // competing for the same click, and a mis-hit left the form entirely.
            //
            // Restored now that the flow it points at actually works (#610): it
            // was removed rather than shipped into a 404, because a link to a
            // dead end is worse than no link. This closes #346's item 2c — "so
            // the first branch is never a dead end" — which is the whole reason
            // a change form needs an escape hatch at all.
            <button
              onClick={onForgotPassword}
              className="mt-1.5 text-xs text-blue-400 hover:text-blue-300 hover:underline"
            >
              Forgot your current password?
            </button>
          )}
        </div>
      )}

      {changing && <div className="border-t themed-border max-w-sm" />}

      <div className="space-y-4 max-w-sm">
        <div>
          <label htmlFor="pw-new" className={LABEL}>
            {changing ? 'New password' : 'Password'}
          </label>
          {/* `new-password` on BOTH fields, or the browser treats them as an
              ordinary login form and autofills the SAVED (current) password
              into them — the user sees their existing password sitting in "New
              password". The hint also tells password managers to offer a
              generated one here instead of the stored credential. */}
          <PasswordInput
            id="pw-new"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            autoComplete="new-password"
            disabled={pending}
            aria-describedby="pw-new-hint"
            className={FIELD}
          />
          {/* The rule, BEFORE it is broken. It used to appear only as an error
              after submitting — the one moment it is no longer useful. */}
          <p id="pw-new-hint" className="mt-1.5 text-xs themed-text-muted">At least 8 characters.</p>
        </div>

        <div>
          <label htmlFor="pw-confirm" className={LABEL}>
            {changing ? 'Confirm new password' : 'Confirm password'}
          </label>
          <PasswordInput
            id="pw-confirm"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            disabled={pending}
            // Guarded by the SAME expression as the button. Enter used to reach
            // `handleSubmit` regardless of every disabled condition, so with the
            // form misconfigured it hit `onChangePassword!` on an undefined,
            // threw, and rendered "Failed to change password" — hiding the
            // "misconfigured, please report this" notice that exists to explain
            // exactly that state.
            onKeyDown={e => { if (e.key === 'Enter' && !submitBlocked) handleSubmit() }}
            className={FIELD}
          />
        </div>
      </div>

      {misconfigured && (
        <p className="text-xs text-red-400 max-w-sm">
          This form is misconfigured — it cannot change a password. Please report this.
        </p>
      )}
      {pending && onRetryHasPassword && (
        <p className="text-xs themed-text-muted max-w-sm">
          Still checking your account.{' '}
          <button onClick={onRetryHasPassword} className="underline hover:themed-text">
            Retry
          </button>
        </p>
      )}
      {hasPassword === false && (
        // Say WHY no current password is asked, or its absence reads as an
        // oversight — or worse, as the account being unprotected.
        //
        // #346 suggests "You signed in with Google — set a password to also sign
        // in with email". Deliberately generalised: an account reaches this
        // branch from the email-CODE flow as well, and telling someone they
        // signed in with Google when they did not is a small, avoidable lie that
        // makes the rest of the sentence less trustworthy.
        <p className="text-xs themed-text-muted max-w-sm">
          You signed in without a password. Set one to also sign in with your email address.
        </p>
      )}
      {/* Mounted UNCONDITIONALLY with its text swapped, rather than created
          together with its content — a region inserted already populated is
          announced unreliably across screen-reader/browser pairs.
          
          STABLE `role`, switching only `aria-live`. An earlier version flipped
          `role` between `status` and `alert` and cited `Toast.tsx` as the
          precedent; `Toast.tsx` records the OPPOSITE ("role='alert' on EVERY
          type… the semantically clean split is not free… Change the specs FIRST
          if this is ever revisited"). And because React reconciles this <p> in
          place, flipping the role mutates it on a live element in the same
          commit that populates the text — which assistive tech may treat as a
          node replacement, re-creating the very unreliability the unconditional
          mount exists to avoid. */}
      <p
        role="status"
        aria-live={message?.type === 'error' ? 'assertive' : 'polite'}
        className={`text-xs max-w-sm ${!message ? 'sr-only' : message.type === 'error' ? 'text-red-400' : 'text-green-400'}`}
      >
        {message?.text ?? ''}
      </p>

      <div className="space-y-2">
        {/* LEFT-ALIGNED, with the fields — not right-aligned. This is a settings
            form, not a dialog: the inputs are left-aligned, so the eye scans
            down their left edge and finds the next action at the end of it.
            Right-alignment is the DIALOG convention, where a separated footer
            bar reads left-to-right to a confirm button; borrowing it here puts
            the action where nothing else on the page is. */}
        <button
          onClick={handleSubmit}
          disabled={submitBlocked}
          className="px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md transition-colors"
        >
          {saving
            ? 'Saving...'
            : pending ? 'Loading...' : changing ? 'Change Password' : 'Set Password'}
        </button>
        {changing && (
          // The consequence BEFORE the click, not only in the success message.
          // Someone changing a password on a borrowed laptop should know their
          // phone is about to be signed out while they can still decide not to.
          <p className="text-xs themed-text-muted max-w-sm">
            Your other devices will be signed out.
          </p>
        )}
      </div>
    </div>
  )
}

interface UserEmail {
  id: string
  email: string
  isPrimary: boolean
  verifiedAt: string | null
}

interface OrgEmailInfo { orgName: string; role: string }

function EmailsSection({ onEmailsChanged, orgsByEmail }: { onEmailsChanged?: () => void; orgsByEmail?: Record<string, OrgEmailInfo[]> }) {
  const { t } = useTranslation('core-ui')
  const [emails, setEmails] = useState<UserEmail[]>([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [verifyCode, setVerifyCode] = useState('')
  const [pendingEmail, setPendingEmail] = useState('')
  const [saving, setSaving] = useState(false)
  const [resending, setResending] = useState(false)
  const sendInFlight = useRef(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const toast = useToast()
  const confirm = useConfirm()

  const loadEmails = useCallback(async () => {
    try {
      const data = await request<{ emails: UserEmail[] }>('/api/emails')
      setEmails(data.emails)
    } catch {
      // silently fail
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadEmails() }, [loadEmails])

  // `address` — the code step's "Send a new code" passes the pending address (#1217: the malformed-code hint tells the
  // person to send a new one, so this page must offer it).
  /** 'sent'; 'kept' — failed, and the typed code is still the live one (`typedCodeSurvivesFailedSend`); 'failed'.
   *  `sendInFlight` covers Verify too: one action at a time. */
  const handleSendCode = async (address: string = newEmail): Promise<'sent' | 'kept' | 'failed'> => {
    // A ref, not `saving`: two triggers in one tick (Enter twice) both see the render's `saving` as false.
    if (sendInFlight.current || !isValidEmailAddress(address)) return 'failed'
    sendInFlight.current = true
    setSaving(true)
    setResending(address === pendingEmail && !!pendingEmail)
    setMessage(null)
    try {
      // The NORMALISED value, not the raw field: the repository lowercases on
    // write but does not trim, so a pasted trailing space would be stored and
    // then match nothing, since every reader compares `lower(email)` to a
    // canonical address.
    const emailToAdd = normalizeEmailAddress(address)
      const result = await request<{ ok: boolean; devCode?: string }>('/api/emails/add', { method: 'POST', body: JSON.stringify({ email: emailToAdd }), headers: { 'Content-Type': 'application/json' } })
      if (result.devCode) {
        setPendingEmail(emailToAdd)
        setNewEmail('')
        setVerifyCode(result.devCode)
        setMessage({ type: 'success', text: `Dev mode — code auto-filled: ${result.devCode}` })
        return 'sent'
      } else if (import.meta.env.DEV) {
        setMessage({ type: 'error', text: 'Dev hint: no code returned — this email may belong to another account' })
        return 'failed'
      } else {
        setPendingEmail(emailToAdd)
        setNewEmail('')
        setMessage({ type: 'success', text: `Verification code sent to ${emailToAdd}` })
        return 'sent'
      }
    } catch (err) {
      // A limit (#1217) says what to do — wait — so it is shown in the person's language (`proofRefusalKind`); another
      // 429 keeps the server's words; anything else stays generic.
      const e = err as { status?: number; message?: string; code?: string }
      const kind = proofRefusalKind(err)
      setMessage({ type: 'error', text: kind ? t(`proofCode.${kind}`, { ns: 'common', length: PROOF_CODE_LENGTH }) : e.status === 429 && e.message ? e.message : 'Failed to send code' })
      return typedCodeSurvivesFailedSend(err) ? 'kept' : 'failed'
    } finally {
      sendInFlight.current = false
      setSaving(false)
      setResending(false)
    }
  }

  // As the server reads it (the ONE rule, `classifyProofCode`): Verify only for a code that could match — a half-typed
  // or mistyped one must not spend one of the window's guesses (#1217).
  const typedCode = classifyProofCode(verifyCode)
  const handleVerify = async () => {
    // The same in-flight REF as the send: two Enters in one tick both see the render's `saving` as false, and each would
    // spend a guess.
    if (sendInFlight.current) return   // the button is disabled meanwhile (`saving`); this covers the keyboard path
    if (typedCode.kind !== 'ok' || !pendingEmail) return
    sendInFlight.current = true
    setSaving(true)
    setMessage(null)
    try {
      await request('/api/emails/verify', { method: 'POST', body: JSON.stringify({ email: pendingEmail, code: typedCode.code }), headers: { 'Content-Type': 'application/json' } })
      setPendingEmail('')
      setVerifyCode('')
      setAdding(false)
      toast.success('Email added')
      loadEmails()
      onEmailsChanged?.()
    } catch (err) {
      const e = err as { status?: number; message?: string }
      const kind = proofRefusalKind(err)   // as in the send
      setMessage({ type: 'error', text: kind ? t(`proofCode.${kind}`, { ns: 'common', length: PROOF_CODE_LENGTH }) : e.status === 429 && e.message ? e.message : 'Invalid or expired code' })
    } finally {
      sendInFlight.current = false
      setSaving(false)
    }
  }

  const handleRemove = async (id: string, email: string) => {
    const affectedOrgs = orgsByEmail?.[email.toLowerCase()] ?? []
    const orgWarning = affectedOrgs.length > 0
      ? `\n\nThis email has access to:\n${affectedOrgs.map(o => `• ${o.orgName} (${o.role})`).join('\n')}\n\nYou will lose access to these organizations using this email.`
      : ''
    const confirmed = await confirm({
      title: 'Remove email',
      message: `Remove ${email} from your account? You won't be able to log in with this email anymore.${orgWarning}`,
      type: 'danger',
    })
    if (!confirmed) return
    try {
      await request(`/api/emails/${id}`, { method: 'DELETE' })
      toast.success('Email removed')
      loadEmails()
      onEmailsChanged?.()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to remove')
    }
  }

  const handleSetPrimary = async (id: string) => {
    try {
      await request(`/api/emails/${id}/primary`, { method: 'PATCH' })
      toast.success('Primary email updated')
      loadEmails()
      onEmailsChanged?.()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update')
    }
  }

  if (loading) return null

  return (
    <div className="space-y-2">
      {emails.map(e => {
        const orgs = orgsByEmail?.[e.email.toLowerCase()] ?? []
        return (
          <div
            key={e.id}
            className="flex items-start gap-3 rounded-md border themed-border px-3 py-2 transition-colors hover:themed-bg-tertiary"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {/* `truncate` + `min-w-0`: a long address used to push the
                    controls off the row entirely. `title` keeps the full value
                    reachable when it is clipped. */}
                <span className="themed-text font-mono text-sm truncate" title={e.email}>
                  {e.email}
                </span>
                {e.isPrimary && (
                  <span className="flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[10px] themed-chip-info themed-bg-tertiary">
                    <Star size={10} /> Primary
                  </span>
                )}
                {!e.verifiedAt && (
                  <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] themed-chip-warn themed-bg-tertiary">
                    Unverified
                  </span>
                )}
              </div>
              {/* The app already knows which organizations an address carries
                  access to — it was only shown inside the delete confirmation,
                  i.e. after the decision was made. Showing it on the row is what
                  makes "can I remove this?" answerable without pressing Remove,
                  and losing that access silently is the whole of #351. */}
              {orgs.length > 0 && (
                <p className="mt-0.5 truncate text-xs themed-text-muted" title={orgs.map(o => `${o.orgName} (${o.role})`).join(', ')}>
                  Grants access to {orgs.map(o => o.orgName).join(', ')}
                </p>
              )}
            </div>

            {/* Actions live together on the right, and Remove is LAST with a gap
                before it. It used to sit immediately after the address — the most
                destructive control on the page, adjacent to the label, a
                mis-click away. */}
            <div className="flex shrink-0 items-center gap-1">
              {!e.isPrimary && (
                <button
                  onClick={() => handleSetPrimary(e.id)}
                  // The label repeats on every row, so on its own it tells a
                  // screen-reader user cycling the button list nothing about
                  // WHICH address it acts on. Same reason the remove button
                  // names its target.
                  aria-label={`Set ${e.email} as primary`}
                  className="rounded px-2 py-1 text-xs themed-text-muted transition-colors hover:themed-text hover:themed-bg-secondary"
                >
                  Set as primary
                </button>
              )}
              {!e.isPrimary && (
                <button
                  onClick={() => handleRemove(e.id, e.email)}
                  // The file's existing convention for a destructive control
                  // (a red tint on hover), not a reveal-on-hover. Dimming it
                  // until hover looked tidier on a desktop and was worse
                  // everywhere else: there is no hover on touch, so it would sit
                  // permanently faded on a phone — a destructive action rendered
                  // at reduced contrast, which is the wrong direction.
                  className="ml-1 rounded p-1 text-red-400 transition-colors hover:bg-red-500/10"
                  aria-label={`Remove ${e.email}`}
                  title={`Remove ${e.email}`}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        )
      })}

      {adding ? (
        <div className="space-y-2 pt-1">
          {!pendingEmail ? (
            <div className="flex gap-2">
              <input
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                placeholder="new@email.com"
                onKeyDown={e => { if (e.key === 'Enter') void handleSendCode() }}
                className="flex-1 themed-input border rounded-md px-3 py-1.5 text-sm outline-none focus:border-blue-500"
              />
              <button
                onClick={() => void handleSendCode()}
                disabled={saving || !isValidEmailAddress(newEmail)}
                className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md transition-colors"
              >
                {saving ? '...' : 'Send code'}
              </button>
            </div>
          ) : (
            <div className="space-y-1">
              <p className="text-xs themed-text-muted">Enter the code sent to <span className="font-mono themed-text">{pendingEmail}</span></p>
              <div className="flex gap-2">
              <input
                type="text"
                value={verifyCode}
                onChange={e => setVerifyCode(e.target.value)}
                placeholder={t('proofCode.placeholder', { ns: 'common', length: PROOF_CODE_LENGTH })}
                inputMode="numeric"
                autoComplete="one-time-code"
                // No maxLength, as on the invitation page: the browser would truncate a paste silently and the result
                // could classify differently from what was pasted; `classifyProofCode` judges the whole input.
                onKeyDown={e => { if (e.key === 'Enter') handleVerify() }}
                className="w-32 themed-input border rounded-md px-3 py-1.5 text-sm outline-none focus:border-blue-500 font-mono tracking-widest"
              />
              <button
                onClick={handleVerify}
                disabled={saving || typedCode.kind !== 'ok'}
                className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md transition-colors"
              >
                {saving ? '...' : 'Verify'}
              </button>
              </div>
              <button
                type="button"
                // A resend replaces the code: clear the box NOW (not when the answer lands, which could wipe a new
                // code pasted meanwhile) — Verify on the old one would only spend a guess (#1217). If the send failed
                // and the typed code is still live (`typedCodeSurvivesFailedSend`), it is put back, unless something
                // new was typed meanwhile.
                onClick={() => {
                  if (saving || sendInFlight.current) return
                  const prev = verifyCode
                  setVerifyCode('')
                  void handleSendCode(pendingEmail).then((r) => { if (r === 'kept') setVerifyCode((c) => (c === '' ? prev : c)) })
                }}
                disabled={saving}
                className="text-xs text-blue-400 hover:text-blue-300 disabled:opacity-50"
              >
                {resending ? t('proofCode.sending', { ns: 'common' }) : t('proofCode.resend', { ns: 'common' })}
              </button>
              {typedCode.kind === 'malformed' && (
                <p className="text-xs text-amber-400">{t('proofCode.malformed', { ns: 'common', length: PROOF_CODE_LENGTH })}</p>
              )}
            </div>
          )}
          {message && (
            <p className={`text-xs ${message.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>
              {message.text}
            </p>
          )}
          <button
            onClick={() => { setAdding(false); setPendingEmail(''); setVerifyCode(''); setMessage(null) }}
            disabled={saving}   // a send still in flight would reopen (or refill) what Cancel closed
            className="text-xs themed-text-muted hover:themed-text transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
        >
          <Plus size={12} /> Add email
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sidebar nav item
// ---------------------------------------------------------------------------

function SidebarItem({
  icon: Icon,
  label,
  active,
  onClick,
  mobile,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>
  label: string
  active: boolean
  onClick: () => void
  mobile?: boolean
}) {
  return (
    <button
      onClick={onClick}
      // Mobile: a shrink-0 chip in the horizontal nav strip; desktop: full-width
      // vertical row.
      className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors ${
        mobile ? 'shrink-0 whitespace-nowrap border themed-border' : 'w-full'
      } ${
        active
          ? 'themed-bg-tertiary themed-text font-medium'
          : 'themed-text-muted hover:themed-text hover:themed-bg-tertiary'
      }`}
    >
      <Icon size={16} />
      {label}
    </button>
  )
}

function SidebarSection({ label, mobile }: { label: string; mobile?: boolean }) {
  // Section headers don't fit a horizontal chip strip — hide them on mobile.
  if (mobile) return null
  return (
    <div className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider themed-text-muted">
      {label}
    </div>
  )
}

// ScrollSpy lived here as a near-copy of the one in `settingsChrome.tsx` until
// 2026-08-12. Both are mounted at once (the Settings and Usage & Billing decks
// stay alive, one merely hidden), so the shared component already took an
// `idPrefix` to keep their DOCUMENT-global heading ids apart — but the copy
// still minted ids from the ARRAY INDEX, and only the shared one had been
// fixed to use a monotonic counter.
//
// That is the whole cost of the duplication, and it was live: ids are minted
// only for headings that lack one, so a section that mounts LATE and ABOVE an
// already-numbered sibling takes an index already handed out. The Spending
// dashboard does exactly that (its sections resolve as their queries land), so
// this deck logged `Encountered two children with the same key, settings-spy-1`
// on every load and `getElementById` could resolve an outline click to the
// wrong heading. Caught in the browser pass, not by a test — nothing renders
// this component headlessly.
//
// The copy is gone rather than patched: one implementation cannot be fixed in
// only one place. The prefix stays `settings-spy`, which is what the billing
// deck's `billing-spy` was chosen to avoid colliding with.

// ---------------------------------------------------------------------------
// Page components
// ---------------------------------------------------------------------------

function ProfilePage({ user, onSignOut, onUpdateName }: {
  user?: { id: string; name: string; email: string }
  onSignOut?: () => void
  onUpdateName?: (newName: string) => Promise<{ error?: string }>
}) {
  const { t } = useTranslation('core-ui')
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(user?.name ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Re-sync draft if the upstream user prop changes (initial load, post-save
  // refresh). Only when not currently editing so we don't trample input.
  useEffect(() => {
    if (!editing) setDraft(user?.name ?? '')
  }, [user?.name, editing])

  const handleSave = async () => {
    if (!onUpdateName) return
    const trimmed = draft.trim()
    if (!trimmed) {
      setError('Name cannot be empty.')
      return
    }
    if (trimmed === user?.name) {
      setEditing(false)
      return
    }
    setSaving(true)
    setError(null)
    const result = await onUpdateName(trimmed)
    setSaving(false)
    if (result.error) {
      setError(result.error)
      return
    }
    setEditing(false)
  }

  return (
    <div>
      <h2 className="text-lg font-medium themed-text mb-4">Profile</h2>
      <div className="themed-bg-secondary rounded-lg p-4 space-y-4">
        <div>
          <label className="text-sm themed-text-secondary">Name</label>
          {editing ? (
            <div className="mt-1 space-y-2">
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                disabled={saving}
                autoFocus
                className="themed-input w-full px-2 py-1.5 text-sm rounded"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); void handleSave() }
                  if (e.key === 'Escape') { setEditing(false); setDraft(user?.name ?? ''); setError(null) }
                }}
              />
              {error && <p className="text-xs text-red-400">{error}</p>}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={saving}
                  className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditing(false); setDraft(user?.name ?? ''); setError(null) }}
                  disabled={saving}
                  className="px-3 py-1.5 text-xs rounded themed-btn-secondary disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-1 flex items-center gap-2">
              <div className="themed-text">{user?.name || 'Unnamed'}</div>
              {onUpdateName && (
                <button
                  type="button"
                  onClick={() => { setEditing(true); setError(null) }}
                  className="p-1 rounded themed-text-secondary hover:themed-text hover:themed-bg-tertiary transition-colors"
                  aria-label="Edit name"
                  title="Edit name"
                >
                  <Pencil size={14} />
                </button>
              )}
            </div>
          )}
        </div>
        <div>
          <label className="text-sm themed-text-secondary">Primary email</label>
          <div className="themed-text mt-1">{user?.email ?? '-'}</div>
        </div>
        {onSignOut && (
          <div className="pt-2 border-t themed-border">
            <button
              onClick={onSignOut}
              className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <LogOut size={16} />
              {t('settings.account.signOut')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function EmailsPage({ onEmailsChanged, orgsByEmail }: { onEmailsChanged?: () => void; orgsByEmail?: Record<string, OrgEmailInfo[]> }) {
  const { t } = useTranslation('core-ui')
  return (
    <div>
      <h2 className="text-lg font-medium themed-text mb-1">Emails</h2>
      {/* The page listed addresses and offered to add one without ever saying
          what a second address DOES, so the only way to find out was to add one
          and wait for something to arrive at it.
          The copy claims delivery and nothing else, deliberately. A verified
          address resolves to this account for share links
          (`share-principal.ts`) and for org invitations, but it is NOT a second
          way to sign in: the login form calls Better Auth's `signIn.email`,
          which matches `users.email` — the primary — with no secondary lookup.
          Only the share-link path resolves secondary → primary, via
          `signInWithVerifiedEmail`. */}
      <p className="text-sm themed-text-muted mb-4 max-w-prose">{t('settings.account.emailsDescription')}</p>
      <div className="themed-bg-secondary rounded-lg p-4">
        <EmailsSection onEmailsChanged={onEmailsChanged} orgsByEmail={orgsByEmail} />
      </div>
    </div>
  )
}

function PasswordPage({ hasPassword, onSetPassword, onChangePassword, onForgotPassword, onRetryHasPassword }: {
  hasPassword?: boolean | null
  onSetPassword?: (newPassword: string) => Promise<{ error?: string }>
  onChangePassword?: (currentPassword: string, newPassword: string) => Promise<{ error?: string }>
  onForgotPassword?: () => void
  onRetryHasPassword?: () => void
}) {
  return (
    <div>
      <h2 className="text-lg font-medium themed-text mb-1">Password</h2>
      {/* A bare heading left the reader to infer what this page is for from
          three unlabelled boxes. One line, and it differs by branch because the
          two cases are genuinely different tasks. */}
      <p className="text-sm themed-text-muted mb-4">
        {/* THREE states, not two. `null` (unresolved) fell into the "you have
            one" branch while the form beneath rendered the SET shape — the page
            asserting one thing and the form another, which is the guess this
            component's docblock argues against. On mobile the settings screen is
            a real route, so an OAuth-only user read "Change the password you use
            to sign in" on EVERY visit for the length of the fetch; if the fetch
            fails it never resolves and the claim is permanent. */}
        {hasPassword === true
          ? 'Change the password you use to sign in with your email address.'
          : hasPassword === false
            ? 'Set a password so you can also sign in with your email address.'
            : 'Set or change the password you use to sign in with your email address.'}
      </p>
      <div className="themed-bg-secondary rounded-lg p-5">
        <PasswordSection
          hasPassword={hasPassword}
          onSetPassword={onSetPassword}
          onChangePassword={onChangePassword}
          onForgotPassword={onForgotPassword}
          onRetryHasPassword={onRetryHasPassword}
        />
      </div>
    </div>
  )
}

function OrgDeleteDialog({ tenantName, tenantId, onDeleted, onClose }: {
  tenantName: string
  tenantId: string
  onDeleted: () => void
  onClose: () => void
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const toast = useToast()
  const [confirmInput, setConfirmInput] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState('')

  const nameMatches = confirmInput.trim() === tenantName.trim()

  const handleDelete = async () => {
    if (!nameMatches) return
    setDeleting(true)
    setError('')
    try {
      await request(`/api/orgs/${tenantId}`, { method: 'DELETE' })
      toast.success('Organization deleted')
      onDeleted()
    } catch (err: any) {
      setError(err?.message || 'Failed to delete organization')
    } finally {
      setDeleting(false)
    }
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative z-10 rounded-lg shadow-xl max-w-md w-full ${themeClass} themed-bg-secondary`}>
        <div className="flex items-center justify-between px-6 pt-5">
          <h3 className="text-lg font-semibold text-red-400">Delete Organization</h3>
          <button onClick={onClose} className="p-1 themed-text-muted hover:themed-text transition-colors rounded">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <p className="text-sm themed-text">
            This will permanently delete <strong>{tenantName}</strong> and all its files, workspaces, and member access. This cannot be undone.
          </p>

          <div>
            <label className="text-sm themed-text-secondary block mb-1">
              Type <strong className="font-mono text-red-400">{tenantName}</strong> to confirm
            </label>
            <input
              type="text"
              value={confirmInput}
              onChange={e => { setConfirmInput(e.target.value); setError('') }}
              onKeyDown={e => { if (e.key === 'Enter' && nameMatches) handleDelete() }}
              placeholder={tenantName}
              autoFocus
              className="w-full themed-input border rounded-md px-3 py-2 text-sm outline-none focus:border-red-500"
            />
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}
        </div>

        <div className="px-6 pb-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 themed-btn-secondary rounded-lg text-sm font-medium">
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={!nameMatches || deleting}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {deleting ? <Loader2 size={14} className="animate-spin" /> : 'Delete organization'}
          </button>
        </div>
      </div>
    </div>
  )
}

/** A blocked entry shown on the Blocked page. `kind` distinguishes an
 *  org-membership invite block from a cross-org connection block — the
 *  unblock handler routes on it. */
interface BlockedItem {
  inviteId: string
  tenantId: string
  tenantName: string
  email: string
  kind?: 'invite' | 'connection'
  /** #152: 'account' = the block covers every address of the account (`email` is the
   *  one it was pressed from); 'address' = this one address only. */
  scope?: 'account' | 'address'
}

function BlockedInvitesPage({ onListBlocked, onUnblock }: {
  onListBlocked?: () => Promise<Array<BlockedItem>>
  onUnblock?: (inviteId: string, kind?: 'invite' | 'connection') => Promise<void>
}) {
  const [items, setItems] = useState<Array<BlockedItem>>([])
  const [loading, setLoading] = useState(true)
  const toast = useToast()

  useEffect(() => {
    if (!onListBlocked) { setLoading(false); return }
    onListBlocked().then(setItems).catch(() => {}).finally(() => setLoading(false))
  }, [onListBlocked])

  const handleUnblock = async (item: BlockedItem) => {
    if (!onUnblock) return
    try {
      await onUnblock(item.inviteId, item.kind)
      setItems(prev => prev.filter(i => i.inviteId !== item.inviteId))
      toast.success('Unblocked')
    } catch {
      toast.error('Failed to unblock')
    }
  }

  return (
    <div>
      <h2 className="text-lg font-medium themed-text mb-4">Blocked Organizations</h2>
      {loading ? (
        <div className="flex items-center gap-2 themed-text-muted"><Loader2 size={16} className="animate-spin" /> Loading...</div>
      ) : items.length === 0 ? (
        <p className="text-sm themed-text-muted">No blocked organizations.</p>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <div key={item.inviteId} className="flex items-center justify-between themed-bg-secondary rounded-lg px-4 py-3">
              <div>
                <div className="text-sm themed-text font-medium">{item.tenantName}</div>
                <div className="text-xs themed-text-muted">
                  {item.scope === 'account' ? `All your addresses (blocked from ${item.email})` : item.email}
                  <span className="ml-2 opacity-70">· {item.kind === 'connection' ? 'Connection' : 'Invitation'}</span>
                </div>
              </div>
              <button
                onClick={() => handleUnblock(item)}
                className="px-3 py-1 text-xs themed-btn-secondary rounded transition-colors"
              >
                Unblock
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** Server-side cap in `PATCH /api/orgs/:id` (parity with org create). */
const ORG_NAME_MAX = 100

/**
 * The org's MCP connections switch (`GET/PUT /api/orgs/:id/mcp-connections`, `manage_org`). Hidden when the caller
 * may not read it (the GET answers 403) — and for a personal drive (400): there the person's own Connected apps page
 * decides.
 */
function OrgMcpConnectionsSetting({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const [state, setState] = useState<{ enabled: boolean; secretsAllowed: boolean; canManage: boolean } | null>(null)
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    let cancelled = false
    setState(null)
    request<{ enabled: boolean; secretsAllowed: boolean; canManage: boolean }>(`/api/orgs/${tenantId}/mcp-connections`)
      .then(r => { if (!cancelled) setState(r) })
      .catch(() => { /* not readable here: the section stays hidden */ })
    return () => { cancelled = true }
  }, [tenantId])
  if (!state) return null
  const save = async (change: { enabled: boolean } | { secretsAllowed: boolean }) => {
    setSaving(true)
    try {
      const r = await request<{ enabled: boolean; secretsAllowed: boolean }>(`/api/orgs/${tenantId}/mcp-connections`, {
        method: 'PUT',
        body: JSON.stringify(change),
        headers: { 'Content-Type': 'application/json' },
      })
      setState({ ...state, enabled: r.enabled, secretsAllowed: r.secretsAllowed })
      toast.success(t('settings.org.mcp.saved'))
    } catch {
      toast.error(t('settings.org.mcp.failed'))
    } finally {
      setSaving(false)
    }
  }
  const toggleRow = (props: { title: string; body: string; status: string; warn: boolean; checked: boolean; disabled: boolean; onChange: () => void; testId: string }) => (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="text-sm themed-text font-medium">{props.title}</div>
        <p className="text-xs themed-text-muted mt-1">{props.body}</p>
        <p className={`text-xs mt-1 ${props.warn ? 'text-amber-500' : 'themed-text-secondary'}`}>{props.status}</p>
      </div>
      <label className="relative inline-flex items-center cursor-pointer shrink-0">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={props.checked}
          disabled={props.disabled}
          onChange={props.onChange}
          aria-label={props.title}
          data-testid={props.testId}
        />
        <div className="w-9 h-5 bg-gray-500 rounded-full peer peer-checked:bg-blue-600 peer-disabled:opacity-50 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
      </label>
    </div>
  )
  return (
    <div className="themed-bg-secondary rounded-lg p-4 mt-4 space-y-4" data-testid="org-mcp-setting">
      {toggleRow({
        title: t('settings.org.mcp.title'), body: t('settings.org.mcp.body'),
        status: state.enabled ? t('settings.org.mcp.on') : t('settings.org.mcp.off'), warn: !state.enabled,
        checked: state.enabled, disabled: !state.canManage || saving,
        onChange: () => void save({ enabled: !state.enabled }), testId: 'org-mcp-toggle',
      })}
      {/* #1297: the org policy above each member's per-connection keybook switch (Connected apps). */}
      {toggleRow({
        title: t('settings.org.mcp.secretsTitle'), body: t('settings.org.mcp.secretsBody'),
        status: state.secretsAllowed ? t('settings.org.mcp.secretsOn') : t('settings.org.mcp.secretsOff'), warn: false,
        checked: state.secretsAllowed, disabled: !state.canManage || saving,
        onChange: () => void save({ secretsAllowed: !state.secretsAllowed }), testId: 'org-mcp-secrets-toggle',
      })}
    </div>
  )
}

function OrgGeneralPage({ tenantName, tenantId, userRole, orgEmail, onDeleteOrg, onRenameOrg }: {
  tenantName?: string
  tenantId?: string
  userRole?: string
  orgEmail?: string
  onDeleteOrg?: () => void
  /** Renamed successfully — lets the shell update its tenant list / taskbar. */
  onRenameOrg?: (name: string) => void
}) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  // delete_org capability (default owner-only) — replaces userRole === 'owner'.
  const { can, unavailable: capsUnavailable, retry: retryCaps } = useMyCapabilities(tenantId, false, orgEmail)
  const canDeleteOrg = can('delete_org', true)
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const confirm = useConfirm()

  /**
   * Rename.
   *
   * `PATCH /api/orgs/:id` has existed since the org routes were written, but
   * nothing in the app ever called it — an org's name was set at creation and
   * then permanent. Two gates, mirroring the route exactly:
   *  - `manage_org: write` — the capability the route checks.
   * There is no system-tenant exclusion: every org — including the
   * platform-admin org — is renameable (decided 2026-08-18; see the note on
   * `PATCH /api/orgs/:id`). DELETE is still refused for system tenants, which
   * is a different control on a different route.
   */
  const canRename = can('manage_org', 'write')
  const [editingName, setEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState(tenantName ?? '')
  const [savingName, setSavingName] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)

  // Re-sync when the upstream name changes (tenant switch, post-save refresh) —
  // but never while the field is open, or a keystroke gets trampled.
  useEffect(() => {
    if (!editingName) setNameDraft(tenantName ?? '')
  }, [tenantName, editingName])

  const cancelRename = () => { setEditingName(false); setNameError(null); setNameDraft(tenantName ?? '') }

  const saveRename = async () => {
    if (!tenantId) return
    const trimmed = nameDraft.trim()
    if (!trimmed) { setNameError(t('settings.org.nameRequired')); return }
    if (trimmed === tenantName) { setEditingName(false); return }
    // Renaming a SYSTEM tenant is confirmed, because it used to be refused
    // outright. The guard was dropped deliberately (renaming breaks no
    // authority, and the old rule froze our brand into every whitelabel org
    // switcher), but the reason it existed is real: `Papan Admins` relabelled
    // to something forgettable hides an org that confers platform-wide power,
    // and renames have no audit trail. One deliberate step is what remains of
    // that protection.
    //
    // UI-ONLY, and deliberately so — do not read this as a boundary. The route
    // has no system-tenant handling left beyond a `console.warn`, so a direct
    // API call renames `Papan Admins` with no confirmation and no audit row.
    // What actually gates it is `manage_org: write` INSIDE that org, which only
    // an allowlisted platform admin holds. If this ever needs to be a real
    // checkpoint it belongs on the route (e.g. a required `confirmSystemRename`
    // field), not here.
    if (isSystemTenant(tenantId)) {
      const ok = await confirm({
        title: t('settings.org.confirmSystemTitle'),
        message: t('settings.org.confirmSystemBody', { from: tenantName ?? tenantId, to: trimmed }),
        type: 'danger',
        confirmText: t('settings.org.rename'),
      })
      if (!ok) return
    }
    setSavingName(true)
    setNameError(null)
    try {
      await request(`/api/orgs/${tenantId}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: trimmed }),
        headers: { 'Content-Type': 'application/json' },
      })
      setEditingName(false)
      toast.success(t('settings.org.renamed'))
      // The name is a PROP owned by the shell's tenant list. Without telling the
      // shell, the field snaps back to the old value on the next render and the
      // taskbar keeps showing it.
      onRenameOrg?.(trimmed)
    } catch (err: any) {
      setNameError(err?.message || t('settings.org.renameFailed'))
    } finally {
      setSavingName(false)
    }
  }

  return (
    <div>
      <h2 className="text-lg font-medium themed-text mb-4">Organization</h2>
      {/* Failed capability fetch hides Delete organization exactly like a denial
          would; say which one it is rather than let an owner think their role
          changed (#341). */}
      {capsUnavailable && <CapabilitiesUnavailableNotice onRetry={() => void retryCaps()} />}
      <div className="themed-bg-secondary rounded-lg p-4 space-y-4">
        <div>
          <label className="text-sm themed-text-secondary">Organization name</label>
          {editingName ? (
            <div className="mt-1 space-y-2">
              <input
                type="text"
                value={nameDraft}
                onChange={e => setNameDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); void saveRename() }
                  if (e.key === 'Escape') cancelRename()
                }}
                disabled={savingName}
                // Mirrors the route's cap. Without it the field accepts any
                // length and the limit is only discovered as a 400 AFTER Save —
                // the server check stays, this just stops you typing past it.
                maxLength={ORG_NAME_MAX}
                autoFocus
                aria-label={t('settings.org.nameLabel')}
                data-testid="org-name-input"
                className="w-full max-w-sm themed-input border rounded-md px-3 py-1.5 text-sm outline-none focus:border-blue-500 disabled:opacity-50"
              />
              {/* A bare N/100 counter, not a sentence: `{{count}}` triggers
                  i18next pluralisation, and a key without `_one`/`_other`
                  renders "1 characters left" in English (CLAUDE.md — a
                  half-declared plural fails silently). Digits need no
                  translation and no plural rules in any of the four locales. */}
              {nameDraft.length >= ORG_NAME_MAX - 20 && (
                <div className="text-[10px] themed-text-muted tabular-nums">
                  {nameDraft.length}/{ORG_NAME_MAX}
                </div>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => void saveRename()}
                  disabled={savingName || !nameDraft.trim()}
                  className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md transition-colors"
                >
                  {savingName ? '...' : t('settings.org.save')}
                </button>
                <button
                  onClick={cancelRename}
                  disabled={savingName}
                  className="px-3 py-1.5 text-sm themed-btn-secondary rounded-md transition-colors disabled:opacity-50"
                >
                  {t('settings.org.cancel')}
                </button>
              </div>
              {nameError && <div className="text-xs text-red-400">{nameError}</div>}
            </div>
          ) : (
            <div className="mt-1 flex items-center gap-2">
              <span className="themed-text">{tenantName ?? '-'}</span>
              {canRename && (
                <button
                  onClick={() => { setNameDraft(tenantName ?? ''); setNameError(null); setEditingName(true) }}
                  title={t('settings.org.rename')}
                  aria-label={t('settings.org.rename')}
                  data-testid="org-name-edit"
                  className="p-1 rounded hover:themed-bg-tertiary transition-colors"
                >
                  <Pencil size={13} className="themed-text-muted" />
                </button>
              )}
            </div>
          )}
        </div>
        {tenantId && (
          <div>
            <label className="text-sm themed-text-secondary">Organization ID</label>
            <div className="themed-text mt-1 font-mono text-sm">{tenantId}</div>
          </div>
        )}
        {orgEmail && (
          <div>
            <label className="text-sm themed-text-secondary">Your email</label>
            <div className="themed-text mt-1">{orgEmail}{userRole ? <span className="ml-2 text-xs themed-text-muted">({userRole})</span> : null}</div>
          </div>
        )}
      </div>

      {tenantId && <OrgMcpConnectionsSetting tenantId={tenantId} />}

      {canDeleteOrg && (
        <div className="mt-6">
          <button
            onClick={() => setShowDeleteDialog(true)}
            className="text-sm text-red-400 hover:text-red-300 transition-colors"
          >
            Delete organization
          </button>
        </div>
      )}

      {showDeleteDialog && tenantId && tenantName && (
        <OrgDeleteDialog
          tenantName={tenantName}
          tenantId={tenantId}
          onDeleted={() => onDeleteOrg?.()}
          onClose={() => setShowDeleteDialog(false)}
        />
      )}
    </div>
  )
}

interface MemberInfo {
  email: string
  name: string
  role: string
  /**
   * Legacy field NAME preserved for wire back-compat. The value is
   * billing-micro of the deployment currency, NOT USD micro — the
   * "Usd" is historical. The server reads this from the JSONB
   * `memberships.budget_adjustments.month` since codex F6 (round 16),
   * which dropped the legacy scalar column in migration 0178.
   */
  budgetAdjustmentUsd: number | null
  accessCount?: number
  status?: 'active' | 'invited' | 'declined'
  inviteId?: string
  /**
   * PLATFORM SUPPORT SEAT (0180). Two fields, two different questions — the
   * same pair the console's People tab reads, for the same reason.
   *
   * `platformSeat` = "is this seat billing the platform?", which needs the
   * marker AND the holder still conferring platform administration — the pair
   * `resolvePlatformPayer` enforces. THIS PAGE NEVER READS IT: it is a money
   * claim, and the money claim lives on the Spending dashboard, where the same
   * field answers it beside the spend it describes. Declared so the wire shape
   * is documented where it is received, and so a future reader reaches for the
   * right field rather than the near one.
   *
   * `seatMarked` = "is there a marker on the row?". It drives the GROUPING,
   * because `seatIsBillable` excludes on the marker alone — so grouping by
   * `platformSeat` would put a demoted administrator's still-free seat back
   * among the customer's own members while the count beside it kept excluding
   * them, and the list would not add up to the number above it.
   *
   * Both absent on an older API mid-deploy; absence means "not a support
   * seat", which renders exactly as today.
   */
  platformSeat?: boolean
  seatMarked?: boolean
}

const ROLE_OPTIONS: { value: string; label: string; desc: string; gate?: 'owner' | 'admin' }[] = [
  { value: 'owner', label: 'Owner', desc: 'Full control', gate: 'owner' },
  { value: 'admin', label: 'Drive admin', desc: 'Manage & Edit', gate: 'admin' },
  { value: 'member', label: 'Member', desc: 'Read & Write' },
  { value: 'viewer', label: 'Viewer', desc: 'Read-only' },
  { value: 'guest', label: 'Guest', desc: 'No access' },
  { value: 'noaccess', label: 'No Access', desc: 'Hidden' },
]

/**
 * The shell behind every role control on this page: a `themed-input` trigger
 * with a chevron, and a body-portaled menu anchored to it.
 *
 * Extracted from `RolePicker` when the members filter needed the same control.
 * A native `<select>` was the first attempt and looked like a different
 * product sitting two pixels from the picker it filters — but the fix for that
 * is ONE shell with two option sets, never a second widget wearing the same
 * classes. Copy the classes and the two drift on the next change; the menu
 * geometry (portal + `useAnchoredPosition`) would then be hand-rolled twice,
 * which is the bug this page just had.
 *
 * Options carry an optional `desc`: role ASSIGNMENT explains what each role
 * can do, role FILTERING has nothing to explain, so the second line is simply
 * absent rather than blank.
 */
interface PickerOption { value: string; label: string; desc?: string }

function PickerMenu({ value, options, onChange, size = 'sm', displayLabel, ariaLabel, testId }: {
  value: string
  options: PickerOption[]
  onChange: (value: string) => void
  size?: 'sm' | 'xs'
  /**
   * Trigger text when the current value is not among `options` — role
   * assignment hides roles the caller may not grant, and the trigger must
   * still name the role the member actually holds.
   */
  displayLabel?: string
  ariaLabel?: string
  testId?: string
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const current = options.find(o => o.value === value)
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return
      if (dropdownRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    // Escape closes. A native `<select>` gave this for free; a custom trigger
    // has to say so, and the menu is portaled to `<body>` so a local handler
    // would never see the key.
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); triggerRef.current?.focus() }
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  /**
   * The menu is `position: fixed` on `document.body`, so nothing clips it —
   * and nothing stopped it running off the bottom edge either. It used to be
   * placed with a bare `top: rect.bottom + 4, left: rect.left` captured once
   * on open, so a role cell in the lower part of a members table opened a
   * ~210px menu straight past the viewport, and the menu then stayed put while
   * the table scrolled underneath it.
   *
   * `useAnchoredPosition` is the ONE implementation of this — flip, clamp,
   * follow the anchor, and dismiss once the anchor is no longer visible. Do
   * not hand-roll it again here; that is the drift the hook was written to
   * end.
   */
  const { pos } = useAnchoredPosition({
    open,
    anchorRef: triggerRef,
    floatingRef: dropdownRef,
    onDismiss: () => setOpen(false),
  })

  const textSize = size === 'xs' ? 'text-xs' : 'text-sm'
  const py = size === 'xs' ? 'py-1' : 'py-1.5'

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={testId}
        className={`themed-input border rounded px-2 ${py} ${textSize} outline-none text-left flex items-center gap-1 min-w-[90px]`}
      >
        <span className="themed-text">{current?.label ?? displayLabel ?? value}</span>
        <ChevronDown size={12} className="themed-text-muted ml-auto" />
      </button>
      {open && createPortal(
        <div
          ref={dropdownRef}
          role="listbox"
          className={`fixed z-[200] ${themeClass} themed-bg-tertiary border themed-border rounded-lg shadow-xl py-1 w-[180px]`}
          style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? undefined : 'hidden' }}
        >
          {options.map(o => (
            <button
              key={o.value}
              role="option"
              aria-selected={o.value === value}
              onClick={() => { onChange(o.value); setOpen(false) }}
              className={`w-full text-left px-3 py-1.5 transition-colors ${
                o.value === value ? 'themed-bg-secondary' : 'hover:themed-bg-secondary'
              }`}
            >
              <div className={`${textSize} themed-text font-medium`}>{o.label}</div>
              {o.desc && <div className="text-[10px] themed-text-muted">{o.desc}</div>}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  )
}

/** Role ASSIGNMENT — the full role list, narrowed to what the caller may grant. */
function RolePicker({ value, onChange, canAssignOwner, canAssignAdmin, size = 'sm' }: {
  value: string
  onChange: (role: string) => void
  /** May the caller assign the owner role? (resolved manage_owner_role) */
  canAssignOwner?: boolean
  /** May the caller assign the admin role? (resolved manage_admin_role) */
  canAssignAdmin?: boolean
  size?: 'sm' | 'xs'
}) {
  const options = ROLE_OPTIONS
    .filter(r => r.gate === 'owner' ? canAssignOwner : r.gate === 'admin' ? canAssignAdmin : true)
    .map(({ value, label, desc }) => ({ value, label, desc }))
  return (
    <PickerMenu
      value={value}
      options={options}
      onChange={onChange}
      size={size}
      // Resolved against the UNFILTERED list: a member's role must still be
      // named on the trigger even when the caller cannot assign it.
      displayLabel={ROLE_OPTIONS.find(r => r.value === value)?.label}
    />
  )
}

/**
 * The email choice for a reinvite, rendered INSIDE the confirm dialog.
 *
 * `confirm()` resolves a boolean, and this decision used to abuse it as a
 * three-way control: "Send email" and "No email" as the two buttons, with the
 * reinvite happening either way. Three things were wrong with that, and only
 * the last one is cosmetic:
 *
 *  - THERE WAS NO WAY OUT. Escape and a backdrop click both resolve `false`,
 *    which meant "reinvite without email" — so the universal dismiss gesture
 *    performed the action. It was the one `confirm()` in this file whose result
 *    was not used as a guard; every sibling does `if (!confirmed) return`.
 *  - It asked two questions ("Reinvite them? Send an email?") and took one
 *    answer.
 *  - "No email" sits where Cancel sits and reads like it, while doing the
 *    opposite.
 *
 * Skipping the email is a REAL option worth keeping, and the reason is not
 * obvious: a reinvite without an email keeps the same token, so resetting without sending
 * makes the recipient's ORIGINAL invitation link work again. With the email, the server
 * mints a NEW link (#1200 — only the token's hash is stored) and the original stops working. Worth a sentence
 * rather than a mystery button — which is what this renders when unchecked.
 *
 * Owns its state and reports through a callback, so the dialog's `message` can
 * stay the static ReactNode `confirm()` captures while the checkbox still
 * re-renders.
 */
function ReinviteEmailChoice({ email, onChange }: { email: string; onChange: (send: boolean) => void }) {
  const { t } = useTranslation('core-ui')
  const [send, setSend] = useState(true)
  return (
    <span className="block">
      <span className="block themed-text-secondary">
        {t('settings.members.reinviteResets', { email, defaultValue: 'This resets {{email}}’s invitation and gives them another 30 days.' })}
      </span>
      <label className="mt-3 flex items-center gap-2 cursor-pointer themed-text">
        <input
          type="checkbox"
          checked={send}
          onChange={e => { setSend(e.target.checked); onChange(e.target.checked) }}
          className="rounded"
        />
        <span>{t('settings.members.reinviteSendEmail', 'Send a new invitation email')}</span>
      </label>
      {send ? (
        <span className="mt-2 block text-xs themed-text-muted">
          {t('settings.members.reinviteNewLink', 'The email carries a new link. Their earlier invitation link stops working.')}
        </span>
      ) : (
        <span className="mt-2 block text-xs themed-text-muted">
          {t('settings.members.reinviteNoEmail', 'No email will be sent. Their original invitation link starts working again.')}
        </span>
      )}
    </span>
  )
}

function MembersPage({ tenantId, userEmail, onPickFile, refreshKey, renderLeaveNotice }: { tenantId?: string; userEmail?: string; onPickFile?: () => Promise<{ id: string; isFolder: boolean } | null>; refreshKey?: number; renderLeaveNotice?: (tenantId: string) => React.ReactNode }) {
  const [members, setMembers] = useState<MemberInfo[]>([])
  const [invites, setInvites] = useState<MemberInfo[]>([])
  const [loading, setLoading] = useState(true)
  /**
   * Seat capacity, from the members endpoint — which is MEMBERSHIP-gated.
   *
   * Deliberately NOT from `GET /billing/subscription`: that is gated on
   * `manage_billing:read`, so a plain member would see an empty slot where the
   * count should be. Capacity is a fact every member bumps into; the commercial
   * relationship is not.
   *
   * `max` has three distinct states and they must not be flattened:
   * `undefined` = the API did not say, `null` = unlimited, a number = the
   * ceiling (`0` meaning nobody). Blank collapsing into 0 is the bug the seat
   * cap is built around.
   */
  const [seats, setSeats] = useState<{ billable: number; max: number | null; support?: number } | null>(null)
  const [memberTeams, setMemberTeams] = useState<Record<string, string[]>>({}) // email → team names
  const [memberTeamsEmail, setMemberTeamsEmail] = useState<string | null>(null) // email for teams dialog
  const [memberAccessEmail, setMemberAccessEmail] = useState<string | null>(null)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('member')
  const [sendEmail, setSendEmail] = useState(true)
  const [inviting, setInviting] = useState(false)
  const [query, setQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const toast = useToast()
  const confirm = useConfirm()
  const { t } = useTranslation('core-ui')
  // Resolved capabilities (replaces userRole === 'admin'/'owner'). Member
  // management = manage_member; touching an owner = manage_owner_role;
  // touching/assigning an admin = manage_admin_role.
  const { can, unavailable: capsUnavailable, retry: retryCaps } = useMyCapabilities(tenantId, false, userEmail)
  const canManage = can('manage_member', 'write')
  const canManageOwner = can('manage_owner_role', 'write')
  const canManageAdmin = can('manage_admin_role', 'write')
  /**
   * May the caller act on a row holding this role?
   *
   * ONE definition, for MEMBER rows and INVITE rows alike. It was spelled out
   * inline on the member row twice and not at all on the invite row, so once
   * `owner` became an invitable role an admin got a fully enabled role picker
   * on an owner invite where every option 403s — a control that can only fail,
   * which is the picker/server disagreement this file has now produced twice.
   *
   * Disclosure only. The server gates every one of these routes on the same
   * capabilities; this decides whether to offer the control or show a static
   * chip in its place.
   */
  const canManageRole = (role: string) =>
    canManage && (role !== 'owner' || canManageOwner) && (role !== 'admin' || canManageAdmin)

  const ROLE_LABEL: Record<string, string> = {
    owner: 'Owner', admin: 'Drive admin', member: 'Member',
    viewer: 'Viewer', guest: 'Guest', noaccess: 'No Access',
  }

  const loadMembers = useCallback(async () => {
    if (!tenantId) return
    try {
      const data = await request<{
        members: MemberInfo[]
        invites: MemberInfo[]
        seats?: { billable: number; max: number | null; support?: number }
      }>(`/api/orgs/${tenantId}/members`)
      setMembers(data.members)
      setInvites(data.invites ?? [])
      // Absent on an older API instance mid-deploy — leave it null and render
      // nothing rather than inventing a count from the rows.
      setSeats(data.seats ?? null)
      // Fetch teams and their members to build email → team names map
      try {
        const teamsData = await request<{ teams: Array<{ id: string; name: string }> }>(`/api/orgs/${tenantId}/teams`)
        const map: Record<string, string[]> = {}
        await Promise.all(teamsData.teams.map(async (team) => {
          const tmData = await request<{ members: Array<{ email: string }> }>(`/api/orgs/${tenantId}/teams/${team.id}/members`)
          for (const m of tmData.members) {
            const key = m.email.toLowerCase()
            if (!map[key]) map[key] = []
            map[key].push(team.name)
          }
        }))
        setMemberTeams(map)
      } catch { /* teams are optional */ }
    } catch { /* silently fail */ }
    finally { setLoading(false) }
  }, [tenantId])

  useEffect(() => { loadMembers() }, [loadMembers])
  useEffect(() => { if (refreshKey) loadMembers() }, [refreshKey]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Ask before conferring ownership — from ANY control that can confer it.
   *
   * THREE do: the invite form, the role picker on a pending invite, and the
   * role picker on a member. They reach the same end state, so guarding one is
   * guarding none — the unguarded two are simply the route a mis-click takes
   * instead. One helper, three call sites, no room for them to drift.
   *
   * Owner alone gets this. It is the strongest role in the organization, it can
   * remove the person granting it, and it sits one keystroke away in a dropdown
   * whose other options are ordinary. Confirming every role change would train
   * people to click through the one that matters — which is why demotions and
   * every lesser role stay silent.
   *
   * `immediate` separates "they are an owner now" from "they will be when they
   * accept": the same grant, but only one of them has already happened, and a
   * dialog that says the wrong one is worse than none.
   */
  const confirmOwnerGrant = (who: string, immediate: boolean) => confirm({
    title: immediate ? 'Make this person an owner?' : 'Invite this person as an owner?',
    message: immediate
      ? `Owner is the strongest role in this organization. ${who} will be able to manage billing, every member, and other owners — including you.`
      : `Owner is the strongest role in this organization. When ${who} accepts, they will be able to manage billing, every member, and other owners — including you.`,
    confirmText: immediate ? 'Make owner' : 'Send owner invitation',
    type: 'danger',
  })

  const handleInvite = async () => {
    if (!tenantId || !inviteEmail.includes('@')) return
    // Before the spinner: a cancelled dialog must leave the form exactly as it
    // was, not flash a disabled button on the way to doing nothing.
    if (inviteRole === 'owner' && !(await confirmOwnerGrant(inviteEmail, false))) return
    setInviting(true)
    try {
      await request(`/api/orgs/${tenantId}/invite`, {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail, role: inviteRole, sendEmail }),
        headers: { 'Content-Type': 'application/json' },
      })
      toast.success(sendEmail ? `Invitation sent to ${inviteEmail}` : `${inviteEmail} invited`)
      setInviteEmail('')
      setInviteRole('member')
      loadMembers()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to invite')
    } finally {
      setInviting(false)
    }
  }

  const handleReinvite = async (inviteId: string, email: string) => {
    if (!tenantId) return
    // Default to sending: re-inviting somebody almost always means telling
    // them. The checkbox carries the other case, and the ref is what lets a
    // static dialog message report a live choice back here.
    const sendEmailRef = { current: true }
    const confirmed = await confirm({
      title: 'Reinvite',
      message: <ReinviteEmailChoice email={email} onChange={v => { sendEmailRef.current = v }} />,
      type: 'info',
      confirmText: 'Reinvite',
      cancelText: 'Cancel',
    })
    // Cancel, Escape and a backdrop click now all mean the same thing: nothing
    // happens. That was the actual defect behind "these two buttons are
    // confusing" — neither of them was a way out.
    if (!confirmed) return
    const sendEmail = sendEmailRef.current
    try {
      await request(`/api/orgs/${tenantId}/invite/${inviteId}/reinvite`, {
        method: 'POST',
        body: JSON.stringify({ sendEmail }),
        headers: { 'Content-Type': 'application/json' },
      })
      toast.success(sendEmail
        ? `Invitation resent to ${email}`
        : 'Invitation reset — their existing link works again')
      loadMembers()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to reinvite')
    }
  }

  const handleCancelInvite = async (inviteId: string, email: string) => {
    if (!tenantId) return
    const confirmed = await confirm({
      title: 'Remove invitation',
      message: `Remove the invitation for ${email}?`,
      type: 'danger',
    })
    if (!confirmed) return
    try {
      await request(`/api/orgs/${tenantId}/invite/${inviteId}`, { method: 'DELETE' })
      toast.success('Invitation removed')
      loadMembers()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to remove')
    }
  }

  interface AccessConflict {
    id: string
    folderId: string
    name: string
    path: string
    currentPermission: string
    suggestedPermission: string
    isUserFolder: boolean
  }
  const [syncDialog, setSyncDialog] = useState<{ email: string; role: string; conflicts: AccessConflict[]; driveAdminNarrowedFolders: number } | null>(null)

  const handleRoleChange = async (email: string, newRole: string) => {
    if (!tenantId) return
    if (newRole === 'owner') {
      const who = members.find(m => m.email.toLowerCase() === email.toLowerCase())?.name || email
      // The picker is controlled by `m.role`, which has not changed — so
      // cancelling leaves the row showing the role it still holds.
      if (!(await confirmOwnerGrant(who, true))) return
    }
    try {
      const result = await request<{ ok: boolean; conflicts: AccessConflict[]; syncedCount?: number; driveAdminNarrowedFolders?: number }>(`/api/orgs/${tenantId}/members/role`, {
        method: 'PATCH',
        body: JSON.stringify({ email, role: newRole }),
        headers: { 'Content-Type': 'application/json' },
      })
      if (result.syncedCount && result.syncedCount > 0) {
        toast.success(`Role updated — ${result.syncedCount} folder access downgraded`)
      } else {
        toast.success('Role updated')
      }
      loadMembers()
      // Show sync dialog for remaining conflicts, but skip if self-demotion (can't manage anymore)
      const isSelf = userEmail && email.toLowerCase() === userEmail.toLowerCase()
      // #1102 I7 — after changing their OWN role, ask the server whether they still hold manage_member:write (the
      // dialog's actions need it); refresh the page's capabilities too
      const stillCanManage = isSelf ? await keepsMemberManagement(tenantId, request) : true
      if (isSelf) retryCaps()
      // #787 §5 — a promotion to drive admin also lists the folders where the
      // Drive Admin ROLE is narrowed below Manage (it now applies to them too).
      const narrowed = result.driveAdminNarrowedFolders ?? 0
      if ((result.conflicts?.length > 0 || narrowed > 0) && (!isSelf || stillCanManage)) {
        setSyncDialog({ email, role: newRole, conflicts: result.conflicts ?? [], driveAdminNarrowedFolders: narrowed })
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update role')
    }
  }

  const handleRemove = async (email: string, name: string) => {
    if (!tenantId) return
    const leaving = !!userEmail && email.toLowerCase() === userEmail.toLowerCase()
    const confirmed = await confirm(leaving
      // Leaving: the app adds what stays behind that the person should act on —
      // e.g. the keybook's keys they own, still working after they go.
      ? {
          title: t('settings.members.leaveTitle'),
          message: <div className="space-y-3"><p>{t('settings.members.leaveMessage')}</p>{renderLeaveNotice?.(tenantId)}</div>,
          confirmText: t('settings.members.leaveConfirm'),
          type: 'danger',
        }
      : {
          title: 'Remove member',
          message: `Remove ${name} (${email}) from this organization? They will lose access to all org files.`,
          type: 'danger',
        })
    if (!confirmed) return
    try {
      await request(`/api/orgs/${tenantId}/members/${encodeURIComponent(email)}`, { method: 'DELETE' })
      toast.success('Member removed')
      loadMembers()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to remove')
    }
  }

  /**
   * Client-side filter on name + email. The members endpoint returns the whole
   * org in one response (no paging), so everything being filtered is already in
   * state — no request, no debounce.
   *
   * PENDING INVITES ARE FILTERED TOO. They render as extra rows in this same
   * table, so filtering only `members` would leave every invite visible under
   * a query none of them match — the search would look broken on exactly the
   * rows an admin is most likely to be hunting for. An invite carries no name
   * of its own in this response, so `name` is optional and the email match
   * carries it.
   */
  const q = query.trim().toLowerCase()
  const matchesQuery = useCallback((name: string | undefined, email: string) =>
    !q || (name ?? '').toLowerCase().includes(q) || email.toLowerCase().includes(q), [q])
  const matchesRole = useCallback((role: string) =>
    roleFilter === 'all' || role === roleFilter, [roleFilter])
  const filtering = q.length > 0 || roleFilter !== 'all'

  /**
   * PLATFORM SUPPORT SEATS RENDER IN THEIR OWN SECTION, NOT INLINE.
   *
   * A support seat is a platform administrator we placed in this organization
   * to maintain an app we provide. Listed among the customer's own people it is
   * indistinguishable from someone they hired — and it is excluded from the
   * seat count printed directly above the table, so inline it also makes that
   * number look wrong.
   *
   * Grouped on `seatMarked`, NOT on `platformSeat`: the server excludes a seat
   * from the billable count on the MARKER alone (`seatIsBillable`), so grouping
   * on the money claim would drop a demoted administrator's still-free seat
   * back among the customer's members while the count kept excluding them. The
   * list has to add up to the number above it.
   *
   * NO `?? platformSeat` FALLBACK, deliberately. Both fields were added to
   * `GET /:id/members` in the same change, so an older API mid-deploy sends
   * NEITHER — the fallback could never fire on this route, and if some
   * intermediate server ever did send `platformSeat` alone it would group the
   * list on the money claim, which is exactly what the paragraph above forbids.
   * Absent means "not a support seat", which renders as this page always did.
   */
  const isSupportSeat = useCallback((m: MemberInfo) => m.seatMarked === true, [])
  const ownMembers = useMemo(() => members.filter(m => !isSupportSeat(m)), [members, isSupportSeat])
  const supportMembers = useMemo(() => members.filter(isSupportSeat), [members, isSupportSeat])

  const applyFilter = useCallback(
    (rows: MemberInfo[]) => (filtering
      ? rows.filter(m => matchesQuery(m.name, m.email) && matchesRole(m.role))
      : rows),
    [filtering, matchesQuery, matchesRole])
  const visibleMembers = useMemo(() => applyFilter(ownMembers), [applyFilter, ownMembers])
  // The same filter runs over BOTH tables, so a search that matches only a
  // support seat still finds it. If it applied to one, the section below would
  // sit there unfiltered under a query none of its rows match — which is the
  // "search looks broken" failure the invite filtering note above describes.
  const visibleSupport = useMemo(() => applyFilter(supportMembers), [applyFilter, supportMembers])
  const visibleInvites = useMemo(
    () => (filtering ? invites.filter(inv => matchesQuery(inv.name, inv.email) && matchesRole(inv.role)) : invites),
    [filtering, invites, matchesQuery, matchesRole])
  // Counts the support table too: with it excluded, a query matching only a
  // support seat would print "No members match" directly above the row it
  // matched.
  const noMatches = filtering && visibleMembers.length === 0
    && visibleInvites.length === 0 && visibleSupport.length === 0
  /**
   * The filter matched, but everything it matched is a SUPPORT seat.
   *
   * `noMatches` is correctly false — there is a match, and it renders in the
   * section below — but the main table's body is empty, and its own empty
   * state is the `noMatches` row it just declined to show. Left alone, the
   * table renders five column headers over nothing while the row the admin
   * searched for sits further down the page under a different heading. Before
   * the split, the one table showed that row; this says where it went.
   */
  const onlySupportMatches = filtering && !noMatches
    && visibleMembers.length === 0 && visibleInvites.length === 0

  /**
   * Roles offered by the filter = the roles actually PRESENT (members and
   * pending invites both, since both are rows in this table), ordered by
   * `ROLE_OPTIONS` so the list reads owner → … → no-access exactly like the
   * assignment picker. Offering all six regardless would put options in the
   * list that can only ever produce an empty table.
   *
   * Any role not in `ROLE_OPTIONS` — a custom role from the server — is
   * appended rather than dropped, so it stays filterable instead of silently
   * being unreachable.
   */
  const rolesPresent = useMemo(() => {
    const present = new Set<string>([...members.map(m => m.role), ...invites.map(inv => inv.role)])
    const known = ROLE_OPTIONS.map(r => r.value).filter(v => present.has(v))
    const extra = [...present].filter(v => !ROLE_OPTIONS.some(r => r.value === v)).sort()
    return [...known, ...extra]
  }, [members, invites])

  /**
   * ONE member row, rendered by BOTH tables — the customer's own members and
   * the platform support seats below them. Extracted rather than duplicated:
   * the two tables show the same five columns with the same controls, and a
   * second copy is how the support section would quietly stop growing a column
   * the main table gained.
   *
   * The last-owner guard inside deliberately counts `members`, not the list
   * being rendered: a support seat holding `owner` is still an owner, and
   * measuring against one half of the split would offer Remove on the org's
   * only one.
   */
  const memberRow = (m: MemberInfo) => (
    <tr key={m.email} className="border-b themed-border last:border-b-0">
      <td className="px-4 py-2.5">
        <div className="themed-text">{m.name}</div>
        {(() => {
          const isSelf = userEmail && m.email.toLowerCase() === userEmail.toLowerCase()
          const isLastOwner = m.role === 'owner' && members.filter(x => x.role === 'owner').length <= 1
          const canRemove = canManageRole(m.role) && !isLastOwner
          const canLeave = isSelf && m.role !== 'owner' || (isSelf && !isLastOwner)
          if (!canRemove && !canLeave) return null
          return (
            <button
              onClick={() => handleRemove(m.email, m.name)}
              className="text-[10px] text-red-400 hover:text-red-300 transition-colors"
            >
              {isSelf ? 'Leave' : 'Remove'}
            </button>
          )
        })()}
      </td>
      <td className="px-4 py-2.5 themed-text-muted font-mono text-xs">{m.email}</td>
      <td className="px-4 py-2.5">
        {canManageRole(m.role) ? (
          <RolePicker value={m.role} onChange={r => handleRoleChange(m.email, r)} canAssignOwner={canManageOwner} canAssignAdmin={canManageAdmin} size="xs" />
        ) : (
          <span className={`text-xs px-2 py-0.5 rounded ${
            m.role === 'owner' ? 'bg-purple-500/20 text-purple-300' :
            m.role === 'admin' ? 'bg-blue-500/20 text-blue-300' :
            m.role === 'noaccess' ? 'bg-red-500/20 text-red-300' :
            m.role === 'guest' ? 'bg-amber-500/20 text-amber-300' :
            'themed-text-muted'
          }`}>
            {ROLE_LABEL[m.role] || m.role}
          </span>
        )}
      </td>
      <td className="px-4 py-2.5 text-xs">
        {(() => {
          const teams = memberTeams[m.email.toLowerCase()]
          const count = teams?.length ?? 0
          if (canManage) {
            return (
              <button
                onClick={() => setMemberTeamsEmail(m.email)}
                className="text-blue-400 hover:text-blue-300 transition-colors"
              >
                {count > 0 ? `${count} team${count > 1 ? 's' : ''}` : '+ Add'}
              </button>
            )
          }
          return <span className="themed-text-muted">{count > 0 ? `${count} team${count > 1 ? 's' : ''}` : '—'}</span>
        })()}
      </td>
      <td className="px-4 py-2.5 text-xs">
        {canManage ? (
          <button
            onClick={() => setMemberAccessEmail(m.email)}
            className="text-blue-400 hover:text-blue-300 transition-colors"
          >
            {m.accessCount && m.accessCount > 0
              ? `${m.accessCount} item${m.accessCount > 1 ? 's' : ''}`
              : '+ Add'}
          </button>
        ) : userEmail && m.email.toLowerCase() === userEmail.toLowerCase() && m.accessCount && m.accessCount > 0 ? (
          <button
            onClick={() => setMemberAccessEmail(m.email)}
            className="text-blue-400/70 hover:text-blue-300 transition-colors underline underline-offset-2"
          >
            {m.accessCount} item{m.accessCount > 1 ? 's' : ''}
          </button>
        ) : (
          <span className="themed-text-muted">
            {m.accessCount && m.accessCount > 0
              ? `${m.accessCount} item${m.accessCount > 1 ? 's' : ''}`
              : '—'}
          </span>
        )}
      </td>
    </tr>
  )

  return (
    <div>
      {/* Seat capacity sits WITH the heading, not in the list: it describes the
          whole org, and it is the number an admin needs BEFORE deciding to
          invite rather than after being refused.

          `<billable> / <max>`, or the bare count when there is no ceiling —
          which is the case on every deployment today, since `max_seats` stays
          null until Gate 3's final step. So the no-denominator branch is the
          primary one, not a fallback. "Unlimited" is never printed as a number.

          Hidden entirely when `seats` is absent (older API mid-deploy). */}
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <h2 className="text-lg font-medium themed-text">Members</h2>
        {seats && (
          <span className="text-sm themed-text-secondary tabular-nums">
            {seats.billable}
            {typeof seats.max === 'number' && (
              <span className="themed-text-muted"> / {seats.max}</span>
            )}
            <span className="themed-text-muted"> {seats.billable === 1 ? 'seat' : 'seats'}</span>
            {/* Over the ceiling is reachable without anything being broken: the
                first owner is seated exemptly, and a cap can be lowered under an
                org that already exceeds it. Naming it stops "3 / 2" reading as a
                rendering fault. */}
            {typeof seats.max === 'number' && seats.billable > seats.max && (
              <span className="text-amber-400"> · over</span>
            )}
            {/* Platform support seats are EXCLUDED from the count to its left —
                the platform provisioned them and the platform pays for them —
                so the number is smaller than the rows on this page. Said here,
                beside the figure, because a bare "2 seats" over a five-row list
                reads as a rendering fault. Absent on an older API mid-deploy,
                and 0 prints nothing rather than "0 support seats". */}
            {!!seats.support && (
              <span className="themed-text-muted">
                {' '}· {seats.support} support {seats.support === 1 ? 'seat' : 'seats'}, not counted
              </span>
            )}
          </span>
        )}
      </div>

      {/* A failed capability fetch collapses this whole surface to read-only —
          no invite form, no role edits — which is exactly what a demotion looks
          like. Name the difference (#341). */}
      {capsUnavailable && (
        <CapabilitiesUnavailableNotice
          onRetry={() => void retryCaps()}
          subject={t('capabilitiesNotice.memberManagementSubject')}
        />
      )}

      {/* Invite form */}
      {canManage && (
        <div className="themed-bg-secondary rounded-lg p-4 mb-4">
          <label className="text-sm themed-text-secondary mb-2 block">Invite member</label>
          <div className="flex gap-2">
            <input
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="email@example.com"
              onKeyDown={e => { if (e.key === 'Enter') handleInvite() }}
              className="flex-1 themed-input border rounded-md px-3 py-1.5 text-sm outline-none focus:border-blue-500"
            />
            <RolePicker value={inviteRole} onChange={setInviteRole} canAssignOwner={canManageOwner} canAssignAdmin={canManageAdmin} />
            <button
              onClick={handleInvite}
              disabled={inviting || !inviteEmail.includes('@')}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md transition-colors"
            >
              {inviting ? '...' : 'Invite'}
            </button>
          </div>
          <label className="flex items-center gap-2 mt-2 text-xs themed-text-muted cursor-pointer">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={e => setSendEmail(e.target.checked)}
              className="rounded"
            />
            Send invitation email
          </label>
        </div>
      )}

      {/* Filter — icon-in-input follows the drive's filter controls
          (ProjectsView / SharedView), but on the SETTINGS surface recipe
          (`themed-input` + blue focus ring) rather than the drive's
          `ws-drive-pill`, matching the invite field directly above it. */}
      {/* Gated on there being ANY row on this page to filter — the customer's
          own members, pending invites, or a support seat in the section at the
          bottom. Deliberately NOT the main table's own condition: `applyFilter`
          runs over the support table too, so an org whose only membership rows
          are support seats has something to search even though the table below
          correctly says "No members yet". The one state this must prevent —
          a search field over an entirely empty page — is the case where all
          three lists are empty, which is what this tests. */}
      {!loading && (members.length > 0 || invites.length > 0) && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {/* min-w keeps the field usable on a narrow phone; with flex-wrap the
              role select drops to its own line rather than crushing it. */}
          <div className="relative flex-1 min-w-[160px] max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 themed-text-muted pointer-events-none" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') setQuery('') }}
              placeholder={t('settings.members.searchPlaceholder')}
              aria-label={t('settings.members.searchPlaceholder')}
              data-testid="members-search"
              className="w-full themed-input border rounded-md pl-9 pr-3 py-1.5 text-sm outline-none focus:border-blue-500"
            />
          </div>
          {/* Role labels come from ROLE_LABEL — the SAME map the table's role
              cells render — so an option can never disagree with the rows it
              filters. Only "All roles" is a new string, and it is translated.
              Shown once there is more than one role to choose between. */}
          {/* The SAME control as the role cells it filters — one shell, two
              option sets. Labels come from ROLE_LABEL, so a filter option can
              never disagree with the rows beneath it, and no `desc` because a
              filter has nothing to explain. */}
          {rolesPresent.length > 1 && (
            <div className="shrink-0">
              <PickerMenu
                value={roleFilter}
                options={[
                  { value: 'all', label: t('settings.members.roleFilterAll') },
                  ...rolesPresent.map(r => ({ value: r, label: ROLE_LABEL[r] || r })),
                ]}
                onChange={setRoleFilter}
                ariaLabel={t('settings.members.roleFilterLabel')}
                testId="members-role-filter"
              />
            </div>
          )}
        </div>
      )}

      {/* Member list */}
      <div className="themed-bg-secondary rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-4 flex items-center gap-2 themed-text-muted">
            <Loader2 size={16} className="animate-spin" /> Loading members...
          </div>
        ) : ownMembers.length === 0 && invites.length === 0 ? (
          /* `ownMembers`, not `members`: an org whose only rows are platform
             support seats has no members of its own, and saying so is right —
             the seats themselves are listed in their own section below rather
             than being lost with this message. */
          <div className="p-4 text-sm themed-text-muted">No members yet</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b themed-border">
                <th className="text-left px-4 py-2 themed-text-secondary font-medium">Name</th>
                <th className="text-left px-4 py-2 themed-text-secondary font-medium">Email</th>
                <th className="text-left px-4 py-2 themed-text-secondary font-medium">Role</th>
                <th className="text-left px-4 py-2 themed-text-secondary font-medium">Teams</th>
                <th className="text-left px-4 py-2 themed-text-secondary font-medium">Access</th>
              </tr>
            </thead>
            <tbody>
              {/* A filter that matches nothing must not reuse "No members yet"
                  — on a populated org that reads as data loss rather than as
                  an empty result. Headers stay visible so the table still
                  explains itself. */}
              {noMatches && (
                <tr>
                  <td colSpan={5} className="px-4 py-3 text-sm themed-text-muted">
                    {/* Name the filter(s) actually in force. "No members match
                        “kok”" while a role filter is also narrowing the table
                        would send an admin looking for a member who is right
                        there under a different role. */}
                    {q && roleFilter !== 'all'
                      ? t('settings.members.noMatchesWithRole', { query: query.trim() })
                      : q
                        ? t('settings.members.noMatches', { query: query.trim() })
                        : t('settings.members.noRoleMatches')}
                  </td>
                </tr>
              )}
              {/* The match is real, it is just not in THIS table — point at the
                  section holding it rather than leaving bare headers. */}
              {onlySupportMatches && (
                <tr>
                  <td colSpan={5} className="px-4 py-3 text-sm themed-text-muted">
                    {t('settings.members.onlySupportMatches')}
                  </td>
                </tr>
              )}
              {visibleMembers.map(memberRow)}
              {visibleInvites.map(inv => (
                <tr key={`inv-${inv.email}`} className="border-b themed-border last:border-b-0">
                  <td className="px-4 py-2.5 opacity-60">
                    {/* Gated on the role the INVITE carries, like the member
                        row's Remove — reinvite re-issues that role and revoke
                        cancels it, and the server now refuses both from a
                        caller who could not have granted it. */}
                    {canManageRole(inv.role) && (
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleReinvite(inv.inviteId!, inv.email)}
                          className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          Reinvite
                        </button>
                        <button
                          onClick={() => handleCancelInvite(inv.inviteId!, inv.email)}
                          className="text-xs text-red-400 hover:text-red-300 transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 themed-text-muted font-mono text-xs opacity-60">
                    {inv.email}
                    {inv.status === 'declined' && <span className="ml-2 text-[10px] text-orange-400 font-sans font-medium">Declined</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    {canManageRole(inv.role) ? (
                      <RolePicker
                        value={inv.role}
                        onChange={async (newRole) => {
                          // Raising a pending invite to owner confers the same
                          // thing the invite form does, so it asks the same way.
                          if (newRole === 'owner' && !(await confirmOwnerGrant(inv.email, false))) return
                          try {
                            await request(`/api/orgs/${tenantId}/invite/${inv.inviteId}`, {
                              method: 'PATCH',
                              body: JSON.stringify({ role: newRole }),
                              headers: { 'Content-Type': 'application/json' },
                            })
                            toast.success('Invite role updated')
                            loadMembers()
                          } catch (err: any) { toast.error(err?.message || 'Failed to update') }
                        }}
                        canAssignOwner={canManageOwner}
                        canAssignAdmin={canManageAdmin}
                        size="xs"
                      />
                    ) : (
                      <span className="text-xs themed-text-muted">{ROLE_LABEL[inv.role] || inv.role}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs opacity-60">
                    {/* Teams can be pre-staged on a pending invite (#112).
                        Skip declined invites — re-inviting clears that
                        state first. */}
                    {canManage && inv.status !== 'declined' ? (
                      <button
                        onClick={() => setMemberTeamsEmail(inv.email)}
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        + Add
                      </button>
                    ) : <span className="themed-text-muted">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-xs opacity-60">
                    {canManage && inv.status !== 'declined' ? (
                      <button
                        onClick={() => setMemberAccessEmail(inv.email)}
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        + Add
                      </button>
                    ) : <span className="themed-text-muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Support access ────────────────────────────────────────────────
          Platform support seats (0180): a platform administrator placed in
          this organization to maintain an app we provide. Listed apart from
          the customer's own people because inline they are indistinguishable
          from someone the customer hired, and because they are excluded from
          the seat count above — inline, that number would look wrong.

          EVERY WORD HERE IS A SEAT FACT, and that is a constraint, not a
          style. This section is grouped on `seatMarked` — the marker alone,
          which is what the count excludes on — so the only thing true of every
          row in it is that the row is marked and therefore not counted.

          Two claims that are NOT true of every row, and must never appear:

            "paid for by the platform" — the SPEND claim. It needs the holder
            to still confer platform administration (`platformSeat`), which is
            what `resolvePlatformPayer` actually enforces. Nothing clears the
            marker when someone leaves the platform team, so for a demoted
            administrator this section still lists them while their AI turns
            have gone back to billing the CUSTOMER. Telling the payer we are
            paying is the shown-vs-enforced split, aimed at the one person it
            costs money. That claim lives on the Spending dashboard, beside the
            spend it describes, where `platformSeat` answers it.

            "with access" — a marked row may hold `noaccess`, in which case it
            has none, and the row's own red chip says so two columns away.

          Rendered only when there is something to show, so an ordinary
          organization's Members page is byte-identical to before. */}
      {!loading && visibleSupport.length > 0 && (
        <div className="mt-6">
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <h3 className="text-sm font-medium themed-text">Support access</h3>
            <span className="text-xs themed-text-muted">
              Not counted toward your seats
            </span>
          </div>
          <p className="text-xs themed-text-muted mb-3">
            Seats the platform placed in this organization to maintain apps we
            provide. They do not count toward your seat limit.
          </p>
          <div className="themed-bg-secondary rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b themed-border">
                  <th className="text-left px-4 py-2 themed-text-secondary font-medium">Name</th>
                  <th className="text-left px-4 py-2 themed-text-secondary font-medium">Email</th>
                  <th className="text-left px-4 py-2 themed-text-secondary font-medium">Role</th>
                  <th className="text-left px-4 py-2 themed-text-secondary font-medium">Teams</th>
                  <th className="text-left px-4 py-2 themed-text-secondary font-medium">Access</th>
                </tr>
              </thead>
              <tbody>
                {visibleSupport.map(memberRow)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Member teams dialog */}
      {memberTeamsEmail && tenantId && (
        <MemberTeamsDialog
          email={memberTeamsEmail}
          tenantId={tenantId}
          onClose={() => { setMemberTeamsEmail(null); loadMembers() }}
        />
      )}

      {/* Member access dialog */}
      {memberAccessEmail && (
        <MemberAccessOverview
          email={memberAccessEmail}
          onClose={() => { setMemberAccessEmail(null); loadMembers() }}
          onPickFile={canManage ? onPickFile : undefined}
          readOnly={!canManage}
        />
      )}
      {syncDialog && tenantId && (
        <AccessSyncDialog
          email={syncDialog.email}
          role={syncDialog.role}
          conflicts={syncDialog.conflicts}
          driveAdminNarrowedFolders={syncDialog.driveAdminNarrowedFolders}
          tenantId={tenantId}
          onClose={() => setSyncDialog(null)}
          onApplied={() => { setSyncDialog(null); loadMembers() }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Teams page — Discord-style split layout: sidebar list + editor panel
// ---------------------------------------------------------------------------

interface TeamInfo {
  id: string
  tenantId: string
  name: string
  parentId: string | null
  priority: number
  createdBy: string
  memberCount?: number
  createdAt: string
  updatedAt: string
}

interface TeamMember {
  id: string
  groupId: string
  email: string
  name?: string | null
  role: string  // 'member' | 'admin'
  addedBy: string
  createdAt: string
}

/** Dialog to manage which teams a member belongs to — level-based navigation like team settings */
function MemberTeamsDialog({ email, tenantId, onClose }: { email: string; tenantId: string; onClose: () => void }) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const toast = useToast()
  const [allTeams, setAllTeams] = useState<Array<{ id: string; name: string; parentId: string | null; memberCount?: number }>>([])
  const [memberOf, setMemberOf] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [viewingParentId, setViewingParentId] = useState<string | null>(null)
  const [slideDir, setSlideDir] = useState<'in' | 'out' | null>(null)
  const slideKey = useRef(0)

  useEffect(() => {
    setLoading(true)
    request<{ teams: Array<{ id: string; name: string; parentId: string | null; memberCount?: number }> }>(`/api/orgs/${tenantId}/teams`)
      .then(async (data) => {
        setAllTeams(data.teams)
        const inTeams = new Set<string>()
        await Promise.all(data.teams.map(async (team) => {
          const tmData = await request<{ members: Array<{ email: string }> }>(`/api/orgs/${tenantId}/teams/${team.id}/members`)
          if (tmData.members.some(m => m.email.toLowerCase() === email.toLowerCase())) {
            inTeams.add(team.id)
          }
        }))
        setMemberOf(inTeams)
      })
      .catch(() => toast.error('Failed to load teams'))
      .finally(() => setLoading(false))
  }, [tenantId, email]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (teamId: string) => {
    setSaving(teamId)
    const isIn = memberOf.has(teamId)
    try {
      if (isIn) {
        await request(`/api/orgs/${tenantId}/teams/${teamId}/members/${encodeURIComponent(email)}`, { method: 'DELETE' })
        setMemberOf(prev => { const next = new Set(prev); next.delete(teamId); return next })
      } else {
        await request(`/api/orgs/${tenantId}/teams/${teamId}/members`, {
          method: 'POST',
          body: JSON.stringify({ email }),
          headers: { 'Content-Type': 'application/json' },
        })
        setMemberOf(prev => new Set(prev).add(teamId))
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update')
    } finally { setSaving(null) }
  }

  const visibleTeams = allTeams.filter(t => (t.parentId ?? null) === viewingParentId)
  const viewingParent = viewingParentId ? allTeams.find(t => t.id === viewingParentId) : null

  // Check if any descendant (recursively) is in memberOf
  const hasDescendantMember = (teamId: string): boolean => {
    const children = allTeams.filter(t => t.parentId === teamId)
    return children.some(c => memberOf.has(c.id) || hasDescendantMember(c.id))
  }

  const drillIn = (teamId: string) => {
    slideKey.current++
    setSlideDir('in')
    setViewingParentId(teamId)
  }

  const goUp = () => {
    if (!viewingParent) return
    slideKey.current++
    setSlideDir('out')
    setViewingParentId(viewingParent.parentId ?? null)
  }

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative z-10 rounded-lg shadow-xl max-w-sm w-full ${themeClass} themed-bg-secondary`}>
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b themed-border">
          <div>
            <h3 className="text-base font-semibold themed-text">Teams</h3>
            <p className="text-xs themed-text-muted mt-0.5 font-mono">{email}</p>
          </div>
          <button onClick={onClose} className="p-1 themed-text-muted hover:themed-text transition-colors rounded">
            <X size={18} />
          </button>
        </div>
        <div className="max-h-[50vh] overflow-hidden">
          {loading ? (
            <div className="py-6 text-center themed-text-muted text-sm">Loading...</div>
          ) : allTeams.length === 0 ? (
            <div className="py-6 text-center themed-text-muted text-sm">No teams created yet</div>
          ) : (
            <div
              key={slideKey.current}
              className="overflow-y-auto max-h-[50vh]"
              style={slideDir ? {
                animation: `${slideDir === 'in' ? 'groupSlideIn' : 'groupSlideOut'} 200ms ease both`,
              } : undefined}
              onAnimationEnd={() => setSlideDir(null)}
            >
              {/* Back button */}
              {viewingParent && (
                <button
                  onClick={goUp}
                  className="w-full flex items-center gap-1.5 px-4 py-2 text-sm themed-text hover:themed-bg-tertiary transition-colors border-b themed-border"
                >
                  <ChevronLeft size={14} className="themed-text-muted" />
                  <span className="themed-text-muted">{viewingParent.name}</span>
                </button>
              )}
              {visibleTeams.length === 0 ? (
                <div className="py-4 text-center text-xs themed-text-muted">No sub-teams</div>
              ) : (
                visibleTeams.map(team => {
                  const isIn = memberOf.has(team.id)
                  const isSaving = saving === team.id
                  const hasSemiSelected = !isIn && hasDescendantMember(team.id)
                  return (
                    <div
                      key={team.id}
                      onClick={() => drillIn(team.id)}
                      className={`flex items-center gap-2 px-4 py-2 text-sm cursor-pointer transition-colors ${
                        isIn ? 'bg-blue-600/10' : 'hover:themed-bg-tertiary'
                      }`}
                    >
                      <div
                        onClick={e => { e.stopPropagation(); if (!isSaving) toggle(team.id) }}
                        className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 cursor-pointer ${
                          isIn ? 'bg-blue-600 border-blue-600'
                          : hasSemiSelected ? 'bg-blue-600/40 border-blue-500'
                          : 'themed-border hover:border-blue-400'
                        } ${isSaving ? 'opacity-50' : ''}`}
                      >
                        {isIn && <Check size={10} className="text-white" />}
                        {hasSemiSelected && !isIn && <Minus size={10} className="text-white" />}
                      </div>
                      <span className={`flex-1 truncate ${isIn ? 'text-blue-300' : 'themed-text'}`}>{team.name}</span>
                      <span className="text-[10px] themed-text-muted shrink-0">({team.memberCount ?? 0})</span>
                      <ChevronRight size={14} className="themed-text-muted shrink-0" />
                    </div>
                  )
                })
              )}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t themed-border flex justify-end">
          <button onClick={onClose} className="px-4 py-2 themed-btn-secondary rounded-lg text-sm font-medium">
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

function TeamsPage({ tenantId, userEmail, onRevealInDrive, activeTeamId, activeTab: activeTabProp, onNavigate: onNavProp, createTeam }: {
  tenantId?: string
  userEmail?: string
  onRevealInDrive?: (folderId: string, parentFolderId?: string | null) => void
  activeTeamId?: string
  activeTab?: 'members' | 'access'
  onNavigate?: (teamId?: string, tab?: string) => void
  /** Keybook-aware team creator. When omitted (e.g., personal tenants
   *  with no keybook), falls through to the legacy direct POST. */
  createTeam?: (input: { tenantId: string; name: string; parentId?: string }) => Promise<{ ok: boolean; team: TeamInfo }>
}) {
  const [teams, setTeams] = useState<TeamInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedTeamId, _setSelectedTeamId] = useState<string | null>(activeTeamId ?? null)
  /**
   * Latest-ref for the selection, read by `loadTeams`' auto-select guard.
   *
   * `loadTeams` deliberately does NOT take `selectedTeamId` as a dependency:
   * it is the `onUpdated` callback for the editor and the effect body of
   * `useEffect(() => { loadTeams() }, [loadTeams])`, so a new identity per
   * selection would refetch every team — plus a members request PER TEAM for
   * a non-org-admin — on every click in the list.
   *
   * Reading the state directly under that dep array is what broke it: the
   * closure kept the FIRST render's value (`null` whenever the page is opened
   * without a team in the URL), so "auto-select first root team on initial
   * load" fired on EVERY load. Adding a member to Operations calls
   * `onUpdated` → `loadTeams` → guard sees a stale `null` → selection jumps
   * to the first root team, and the left list re-levels with it.
   */
  const selectedTeamIdRef = useRef(selectedTeamId)
  selectedTeamIdRef.current = selectedTeamId
  const setSelectedTeamId = useCallback((id: string | null, tab?: string) => {
    _setSelectedTeamId(id)
    selectedTeamIdRef.current = id
    onNavProp?.(id ?? undefined, tab)
  }, [onNavProp])
  const [viewingParentId, setViewingParentId] = useState<string | null>(null) // null = root level
  const [slideDir, setSlideDir] = useState<'in' | 'out' | null>(null)
  const slideKey = useRef(0)
  const [creating, setCreating] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [dragOverId, setDragOverId] = useState<string | null>(null)
  const [dragAction, setDragAction] = useState<'reorder' | 'nest'>('reorder')
  const dragItemId = useRef<string | null>(null)
  const dragGrabX = useRef(0) // X offset within source where user grabbed
  const nestTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const nestTargetId = useRef<string | null>(null)
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const posCache = useRef<Map<string, number>>(new Map())
  const toast = useToast()
  const confirm = useConfirm()
  // #1102 item 1 — capabilities, never the role. The server decides a team action with the team-scoped caps, which for a
  // team with no team-specific grant fall through to the TENANT-level answer (an org admin's default, or an owner's
  // edit of it) — the same answer `my-capabilities` returns for them. `manage_team:write` is the top-level team
  // creator. All three read false while loading: actions stay hidden, never offered and refused.
  const { can, loading: capsLoading, unavailable: capsUnavailable, retry: retryCaps } = useMyCapabilities(tenantId, false, userEmail)
  const isOrgAdmin = can('manage_subteam', 'write')
  const canRenameAnyTeam = can('rename_team', true)
  const canCreateRootTeam = can('manage_team', 'write')
  const [myAdminTeamIds, setMyAdminTeamIds] = useState<Set<string>>(new Set())

  const loadTeams = useCallback(async () => {
    // wait for the capability answer: before it arrives `isOrgAdmin` reads false, and an org admin's first load would
    // take the non-admin path (one members request per team) and then run again
    if (!tenantId || capsLoading) return
    try {
      const data = await request<{ teams: TeamInfo[] }>(`/api/orgs/${tenantId}/teams`)
      setTeams(data.teams)

      // Fetch which teams the current user is admin of
      if (!isOrgAdmin && userEmail) {
        const adminIds = new Set<string>()
        // a team's admin is IN it: only the caller's own teams are asked (a `manage_team` reader sees every team, and
        // asking each one would be a request per team in the tenant)
        // (a failed /teams/my falls back to asking every listed team, as before — never "admin of nothing")
        const mine = await request<{ teams: Array<{ id: string }> }>(`/api/orgs/${tenantId}/teams/my`).then(r => new Set(r.teams.map(t => t.id))).catch(() => null)
        const askOne = async (team: TeamInfo) => {
          try {
            const tmData = await request<{ members: Array<{ email: string; role: string }> }>(`/api/orgs/${tenantId}/teams/${team.id}/members`)
            if (tmData.members.some(m => m.email.toLowerCase() === userEmail.toLowerCase() && m.role === 'admin')) {
              adminIds.add(team.id)
            }
          } catch { /* ignore */ }
        }
        // own teams (a few) at once; the fallback over every listed team in a bounded pool — never a burst, never serial
        const queue = data.teams.filter(t => !mine || mine.has(t.id))
        let next = 0
        await Promise.all(Array.from({ length: Math.min(6, queue.length) }, async () => {
          while (next < queue.length) await askOne(queue[next++])
        }))
        setMyAdminTeamIds(adminIds)
      }
      // Auto-select the first root team when NOTHING is selected — the state
      // is read through the ref so this stays true of the current selection
      // rather than the one captured when this callback was created.
      if (!selectedTeamIdRef.current && data.teams.length > 0) {
        const firstRoot = data.teams.find(t => !t.parentId)
        if (firstRoot) setSelectedTeamId(firstRoot.id)
      }
    } catch { /* silent */ }
    finally { setLoading(false) }
  }, [tenantId, isOrgAdmin, capsLoading, userEmail]) // eslint-disable-line react-hooks/exhaustive-deps

  // Check if user is admin of a team or any ancestor (recursive)
  const isTeamAdminOf = useCallback((teamId: string): boolean => {
    if (isOrgAdmin) return true
    let tid: string | null = teamId
    while (tid) {
      if (myAdminTeamIds.has(tid)) return true
      const team = teams.find(t => t.id === tid)
      tid = team?.parentId ?? null
    }
    return false
  }, [isOrgAdmin, myAdminTeamIds, teams])

  // Per-team permission checks
  const canManageTeam = useCallback((teamId: string) => isTeamAdminOf(teamId), [isTeamAdminOf])
  const canRenameTeam = useCallback((teamId: string) => {
    if (isOrgAdmin || canRenameAnyTeam) return true
    const team = teams.find(t => t.id === teamId)
    if (!team?.parentId) return false // Root team — org admin only
    return isTeamAdminOf(team.parentId)
  }, [isOrgAdmin, canRenameAnyTeam, teams, isTeamAdminOf])
  const canDeleteTeam = useCallback((teamId: string) => {
    if (isOrgAdmin) return true
    const team = teams.find(t => t.id === teamId)
    if (!team?.parentId) return false // Root team — org admin only
    return isTeamAdminOf(team.parentId)
  }, [isOrgAdmin, teams, isTeamAdminOf])

  useEffect(() => { loadTeams() }, [loadTeams])

  // Sync from URL param
  useEffect(() => {
    if (activeTeamId && activeTeamId !== selectedTeamId) {
      _setSelectedTeamId(activeTeamId)
      // Navigate sidebar to show this team's parent level
      const team = teams.find(t => t.id === activeTeamId)
      if (team) setViewingParentId(team.parentId ?? null)
    }
  }, [activeTeamId]) // eslint-disable-line react-hooks/exhaustive-deps

  const [newInline, setNewInline] = useState(false)
  const [newName, setNewName] = useState('')

  const startCreate = () => {
    setNewInline(true)
    setNewName('')
  }

  const confirmCreate = async () => {
    if (!tenantId || !newName.trim()) { setNewInline(false); return }
    setCreating(true)
    try {
      const res = createTeam
        ? await createTeam({ tenantId, name: newName.trim(), parentId: viewingParentId || undefined })
        : await request<{ ok: boolean; team: TeamInfo }>(`/api/orgs/${tenantId}/teams`, {
            method: 'POST',
            body: JSON.stringify({ name: newName.trim(), parentId: viewingParentId || undefined }),
            headers: { 'Content-Type': 'application/json' },
          })
      setNewInline(false)
      await loadTeams()
      setSelectedTeamId(res.team.id)
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create team')
    } finally { setCreating(false) }
  }

  const cancelCreate = () => {
    setNewInline(false)
    setNewName('')
  }

  const handleDelete = async (team: TeamInfo) => {
    if (!tenantId) return
    // Count all descendant sub-teams
    const countDescendants = (id: string): number => {
      const children = teams.filter(t => t.parentId === id)
      return children.length + children.reduce((sum, c) => sum + countDescendants(c.id), 0)
    }
    const subCount = countDescendants(team.id)
    const parts = [`Delete "${team.name}"?`]
    if (subCount > 0) parts.push(`This will also delete ${subCount} sub-team${subCount !== 1 ? 's' : ''}.`)
    parts.push('All folder permissions assigned to this team will be removed.')
    const confirmed = await confirm({
      title: 'Delete team',
      message: parts.join(' '),
      type: 'danger',
    })
    if (!confirmed) return
    try {
      await request(`/api/orgs/${tenantId}/teams/${team.id}`, { method: 'DELETE' })
      toast.success(`Team "${team.name}" deleted`)
      if (selectedTeamId === team.id) {
        const siblings = visibleTeams.filter(t => t.id !== team.id)
        const idx = visibleTeams.findIndex(t => t.id === team.id)
        const next = siblings[Math.min(idx, siblings.length - 1)]
        setSelectedTeamId(next?.id ?? null)
      }
      loadTeams()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete')
    }
  }

  /** Move a team to a new parent with confirmation dialog. Returns true if moved. */
  const confirmMove = async (sourceId: string, newParentId: string | null): Promise<boolean> => {
    const source = teams.find(t => t.id === sourceId)
    if (!source) return false
    // #1102 — moving a team to the TOP LEVEL makes a top-level team: `manage_team:write`, as the server asks
    // (only once the answer is known — loading or unavailable, the server decides)
    if (newParentId === null && source.parentId && !canCreateRootTeam && !capsLoading && !capsUnavailable) {
      toast.error('Moving a team to the top level needs the "Manage all teams" permission')
      return false
    }
    const targetName = newParentId ? teams.find(t => t.id === newParentId)?.name ?? 'unknown' : 'root'
    const confirmed = await confirm({
      title: 'Move team',
      message: `Move "${source.name}" into ${newParentId ? `"${targetName}"` : 'the root level'}?`,
      type: 'info',
      confirmText: 'Move',
    })
    if (!confirmed) return false
    try {
      await request(`/api/orgs/${tenantId}/teams/${sourceId}`, {
        method: 'PATCH',
        body: JSON.stringify({ parentId: newParentId }),
        headers: { 'Content-Type': 'application/json' },
      })
      return true
    } catch (err: any) {
      toast.error(err?.message || 'Failed to move team')
      return false
    }
  }

  const handleRename = async (teamId: string) => {
    if (!tenantId || !editName.trim()) { setEditingId(null); return }
    try {
      await request(`/api/orgs/${tenantId}/teams/${teamId}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: editName.trim() }),
        headers: { 'Content-Type': 'application/json' },
      })
      setEditingId(null)
      loadTeams()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to rename')
    }
  }

  // ── FLIP animation for reorder ───────────────────────────────────
  const capturePositions = () => {
    posCache.current.clear()
    rowRefs.current.forEach((el, id) => {
      posCache.current.set(id, el.getBoundingClientRect().top)
    })
  }

  // After React re-renders with new order, animate from old positions
  useEffect(() => {
    if (posCache.current.size === 0) return
    rowRefs.current.forEach((el, id) => {
      const oldTop = posCache.current.get(id)
      if (oldTop === undefined) return
      const newTop = el.getBoundingClientRect().top
      const delta = oldTop - newTop
      if (Math.abs(delta) < 1) return
      el.style.transition = 'none'
      el.style.transform = `translateY(${delta}px)`
      requestAnimationFrame(() => {
        el.style.transition = 'transform 200ms ease'
        el.style.transform = ''
      })
    })
    posCache.current.clear()
  }, [teams])

  // ── Drag-to-reorder ────────────────────────────────────────────────
  const handleDragStart = (e: React.DragEvent, teamId: string) => {
    dragItemId.current = teamId
    e.dataTransfer.effectAllowed = 'copyMove'
    // Record cursor offset from the grip handle center (~14px from row left).
    // During dragOver we use this to find where the ghost's grip sits in the target.
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    dragGrabX.current = e.clientX - rect.left - 14
  }

  const clearNestTimer = () => {
    if (nestTimer.current) { clearTimeout(nestTimer.current); nestTimer.current = null }
    nestTargetId.current = null
  }

  const handleDragOver = (e: React.DragEvent, teamId: string) => {
    e.preventDefault()
    setDragOverId(teamId)

    // Where the ghost's grip handle sits in the target row.
    // Dragging straight down → grip stays aligned (~14px) → reorder.
    // Nudging right (indenting) → grip moves over name area → nest.
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const gripInTarget = e.clientX - dragGrabX.current - rect.left
    const isNest = gripInTarget > 28
    setDragAction(isNest ? 'nest' : 'reorder')
    e.dataTransfer.dropEffect = isNest ? 'copy' : 'move'

    // Auto-drill: hover in nest mode on same team for 3s → slide into it
    if (isNest && teamId !== dragItemId.current) {
      if (nestTargetId.current !== teamId) {
        clearNestTimer()
        nestTargetId.current = teamId
        nestTimer.current = setTimeout(() => {
          if (dragItemId.current && nestTargetId.current === teamId) {
            drillInto(teamId)
            setDragAction('reorder')
            setDragOverId(null)
          }
          nestTimer.current = null
          nestTargetId.current = null
        }, 1000)
      }
    } else {
      clearNestTimer()
    }
  }

  const handleDrop = async (targetId: string) => {
    const sourceId = dragItemId.current
    const action = dragAction
    dragItemId.current = null
    setDragOverId(null)
    setDragAction('reorder')
    clearNestTimer()
    if (!sourceId || sourceId === targetId || !tenantId) return

    // ── Nest: make source a child of target ──────────────────────
    if (action === 'nest') {
      if (await confirmMove(sourceId, targetId)) loadTeams()
      return
    }

    // ── Reorder (or cross-level move + reorder) ────────────────
    const sourceInView = visibleTeams.some(t => t.id === sourceId)

    // Cross-level: move to this level and place at the drop position
    if (!sourceInView) {
      if (!(await confirmMove(sourceId, viewingParentId))) return
      try {
        const allIds = teams.map(t => t.id).filter(id => id !== sourceId)
        const tgtIdx = allIds.indexOf(targetId)
        if (tgtIdx !== -1) allIds.splice(tgtIdx, 0, sourceId)
        else allIds.push(sourceId)
        await request(`/api/orgs/${tenantId}/teams/reorder`, {
          method: 'PUT',
          body: JSON.stringify({ teamIds: allIds }),
          headers: { 'Content-Type': 'application/json' },
        })
      } catch { /* reorder is best-effort after move */ }
      loadTeams()
      return
    }

    const ids = teams.map(t => t.id)
    const sourceIdx = ids.indexOf(sourceId)
    const targetIdx = ids.indexOf(targetId)
    if (sourceIdx === -1 || targetIdx === -1) return

    ids.splice(sourceIdx, 1)
    // When dragging down, removing the source shifts indices — adjust so the
    // item lands above the target (where the blue line is), not below it.
    const insertIdx = sourceIdx < targetIdx ? targetIdx - 1 : targetIdx
    ids.splice(insertIdx, 0, sourceId)

    // Capture positions before DOM update for FLIP animation
    capturePositions()

    // Optimistic update
    const reordered = ids.map(id => teams.find(t => t.id === id)!).filter(Boolean)
    setTeams(reordered)

    try {
      await request(`/api/orgs/${tenantId}/teams/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ teamIds: ids }),
        headers: { 'Content-Type': 'application/json' },
      })
    } catch {
      toast.error('Failed to reorder')
      loadTeams()
    }
  }

  const handleDropToEnd = async () => {
    const sourceId = dragItemId.current
    dragItemId.current = null
    setDragOverId(null)
    clearNestTimer()
    if (!sourceId || !tenantId) return

    const sourceInView = visibleTeams.some(t => t.id === sourceId)

    // Cross-level: move to this level, place at the end
    if (!sourceInView) {
      if (!(await confirmMove(sourceId, viewingParentId))) return
      try {
        const lastSibling = visibleTeams[visibleTeams.length - 1]
        const allIds = teams.map(t => t.id).filter(id => id !== sourceId)
        const insertAt = lastSibling ? allIds.indexOf(lastSibling.id) + 1 : allIds.length
        allIds.splice(insertAt, 0, sourceId)
        await request(`/api/orgs/${tenantId}/teams/reorder`, {
          method: 'PUT',
          body: JSON.stringify({ teamIds: allIds }),
          headers: { 'Content-Type': 'application/json' },
        })
      } catch { /* reorder is best-effort after move */ }
      loadTeams()
      return
    }

    const ids = visibleTeams.map(t => t.id)
    const sourceIdx = ids.indexOf(sourceId)
    if (sourceIdx === -1 || sourceIdx === ids.length - 1) return

    ids.splice(sourceIdx, 1)
    ids.push(sourceId)

    // Capture positions before DOM update for FLIP animation
    capturePositions()

    // Full list with non-visible teams unchanged
    const allIds = teams.map(t => t.id)
    const reordered = ids.concat(allIds.filter(id => !ids.includes(id)))
    setTeams(reordered.map(id => teams.find(t => t.id === id)!).filter(Boolean))

    try {
      await request(`/api/orgs/${tenantId}/teams/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ teamIds: reordered }),
        headers: { 'Content-Type': 'application/json' },
      })
    } catch {
      toast.error('Failed to reorder')
      loadTeams()
    }
  }

  const handleDragEnd = () => {
    dragItemId.current = null
    setDragOverId(null)
    setDragAction('reorder')
    clearNestTimer()
  }

  const selectedTeam = teams.find(t => t.id === selectedTeamId) ?? null

  // Level-based navigation: show only teams at the current level
  const visibleTeams = teams.filter(t => (t.parentId ?? null) === viewingParentId)
  const viewingParent = viewingParentId ? teams.find(t => t.id === viewingParentId) : null
  const hasChildren = (teamId: string) => teams.some(t => t.parentId === teamId)

  // Navigate into a team's sub-teams
  const drillInto = (teamId: string) => {
    slideKey.current++
    setSlideDir('in')
    setViewingParentId(teamId)
    setSelectedTeamId(teamId)
  }

  // Navigate up one level
  const goUp = () => {
    if (!viewingParent) return
    slideKey.current++
    setSlideDir('out')
    setViewingParentId(viewingParent.parentId ?? null)
  }

  return (
    <div>
      <h2 className="text-lg font-medium themed-text mb-1">Teams</h2>
      {/* A failed capability fetch hides team actions exactly like a denial would — say which one it is (#341) */}
      {capsUnavailable && <CapabilitiesUnavailableNotice onRetry={() => void retryCaps()} />}
      <p className="text-xs themed-text-muted mb-4">
        Organize members into teams to manage folder access by group.
        Teams higher in the list have higher priority &mdash; when a member belongs to
        multiple teams with different permissions on the same folder, the higher team wins.
        <br/>Drag to reorder. Sub-teams inherit their parent's permissions unless overridden.
      </p>

      <div className="flex gap-4" style={{ minHeight: 320 }}>
        {/* ── Left sidebar: group list ──────────────────────────────── */}
        <div className="w-52 shrink-0 themed-bg-secondary rounded-lg flex flex-col overflow-hidden">
          {/* New team button */}
          {(viewingParentId ? canManageTeam(viewingParentId) : canCreateRootTeam) && (
            <button
              onClick={startCreate}
              disabled={creating || newInline}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-blue-400 hover:text-blue-300 hover:themed-bg-tertiary transition-colors border-b themed-border"
            >
              <Plus size={14} />
              {creating ? 'Creating...' : 'New Team'}
            </button>
          )}

          {/* Team list for current level */}
          <div className="flex-1 overflow-hidden">
            <div
              key={slideKey.current}
              className="h-full overflow-y-auto flex flex-col"
              style={slideDir ? {
                animation: `${slideDir === 'in' ? 'groupSlideIn' : 'groupSlideOut'} 200ms ease both`,
              } : undefined}
              onAnimationEnd={() => setSlideDir(null)}
            >
            {/* Back row — chevron navigates up, name selects parent in editor */}
            {viewingParent && (
              <div
                onDragOver={e => {
                  e.preventDefault()
                  e.dataTransfer.dropEffect = 'move'
                  setDragOverId('__back__')
                  if (nestTargetId.current !== '__back__') {
                    clearNestTimer()
                    nestTargetId.current = '__back__'
                    nestTimer.current = setTimeout(() => {
                      if (dragItemId.current) { goUp(); setDragAction('reorder'); setDragOverId(null) }
                      nestTimer.current = null
                      nestTargetId.current = null
                    }, 1000)
                  }
                }}
                onDragLeave={() => { setDragOverId(null); clearNestTimer() }}
                onDrop={async (e) => {
                  e.preventDefault()
                  const sourceId = dragItemId.current
                  dragItemId.current = null
                  setDragOverId(null)
                  setDragAction('reorder')
                  clearNestTimer()
                  if (!sourceId || !tenantId) return
                  const targetParentId = viewingParent.parentId ?? null
                  if (!(await confirmMove(sourceId, targetParentId))) return
                  try {
                    const siblings = teams.filter(t => (t.parentId ?? null) === targetParentId && t.id !== sourceId).map(t => t.id)
                    const parentIdx = siblings.indexOf(viewingParentId!)
                    siblings.splice(parentIdx + 1, 0, sourceId)
                    const allIds = siblings.concat(teams.filter(t => !siblings.includes(t.id)).map(t => t.id))
                    await request(`/api/orgs/${tenantId}/teams/reorder`, {
                      method: 'PUT',
                      body: JSON.stringify({ teamIds: allIds }),
                      headers: { 'Content-Type': 'application/json' },
                    })
                  } catch { /* reorder is best-effort after move */ }
                  loadTeams()
                }}
                className={`flex items-center gap-1.5 px-2 py-1.5 text-sm cursor-pointer transition-colors border-b themed-border ${
                  dragOverId === '__back__' ? 'bg-green-500/10'
                  : selectedTeamId === viewingParentId ? 'bg-blue-600/20 text-blue-300'
                  : 'themed-text hover:themed-bg-tertiary'
                }`}
                onClick={() => setSelectedTeamId(viewingParentId)}
                onDoubleClick={goUp}
              >
                <button
                  onClick={e => { e.stopPropagation(); setSelectedTeamId(viewingParentId); goUp() }}
                  className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center hover:themed-bg-secondary transition-colors"
                  title="Go back"
                >
                  <ChevronLeft size={14} className={dragOverId === '__back__' ? 'text-green-400' : 'themed-text-muted'} />
                </button>
                <span
                  className={`flex-1 truncate ${
                    dragOverId === '__back__' ? 'text-green-400' : ''
                  }`}
                >{viewingParent.name}</span>
              </div>
            )}
            {loading ? (
              <div className="p-3 flex items-center gap-2 themed-text-muted text-sm">
                <Loader2 size={14} className="animate-spin" />
              </div>
            ) : visibleTeams.length === 0 ? (
              <>
                {newInline && (
                  <div className="flex items-center gap-1.5 px-2 py-1.5 rounded" style={{ animation: 'groupFlash 600ms ease' }}>
                    <input
                      autoFocus
                      value={newName}
                      onChange={e => setNewName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') confirmCreate()
                        if (e.key === 'Escape') cancelCreate()
                      }}
                      onBlur={cancelCreate}
                      placeholder="Team name"
                      className="flex-1 min-w-0 bg-transparent border-none text-sm themed-text outline-none placeholder:themed-text-muted ml-4"
                    />
                    <button
                      onMouseDown={e => { e.preventDefault(); confirmCreate() }}
                      disabled={!newName.trim() || creating}
                      className="p-0.5 rounded hover:themed-bg-secondary transition-colors disabled:opacity-30"
                    >
                      <Check size={14} className="text-green-400" />
                    </button>
                    <button onMouseDown={e => { e.preventDefault(); cancelCreate() }} className="p-0.5 rounded hover:themed-bg-secondary transition-colors">
                      <X size={12} className="themed-text-muted" />
                    </button>
                  </div>
                )}
                <div
                  className={`flex-1 p-3 text-xs themed-text-muted ${dragOverId === '__empty__' ? 'bg-green-500/10' : ''}`}
                  onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverId('__empty__') }}
                  onDragLeave={() => setDragOverId(null)}
                  onDrop={handleDropToEnd}
                >
                  {dragOverId === '__empty__' ? 'Drop here' : teams.length === 0 && !newInline ? 'No teams yet' : !newInline ? 'No sub-teams' : ''}
                </div>
              </>
            ) : (
              <>
                {visibleTeams.map(team => {
                  const isEditing = editingId === team.id
                  const isSelected = selectedTeamId === team.id
                  const hasKids = hasChildren(team.id)

                  return (
                    <div
                      key={team.id}
                      ref={el => { if (el) rowRefs.current.set(team.id, el); else rowRefs.current.delete(team.id) }}
                      draggable={(isOrgAdmin || canManageTeam(team.id)) && !isEditing}
                      onDragStart={e => handleDragStart(e, team.id)}
                      onDragOver={e => handleDragOver(e, team.id)}
                      onDragLeave={() => { setDragOverId(null); setDragAction('reorder'); clearNestTimer() }}
                      onDrop={() => handleDrop(team.id)}
                      onDragEnd={handleDragEnd}
                      onClick={() => { if (!isEditing) setSelectedTeamId(team.id) }}
                      onDoubleClick={() => { if (!isEditing) drillInto(team.id) }}
                      className={`group flex items-center gap-1.5 px-2 py-1.5 cursor-pointer transition-colors text-sm ${
                        isSelected ? 'bg-blue-600/20 text-blue-300' : 'themed-text hover:themed-bg-tertiary'
                      } ${dragOverId === team.id && dragItemId.current !== team.id
                        ? dragAction === 'nest'
                          ? 'border-r-2 border-r-green-400 border-t-2 border-t-transparent bg-green-500/10'
                          : 'border-t-2 border-t-blue-400 border-r-2 border-r-transparent'
                        : 'border-t-2 border-t-transparent border-r-2 border-r-transparent'
                      }`}
                    >
                      {(isOrgAdmin || canManageTeam(team.id)) && !isEditing && (
                        <GripVertical size={12} className="shrink-0 opacity-0 group-hover:opacity-40 cursor-grab" />
                      )}

                      {isEditing ? (
                        <input
                          autoFocus
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleRename(team.id)
                            if (e.key === 'Escape') setEditingId(null)
                          }}
                          onBlur={() => handleRename(team.id)}
                          onClick={e => e.stopPropagation()}
                          className="flex-1 min-w-0 themed-input border rounded px-1.5 py-0.5 text-xs outline-none focus:border-blue-500"
                        />
                      ) : (
                        <span
                        className={`flex-1 truncate ${dragOverId === team.id && dragItemId.current !== team.id && dragAction === 'nest' ? 'text-green-400' : ''}`}
                        data-drop-nest
                      >{team.name}</span>
                      )}

                      {!isEditing && (
                        <span className="text-[10px] themed-text-muted shrink-0 w-5 h-5 rounded-full themed-bg-primary flex items-center justify-center">
                          {team.memberCount ?? 0}
                        </span>
                      )}

                      {/* Drill-in: always reserve space, only clickable when has children */}
                      {!isEditing && (
                        hasKids ? (
                          <button
                            onClick={e => { e.stopPropagation(); drillInto(team.id) }}
                            className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center hover:themed-bg-secondary transition-colors"
                            title="View sub-teams"
                          >
                            <ChevronRight size={14} className="themed-text-muted" />
                          </button>
                        ) : (
                          <span className="shrink-0 w-5" />
                        )
                      )}
                    </div>
                  )
                })}
                {/* Inline create input */}
                {newInline && (
                  <div
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded border-t-2 border-t-transparent border-r-2 border-r-transparent"
                    style={{ animation: 'groupFlash 600ms ease' }}
                    onMouseDown={e => { if (e.target === e.currentTarget) e.preventDefault() }}
                  >
                    <input
                      autoFocus
                      value={newName}
                      onChange={e => setNewName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') confirmCreate()
                        if (e.key === 'Escape') cancelCreate()
                      }}
                      onBlur={cancelCreate}
                      placeholder="Team name"
                      className="flex-1 min-w-0 bg-transparent border-none text-sm themed-text outline-none placeholder:themed-text-muted ml-4"
                    />
                    <button
                      onMouseDown={e => { e.preventDefault(); confirmCreate() }}
                      disabled={!newName.trim() || creating}
                      className="p-0.5 rounded hover:themed-bg-secondary transition-colors disabled:opacity-30"
                    >
                      <Check size={14} className="text-green-400" />
                    </button>
                    <button onMouseDown={e => { e.preventDefault(); cancelCreate() }} className="p-0.5 rounded hover:themed-bg-secondary transition-colors">
                      <X size={12} className="themed-text-muted" />
                    </button>
                  </div>
                )}
                {/* Bottom drop zone for dragging to end of list */}
                {/* the end drop zone REORDERS (the server's tenant-level manage_subteam), it creates nothing */}
                {(viewingParentId ? canManageTeam(viewingParentId) : isOrgAdmin) && (
                  <div
                    onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverId('__end__'); setDragAction('reorder') }}
                    onDrop={handleDropToEnd}
                    onDragLeave={() => setDragOverId(null)}
                    className={`flex-1 min-h-8 ${dragOverId === '__end__' ? 'border-t-2 border-blue-400' : ''}`}
                  />
                )}
              </>
            )}
            </div>
          </div>
        </div>

        {/* ── Right panel: team editor ─────────────────────────────── */}
        {/*
          Deliberately has NO click handler. This wrapper used to call
          `drillInto(selectedTeamId)` on any click, which meant every click
          inside the editor — the member search box, the tab strip, the Add
          button — bubbled up and re-levelled the LEFT list to that team's
          sub-teams (and flipped what `New Team` creates). Clicking the search
          box is simply the first click most people make here.

          Drilling stays available in the left list, where the level being
          navigated actually lives: double-click a row, its `>` chevron
          ("View sub-teams"), or `New Sub-team` in this panel.
        */}
        <div className="flex-1 min-w-0">
          {selectedTeam ? (
            <TeamEditor
              key={selectedTeam.id}
              tenantId={tenantId!}
              team={selectedTeam}
              teams={teams}
              canManage={canManageTeam(selectedTeam.id)}
              canRename={canRenameTeam(selectedTeam.id)}
              canDelete={canDeleteTeam(selectedTeam.id)}
              myRole={
                isOrgAdmin ? 'org' :
                myAdminTeamIds.has(selectedTeam.id) ? 'admin' :
                isTeamAdminOf(selectedTeam.id) ? 'inherited' : 'member'
              }
              onUpdated={loadTeams}
              onDelete={() => handleDelete(selectedTeam)}
              onCreateSubteam={() => { drillInto(selectedTeam.id); startCreate() }}
              onRevealInDrive={onRevealInDrive}
              activeTabProp={activeTabProp}
              onTabChange={(tab) => onNavProp?.(selectedTeam.id, tab)}
            />
          ) : (
            <div className="themed-bg-secondary rounded-lg h-full flex items-center justify-center">
              <p className="text-sm themed-text-muted">
                {teams.length === 0 ? 'Create a team to get started' : 'Select a team to manage'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function SummaryTooltip({ label, items, total }: { label: string; items: string[]; total?: number }) {
  const remaining = (total ?? items.length) - Math.min(items.length, 5)
  const list = (
    <div className="max-w-[200px]">
      {items.slice(0, 5).map((item, i) => (
        <div key={i} className="truncate py-0.5">{item}</div>
      ))}
      {remaining > 0 ? <div className="text-zinc-400 pt-1">...{remaining} more</div> : null}
    </div>
  )
  return (
    <Tooltip always content={items.length ? list : null} className="cursor-default hover:themed-text transition-colors">
      {label}
    </Tooltip>
  )
}

function TeamEditor({ tenantId, team, teams, canManage, canRename, canDelete, myRole, onUpdated, onDelete, onCreateSubteam, onRevealInDrive, activeTabProp, onTabChange }: {
  tenantId: string
  team: TeamInfo
  teams: TeamInfo[]
  canManage: boolean
  canRename: boolean
  canDelete: boolean
  myRole: 'org' | 'admin' | 'inherited' | 'member'
  onUpdated: () => void
  onDelete: () => void
  onCreateSubteam: () => void
  onRevealInDrive?: (folderId: string, parentFolderId?: string | null) => void
  activeTabProp?: 'members' | 'access'
  onTabChange?: (tab: string) => void
}) {
  const [members, setMembers] = useState<TeamMember[]>([])
  const [allPreview, setAllPreview] = useState<string[]>([])
  const [allTotal, setAllTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [addEmail, setAddEmail] = useState('')
  const [adding, setAdding] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [renameName, setRenameName] = useState('')
  const [activeTab, _setActiveTab] = useState<'members' | 'access'>(activeTabProp ?? 'members')
  const setActiveTab = (tab: 'members' | 'access') => {
    _setActiveTab(tab)
    onTabChange?.(tab)
  }
  const [teamAccess, setTeamAccess] = useState<Array<{ id: string; folderId: string; parentFolderId: string | null; folderName: string; folderPath: string; permission: string }>>([])
  const [accessLoading, setAccessLoading] = useState(false)
  const [orgMembers, setOrgMembers] = useState<Array<{ email: string; name: string; status?: 'active' | 'invited' | 'declined' }>>([])
  const [suggestIdx, setSuggestIdx] = useState(0)
  const suggestRef = useRef<HTMLDivElement>(null)
  const toast = useToast()

  // Load org members for suggestions — includes pending invites so admins
  // can pre-stage team membership before the invitee accepts (#112).
  // The backend accepts these too (teams.ts mirrors folder-permissions).
  useEffect(() => {
    request<{
      members: Array<{ email: string; name: string }>
      invites?: Array<{ email: string; name?: string; status: 'invited' | 'declined' }>
    }>(`/api/orgs/${tenantId}/members`)
      .then(d => {
        const merged = [
          ...d.members.map(m => ({ ...m, status: 'active' as const })),
          // Skip declined invites — they can't be added until re-invited.
          ...(d.invites ?? [])
            .filter(inv => inv.status !== 'declined')
            .map(inv => ({ email: inv.email, name: inv.name ?? inv.email, status: inv.status })),
        ]
        setOrgMembers(merged)
      })
      .catch(() => {})
  }, [tenantId])

  // Fuzzy-filter org members
  const suggestions = useMemo(() => {
    const q = addEmail.toLowerCase().trim()
    if (!q) return []
    const currentEmails = new Set(members.map(m => m.email.toLowerCase()))
    return orgMembers
      .filter(m => !currentEmails.has(m.email.toLowerCase()))
      .map(m => {
        const email = m.email.toLowerCase()
        const name = (m.name || '').toLowerCase()
        let score = 0
        if (email === q) score = 100
        else if (email.startsWith(q)) score = 80
        else if (name.startsWith(q)) score = 70
        else if (name.includes(q)) score = 50
        else if (email.includes(q)) score = 30
        else return null
        return { ...m, score }
      })
      .filter((x): x is { email: string; name: string; status?: 'active' | 'invited' | 'declined'; score: number } => x !== null)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8)
  }, [addEmail, orgMembers, members])

  const startRename = () => { setRenaming(true); setRenameName(team.name) }
  const confirmRename = async () => {
    if (!renameName.trim()) { setRenaming(false); return }
    try {
      await request(`/api/orgs/${tenantId}/teams/${team.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: renameName.trim() }),
        headers: { 'Content-Type': 'application/json' },
      })
      setRenaming(false)
      onUpdated()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to rename')
    }
  }

  const loadMembers = useCallback(async () => {
    setLoading(true)
    try {
      const data = await request<{ members: TeamMember[]; allMembersPreview: string[]; allMembersTotal: number }>(`/api/orgs/${tenantId}/teams/${team.id}/members`)
      setMembers(data.members)
      setAllPreview(data.allMembersPreview ?? [])
      setAllTotal(data.allMembersTotal ?? 0)
    } catch { /* silent */ }
    finally { setLoading(false) }
  }, [tenantId, team.id])

  useEffect(() => { loadMembers() }, [loadMembers])

  // Load folder access for this team
  const loadAccess = useCallback(async () => {
    setAccessLoading(true)
    try {
      const data = await request<{ permissions: Array<{ id: string; folderId: string; parentFolderId: string | null; folderName: string; folderPath: string; permission: string }> }>(`/api/folders/team/${team.id}/permissions`)
      setTeamAccess(data.permissions)
    } catch { setTeamAccess([]) }
    finally { setAccessLoading(false) }
  }, [team.id])

  useEffect(() => { loadAccess() }, [loadAccess])

  const handleAddMember = async () => {
    if (!addEmail.includes('@')) return
    setAdding(true)
    try {
      await request(`/api/orgs/${tenantId}/teams/${team.id}/members`, {
        method: 'POST',
        body: JSON.stringify({ email: addEmail }),
        headers: { 'Content-Type': 'application/json' },
      })
      setAddEmail('')
      loadMembers()
      onUpdated()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to add member')
    } finally { setAdding(false) }
  }

  const handleToggleAdmin = async (email: string, currentRole: string) => {
    const newRole = currentRole === 'admin' ? 'member' : 'admin'
    try {
      await request(`/api/orgs/${tenantId}/teams/${team.id}/members/${encodeURIComponent(email)}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: newRole }),
        headers: { 'Content-Type': 'application/json' },
      })
      loadMembers()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update role')
    }
  }

  const handleRemoveMember = async (email: string) => {
    try {
      await request(`/api/orgs/${tenantId}/teams/${team.id}/members/${encodeURIComponent(email)}`, {
        method: 'DELETE',
      })
      loadMembers()
      onUpdated()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to remove')
    }
  }

  // Build ancestry breadcrumbs
  const breadcrumbs: Array<{ id: string; name: string }> = []
  let current = team
  while (current.parentId) {
    const parent = teams.find(t => t.id === current.parentId)
    if (!parent) break
    breadcrumbs.unshift({ id: parent.id, name: parent.name })
    current = parent
  }

  const subTeams = teams.filter(t => t.parentId === team.id)

  return (
    <div className="themed-bg-secondary rounded-lg p-4 h-full flex flex-col">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-1 mb-2 text-xs themed-text-muted min-w-0 overflow-hidden">
        <span>Teams</span>
        {breadcrumbs.map(crumb => (
          <span key={crumb.id} className="flex items-center gap-1 min-w-0 shrink">
            <ChevronRight size={10} className="shrink-0" />
            <span className="truncate max-w-[80px]">{crumb.name}</span>
          </span>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b themed-border">
        <h3 className="text-sm font-medium themed-text flex items-center gap-2 min-w-0">
          <UsersRound size={16} className="shrink-0" />
          {renaming ? (
            <input
              autoFocus
              value={renameName}
              onChange={e => setRenameName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') confirmRename()
                if (e.key === 'Escape') setRenaming(false)
              }}
              onBlur={confirmRename}
              className="flex-1 min-w-0 bg-transparent border-none outline-none themed-text"
            />
          ) : (
            <>
              <span className="truncate">{team.name}</span>
              {myRole === 'admin' && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 shrink-0">admin</span>
              )}
              {myRole === 'inherited' && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 shrink-0">admin</span>
              )}
              {myRole === 'member' && (
                <span className="text-[10px] px-1.5 py-0.5 rounded themed-bg-tertiary themed-text-muted shrink-0">member</span>
              )}
            </>
          )}
        </h3>
        {(canRename || canDelete) && !renaming && (
          <div className="flex items-center gap-1 shrink-0">
            {canRename && (
              <button onClick={startRename} className="p-1.5 rounded hover:themed-bg-tertiary transition-colors" title="Rename">
                <Pencil size={13} className="themed-text-muted" />
              </button>
            )}
            {canDelete && (
              <button onClick={onDelete} className="p-1.5 rounded hover:bg-red-500/20 transition-colors" title="Delete">
                <Trash2 size={13} className="text-red-400" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Sub-team summary + add button */}
      <div className="mb-10">
        <div className="text-xs themed-text-muted mb-2 flex items-center gap-1">
          <SummaryTooltip
            label={`${subTeams.length} sub-team${subTeams.length !== 1 ? 's' : ''}`}
            items={subTeams.map(t => t.name)}
          />
          <span>&middot;</span>
          <SummaryTooltip
            label={`${allTotal} total member${allTotal !== 1 ? 's' : ''}`}
            items={allPreview}
            total={allTotal}
          />
        </div>
        {canManage && (
          <button
            onClick={onCreateSubteam}
            className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
          >
            <Plus size={14} />
            New Sub-team
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b themed-border mb-3">
        <button
          onClick={() => setActiveTab('members')}
          className={`pb-2 text-xs font-medium transition-colors border-b-2 ${
            activeTab === 'members' ? 'border-blue-500 text-blue-400' : 'border-transparent themed-text-muted hover:themed-text'
          }`}
        >
          Direct Members ({members.length})
        </button>
        <button
          onClick={() => setActiveTab('access')}
          className={`pb-2 text-xs font-medium transition-colors border-b-2 ${
            activeTab === 'access' ? 'border-blue-500 text-blue-400' : 'border-transparent themed-text-muted hover:themed-text'
          }`}
        >
          Access ({teamAccess.length})
        </button>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {activeTab === 'members' ? (<>
        {/* Members tab */}

        {/* Add member with fuzzy suggestions */}
        {canManage && (
          <div className="relative flex gap-2 mb-3">
            <div className="relative flex-1">
              <input
                value={addEmail}
                onChange={e => { setAddEmail(e.target.value); setSuggestIdx(0) }}
                placeholder="Search members..."
                onKeyDown={e => {
                  if (suggestions.length > 0) {
                    if (e.key === 'ArrowDown') { e.preventDefault(); setSuggestIdx(i => Math.min(suggestions.length - 1, i + 1)) }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); setSuggestIdx(i => Math.max(0, i - 1)) }
                    else if (e.key === 'Enter') { e.preventDefault(); setAddEmail(suggestions[suggestIdx].email); setSuggestIdx(0) }
                    else if (e.key === 'Escape') { setAddEmail('') }
                  } else if (e.key === 'Enter') { handleAddMember() }
                }}
                onBlur={() => setTimeout(() => setSuggestIdx(-1), 150)}
                onFocus={() => setSuggestIdx(0)}
                className="w-full themed-input border rounded-md px-3 py-1.5 text-sm outline-none focus:border-blue-500"
              />
              {suggestions.length > 0 && suggestIdx >= 0 && (
                <div ref={suggestRef} className="absolute top-full left-0 right-0 mt-1 max-h-[200px] overflow-y-auto rounded-md border themed-border themed-bg-secondary shadow-lg z-50">
                  {suggestions.map((s, idx) => (
                    <button
                      key={s.email}
                      onMouseDown={e => { e.preventDefault(); setAddEmail(s.email); setSuggestIdx(-1) }}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors ${
                        idx === suggestIdx ? 'themed-bg-tertiary themed-text' : 'themed-text hover:themed-bg-tertiary'
                      }`}
                    >
                      <span className="truncate font-medium">{s.name || s.email}</span>
                      {s.status === 'invited' && (
                        <span className="shrink-0 text-[9px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-300">Invited</span>
                      )}
                      {s.name && <span className="truncate themed-text-muted ml-auto text-[10px]">{s.email}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={handleAddMember}
              disabled={adding || !addEmail.includes('@')}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md transition-colors shrink-0"
            >
              {adding ? '...' : 'Add'}
            </button>
          </div>
        )}

        {/* Members list */}
        <div className="flex-1 overflow-y-auto -mx-1">
          {loading ? (
            <div className="flex items-center gap-2 themed-text-muted text-sm py-2 px-1">
              <Loader2 size={14} className="animate-spin" /> Loading...
            </div>
          ) : members.length === 0 ? (
            <div className="text-xs themed-text-muted py-2 px-1">No direct members in this team</div>
          ) : (
            members.map(m => (
              <div key={m.email} className="flex items-center justify-between py-1.5 px-2 rounded hover:themed-bg-tertiary group">
                <div className="min-w-0 truncate">
                  {m.name && <span className="text-sm themed-text">{m.name}</span>}
                  <span className={`text-xs font-mono ${m.name ? ' ml-2 themed-text-muted' : ' text-sm themed-text'}`}>{m.email}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  {canManage ? (
                    <button
                      onClick={() => handleToggleAdmin(m.email, m.role)}
                      className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${
                        m.role === 'admin' ? 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30' : 'themed-bg-tertiary themed-text-muted hover:themed-text'
                      }`}
                      title={m.role === 'admin' ? 'Click to demote to member' : 'Click to promote to admin'}
                    >
                      {m.role === 'admin' ? 'admin' : 'member'}
                    </button>
                  ) : m.role === 'admin' ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">admin</span>
                  ) : null}
                  {canManage && (
                    <button
                      onClick={() => handleRemoveMember(m.email)}
                      className="p-1 rounded hover:bg-red-500/20 transition-colors opacity-0 group-hover:opacity-100"
                      title="Remove from team"
                    >
                      <X size={12} className="text-red-400" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        </>) : (
          /* Access tab */
          <div className="flex-1 overflow-y-auto -mx-1">
            {accessLoading ? (
              <div className="flex items-center gap-2 themed-text-muted text-sm py-2 px-1">
                <Loader2 size={14} className="animate-spin" /> Loading...
              </div>
            ) : teamAccess.length === 0 ? (
              <div className="text-xs themed-text-muted py-2 px-1">No folder access assigned to this team</div>
            ) : (
              teamAccess.map(a => (
                <div
                  key={a.id}
                  className="flex items-center justify-between py-1.5 px-2 rounded hover:themed-bg-tertiary cursor-pointer"
                  onClick={() => onRevealInDrive?.(a.folderId, a.parentFolderId)}
                >
                  <div className="min-w-0">
                    <div className="text-sm themed-text flex items-center gap-1.5">
                      <Folder size={14} className="text-yellow-500 shrink-0" />
                      <span className="truncate">{a.folderName}</span>
                    </div>
                    {a.folderPath && <div className="text-[10px] themed-text-muted ml-5 truncate">{a.folderPath}</div>}
                  </div>
                  <span className={`text-xs shrink-0 ml-2 px-2 py-0.5 rounded ${
                    a.permission === 'admin' ? 'bg-purple-500/20 text-purple-300' :
                    a.permission === 'readwrite' ? 'bg-blue-500/20 text-blue-300' :
                    a.permission === 'read' || a.permission === 'readcopy' ? 'bg-green-500/20 text-green-300' :
                    a.permission === 'hidden' ? 'bg-red-500/20 text-red-300' :
                    'themed-text-muted'
                  }`}>
                    {a.permission === 'admin' ? 'Manage & Edit' :
                     a.permission === 'readwrite' ? 'Read & Write' :
                     a.permission === 'readcopy' ? 'Read-only' :
                     a.permission === 'hidden' ? 'No access' : a.permission}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

    </div>
  )
}

const PERM_LABEL: Record<string, string> = {
  hidden: 'No access', readcopy: 'Read-only',
  readwrite: 'Read & Write', admin: 'Manage & Edit',
}
const PERM_OPTIONS_ALL = ['admin', 'readwrite', 'readcopy', 'hidden']

function AccessSyncDialog({ email, role, conflicts, driveAdminNarrowedFolders = 0, tenantId, onClose, onApplied }: {
  email: string
  role: string
  conflicts: Array<{ id: string; folderId: string; name: string; path: string; currentPermission: string; suggestedPermission: string; isUserFolder: boolean }>
  /** #787 §5 — folders where the Drive Admin role is set below Manage. */
  driveAdminNarrowedFolders?: number
  tenantId: string
  onClose: () => void
  onApplied: () => void
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const toast = useToast()
  const [suggestions, setSuggestions] = useState(() =>
    conflicts.map(c => ({ folderId: c.folderId, permission: c.suggestedPermission }))
  )
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const handleApply = async () => {
    setSaving(true)
    try {
      const res = await request<{ ok: boolean; refusals?: Array<{ folderId: string; error: string }> }>('/api/orgs/' + tenantId + '/members/sync-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, updates: suggestions }),
      })
      // #787 §4 — a change that would take Manage from someone it may not is
      // refused per folder; say so rather than report every row as synced.
      if (res?.refusals?.length) toast.error(`${res.refusals.length} not changed: ${res.refusals[0].error}`)
      else toast.success('Access synced')
      onApplied()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to sync access')
    } finally {
      setSaving(false)
    }
  }

  const ROLE_LABEL_MAP: Record<string, string> = {
    owner: 'Owner', admin: 'Drive admin', member: 'Member',
    viewer: 'Viewer', guest: 'Guest', noaccess: 'No Access',
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative z-10 rounded-lg shadow-xl max-w-lg w-full ${themeClass} themed-bg-secondary`}>
        <div className="flex items-center justify-between px-6 pt-5">
          <h3 className="text-lg font-semibold themed-text">Sync Folder Access</h3>
          <button onClick={onClose} className="p-1 themed-text-muted hover:themed-text transition-colors rounded">
            <X size={18} />
          </button>
        </div>
        <div className="px-6 pt-2 pb-1">
          <p className="text-xs themed-text-muted">
            Role changed to <strong>{ROLE_LABEL_MAP[role] || role}</strong> for <span className="font-mono">{email}</span>.
            {conflicts.length > 0 && ' These folders have different access:'}
          </p>
          {driveAdminNarrowedFolders > 0 && (
            <p className="text-xs themed-text-muted mt-2 flex items-start gap-1.5">
              <Lock size={12} className="shrink-0 mt-0.5" />
              <span>
                Drive admins have limited access on {driveAdminNarrowedFolders} {driveAdminNarrowedFolders === 1 ? 'folder' : 'folders'}.
                That limit is set on the Drive Admin role, so it applies to {email} too — change it from each folder&apos;s access dialog.
              </span>
            </p>
          )}
        </div>

        {conflicts.length > 0 && (
        <div className="px-6 pt-3 pb-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b themed-border">
                <th className="text-left py-1.5 themed-text-secondary font-medium text-xs">Folder</th>
                <th className="text-left py-1.5 themed-text-secondary font-medium text-xs w-28">Current</th>
                <th className="text-left py-1.5 text-blue-400 font-medium text-xs w-36">Suggested</th>
              </tr>
            </thead>
            <tbody>
              {conflicts.map((c, idx) => (
                <tr key={c.folderId} className="border-b themed-border last:border-b-0">
                  <td className="py-2">
                    <div className="flex items-center gap-1.5">
                      {(c as any).isFolder !== false
                        ? <Folder size={14} className="text-yellow-500 shrink-0" />
                        : <File size={14} className="themed-text-muted shrink-0" />
                      }
                      <div className="min-w-0">
                        <div className="text-sm themed-text truncate">{c.name}</div>
                        {c.path && <div className="text-[10px] themed-text-muted truncate">{c.path}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="py-2 text-xs themed-text-muted">
                    {PERM_LABEL[c.currentPermission] || c.currentPermission}
                  </td>
                  <td className="py-2">
                    {(c as any).locked ? (
                      <div className="flex items-center gap-1 text-xs themed-text-muted">
                        <Lock size={12} className="shrink-0" />
                        <span>{PERM_LABEL[c.currentPermission] || c.currentPermission}</span>
                        <span className="text-[10px] opacity-60">(no manage access)</span>
                      </div>
                    ) : (
                    <div className="flex items-center gap-1">
                      {(() => {
                        const RANK: Record<string, number> = { hidden: 0, read: 1, readcopy: 2, readwrite: 3, admin: 4 }
                        const curRank = RANK[c.currentPermission] ?? 0
                        const sugRank = RANK[suggestions[idx].permission] ?? 0
                        if (sugRank > curRank) return <ArrowUp size={12} className="text-green-400 shrink-0" />
                        if (sugRank < curRank) return <ArrowDown size={12} className="text-red-400 shrink-0" />
                        return <Minus size={12} className="themed-text-muted shrink-0" />
                      })()}
                      <select
                        value={suggestions[idx].permission}
                        onChange={e => setSuggestions(prev => prev.map((s, i) => i === idx ? { ...s, permission: e.target.value } : s))}
                        className="themed-input border rounded px-2 py-1 text-xs outline-none flex-1"
                      >
                        {PERM_OPTIONS_ALL.map(p => (
                          <option key={p} value={p}>{PERM_LABEL[p] || p}</option>
                        ))}
                      </select>
                    </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        <div className="px-6 pb-5 pt-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 themed-btn-secondary rounded-lg text-sm font-medium">
            Skip
          </button>
          <button
            onClick={handleApply}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {saving ? 'Applying...' : 'Apply Suggestions'}
          </button>
        </div>
      </div>
    </div>
  )
}

function MemberAccessOverview({ email, onClose, onPickFile, readOnly = false }: {
  email: string
  onClose: () => void
  onPickFile?: () => Promise<{ id: string; isFolder: boolean } | null>
  readOnly?: boolean
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const toast = useToast()

  interface AccessItem {
    id: string
    folderId: string
    name: string
    path: string
    isFolder: boolean
    permission: string
  }

  const [items, setItems] = useState<AccessItem[]>([])
  const [loading, setLoading] = useState(true)
  const [pendingItems, setPendingItems] = useState<Array<{ id: string; name: string; path: string; isFolder: boolean; permission: string }>>([])
  const [saving, setSaving] = useState(false)

  const reloadItems = useCallback(() => {
    setLoading(true)
    request<{ permissions: Array<{ id: string; folderId: string; permission: string; targetType: string; targetId: string }> }>(
      `/api/folders/user/${encodeURIComponent(email)}/permissions`
    )
      .then(async (data) => {
        const userPerms = data.permissions.filter(p => p.targetType === 'user' && p.targetId === email)
        const resolved = await Promise.all(
          userPerms.map(async (p) => {
            let name = p.folderId
            let path = ''
            let isFolder = true
            try {
              const file = await request<{ name: string; isFolder: boolean; parentId: string | null }>(`/api/files/${p.folderId}`)
              name = file.name
              isFolder = file.isFolder
              try {
                const crumbs = await request<{ breadcrumbs: Array<{ name: string }> }>(`/api/files/${p.folderId}/breadcrumbs`)
                const parents = crumbs.breadcrumbs.slice(0, -1)
                path = '/' + parents.map(c => c.name).join('/')
              } catch {}
            } catch {}
            return { id: p.id, folderId: p.folderId, name, path, isFolder, permission: p.permission }
          })
        )
        resolved.sort((a, b) => {
          // User folder (name matches email) first, then folders, then by path
          const aIsUserFolder = a.name.toLowerCase() === email.toLowerCase()
          const bIsUserFolder = b.name.toLowerCase() === email.toLowerCase()
          if (aIsUserFolder && !bIsUserFolder) return -1
          if (!aIsUserFolder && bIsUserFolder) return 1
          if (a.isFolder && !b.isFolder) return -1
          if (!a.isFolder && b.isFolder) return 1
          return a.path.localeCompare(b.path)
        })
        setItems(resolved)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [email])

  useEffect(() => { reloadItems() }, [reloadItems])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const PERM_OPTIONS = [
    { value: 'admin', label: 'Manage & Edit' },
    { value: 'readwrite', label: 'Read & Write' },
    { value: 'readcopy', label: 'Read-only' },
    { value: 'hidden', label: 'No access' },
  ]

  // In the read-only "My Access" view, mirror the label the drive's Folder Info
  // shows for the same folder (#231). Folder Info reads the EFFECTIVE permission
  // via GET /api/files/:id/count, which reports a `hidden` folder's access as
  // `readcopy` ("Hidden folder: fake as empty with readcopy access") — rendered
  // "Read-only". Fold hidden→readcopy here so the two surfaces agree.
  //   ⚠ This is the effective TRAVERSAL permission: the member can open the
  //   folder but its contents come back empty — "Read-only" overstates it.
  //   Applied ONLY to the read-only display; the admin editor (readOnly=false)
  //   keeps the raw `hidden`/"No access" so it stays editable and honest.
  const displayPermission = (perm: string) => (readOnly && perm === 'hidden' ? 'readcopy' : perm)

  const handlePermChange = async (item: AccessItem, newPerm: string) => {
    try {
      if (newPerm === 'remove') {
        await request(`/api/folders/${item.folderId}/permissions/${item.id}`, { method: 'DELETE' })
        setItems(prev => prev.filter(i => i.id !== item.id))
        toast.success('Access removed')
      } else {
        await request(`/api/folders/${item.folderId}/permissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ targetType: 'user', targetId: email, permission: newPerm }),
        })
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, permission: newPerm } : i))
        toast.success('Access updated')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative z-10 rounded-lg shadow-xl max-w-lg w-full ${themeClass} themed-bg-secondary`}>
        <div className="flex items-center justify-between px-6 pt-5">
          <h3 className="text-lg font-semibold themed-text">{readOnly ? 'My Access' : 'Member Access'}</h3>
          <button onClick={onClose} className="p-1 themed-text-muted hover:themed-text transition-colors rounded">
            <X size={18} />
          </button>
        </div>
        <div className="px-6 pt-2 pb-1">
          <p className="text-xs themed-text-muted font-mono">{email}</p>
        </div>

        <div className="px-6 pb-6 pt-3">
          {loading ? (
            <div className="py-4 text-center themed-text-muted text-sm">Loading...</div>
          ) : items.length === 0 ? (
            <div className="py-4 text-center themed-text-muted text-sm">
              No explicit access set.
              <br />
              <span className="text-xs">This member uses role-based defaults only.</span>
            </div>
          ) : (
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {items.map(item => (
                <div key={item.id} className="flex items-center justify-between py-2 gap-3">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {item.isFolder
                      ? <Folder size={16} className="text-yellow-500 shrink-0" />
                      : <File size={16} className="themed-text-muted shrink-0" />
                    }
                    <div className="min-w-0">
                      <div className="text-sm themed-text truncate">{item.name}</div>
                      {item.path && <div className="text-[10px] themed-text-muted truncate">{item.path}</div>}
                    </div>
                  </div>
                  {readOnly ? (
                    <span className="text-xs themed-text-muted shrink-0">
                      {PERM_OPTIONS.find(o => o.value === displayPermission(item.permission))?.label || item.permission}
                    </span>
                  ) : (
                    <select
                      value={item.permission}
                      onChange={e => handlePermChange(item, e.target.value)}
                      className="themed-input border rounded px-2 py-1 text-xs outline-none shrink-0"
                    >
                      {PERM_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                      <option value="remove">— Remove —</option>
                    </select>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pending access items — each with its own Add button */}
          {!readOnly && pendingItems.length > 0 && (
            <div className="mt-3 space-y-1">
              {pendingItems.map((item, idx) => (
                <div key={item.id} className="flex items-center gap-2">
                  <button
                    className="flex items-center gap-1.5 flex-1 min-w-0 text-left hover:themed-bg-tertiary rounded px-1 py-0.5 -mx-1 transition-colors"
                    title="Click to change"
                    onClick={async () => {
                      if (!onPickFile) return
                      const result = await onPickFile()
                      if (!result) return
                      const isDupe = pendingItems.some((p, i2) => i2 !== idx && p.id === result.id) || items.some(i => i.folderId === result.id)
                      if (isDupe) {
                        toast.error('This item already has access configured')
                        return
                      }
                      try {
                        const f = await request<{ name: string }>(`/api/files/${result.id}`)
                        let path = ''
                        try {
                          const crumbs = await request<{ breadcrumbs: Array<{ name: string }> }>(`/api/files/${result.id}/breadcrumbs`)
                          const parents = crumbs.breadcrumbs.slice(0, -1)
                          path = '/' + parents.map(c => c.name).join('/')
                        } catch {}
                        setPendingItems(prev => prev.map((p, i) => i === idx ? { ...p, id: result.id, name: f.name, path, isFolder: result.isFolder } : p))
                      } catch {}
                    }}
                  >
                    {item.isFolder
                      ? <Folder size={14} className="text-yellow-500 shrink-0" />
                      : <File size={14} className="themed-text-muted shrink-0" />
                    }
                    <div className="min-w-0">
                      <div className="text-sm themed-text truncate">{item.name}</div>
                      <div className="text-[10px] themed-text-muted truncate">{item.path || '/'}</div>
                    </div>
                  </button>
                  <select
                    value={item.permission}
                    onChange={e => setPendingItems(prev => prev.map((p, i) => i === idx ? { ...p, permission: e.target.value } : p))}
                    className="themed-input border rounded px-2 py-1 text-xs outline-none"
                  >
                    {PERM_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <button
                    disabled={saving}
                    onClick={async () => {
                      setSaving(true)
                      try {
                        await request(`/api/folders/${item.id}/permissions`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ targetType: 'user', targetId: email, permission: item.permission }),
                        })
                        toast.success('Access added')
                        setPendingItems(prev => prev.filter((_, i) => i !== idx))
                        reloadItems()
                      } catch (err: any) {
                        toast.error(err?.message || 'Failed to add access')
                      } finally {
                        setSaving(false)
                      }
                    }}
                    className="px-2.5 py-1 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded transition-colors"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => setPendingItems(prev => prev.filter((_, i) => i !== idx))}
                    className="themed-text-muted hover:themed-text transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Always visible pick button (hidden in read-only mode) */}
          {onPickFile && (
            <button
              onClick={async () => {
                const result = await onPickFile()
                if (!result) return
                if (pendingItems.some(p => p.id === result.id) || items.some(i => i.folderId === result.id)) {
                  toast.error('This item already has access configured')
                  return
                }
                try {
                  const f = await request<{ name: string }>(`/api/files/${result.id}`)
                  let path = ''
                  try {
                    const crumbs = await request<{ breadcrumbs: Array<{ name: string }> }>(`/api/files/${result.id}/breadcrumbs`)
                    const parents = crumbs.breadcrumbs.slice(0, -1)
                    path = '/' + parents.map(c => c.name).join('/')
                  } catch {}
                  setPendingItems(prev => [...prev, { id: result.id, name: f.name, path, isFolder: result.isFolder, permission: 'readwrite' }])
                } catch {
                  setPendingItems(prev => [...prev, { id: result.id, name: result.id, path: '', isFolder: result.isFolder, permission: 'readwrite' }])
                }
              }}
              className="mt-2 text-xs text-blue-400 hover:text-blue-300 transition-colors"
            >
              + Add access
            </button>
          )}

          <div className="mt-4 flex justify-end">
            <button onClick={onClose} className="px-4 py-2 themed-btn-secondary rounded-lg text-sm font-medium">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// `PermissionsPage` lived here and now lives in the platform console, as
// `features/orgs/ui/budget/CustomerPermissions.tsx` (Customers → a customer →
// Configuration → Permission defaults).
//
// TEMPORARY — see `RETIRED_SETTINGS` above and the TODO(permissions-relocation)
// at the routes. Permission defaults are platform-administered for now; the
// eventual permissions product decides whether control returns to tenant
// owners, stays with platform administrators, or is split between them, and
// this relocation is unwound accordingly.

/** Settings → App → Editor (#987): the text editors' personal preferences. */
function EditorPage() {
  const { t } = useTranslation('core-ui')
  const { settings, updateSetting } = useTheme()
  return (
    <div>
      <h2 className="text-lg font-medium themed-text mb-4">{t('settings.editor.title')}</h2>
      <div className="themed-bg-secondary rounded-lg p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <label htmlFor="settings-format-on-save" className="text-sm themed-text">{t('settings.editor.formatOnSave')}</label>
            <p className="text-xs themed-text-muted mt-0.5">{t('settings.editor.formatOnSaveHint')}</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input
              id="settings-format-on-save"
              type="checkbox"
              role="switch"
              checked={settings.formatOnSave}
              onChange={(e) => updateSetting('formatOnSave', e.target.checked)}
              className="sr-only peer"
              data-testid="settings-format-on-save"
            />
            <div className="w-11 h-6 themed-bg-tertiary peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>
    </div>
  )
}

function ThemePage() {
  const { t } = useTranslation('core-ui')
  const { settings, updateSetting } = useTheme()

  const handleSyncAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSetting('theme', {
      ...settings.theme,
      syncAll: e.target.checked,
    })
  }

  const handleThemeChange = (area: 'drive' | 'workspace' | 'chat', value: ThemeMode) => {
    if (settings.theme.syncAll) {
      updateSetting('theme', {
        ...settings.theme,
        drive: value,
        workspace: value,
        chat: value,
      })
    } else {
      updateSetting('theme', {
        ...settings.theme,
        [area]: value,
      })
    }
  }

  return (
    <div>
      <h2 className="text-lg font-medium themed-text mb-4">{t('settings.theme.title')}</h2>
      <div className="themed-bg-secondary rounded-lg p-4 space-y-4">
        {/* Sync toggle */}
        <div className="flex items-center justify-between">
          <label className="text-sm themed-text-secondary">{t('settings.theme.syncAll')}</label>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.theme.syncAll}
              onChange={handleSyncAllChange}
              className="sr-only peer"
            />
            <div className="w-11 h-6 themed-bg-tertiary peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {settings.theme.syncAll ? (
          <div>
            <label className="text-sm themed-text-secondary">{t('settings.theme.allAreas')}</label>
            <div className="mt-2 flex gap-2">
              {themeModes.map((mode) => (
                <button
                  key={mode.value}
                  onClick={() => handleThemeChange('workspace', mode.value)}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    settings.theme.workspace === mode.value
                      ? 'themed-btn-primary'
                      : 'themed-btn-secondary'
                  }`}
                >
                  {t(mode.labelKey)}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div>
              <label className="text-sm themed-text-secondary">{t('settings.theme.workspace')}</label>
              <div className="mt-2 flex gap-2">
                {themeModes.map((mode) => (
                  <button
                    key={mode.value}
                    onClick={() => handleThemeChange('workspace', mode.value)}
                    className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      settings.theme.workspace === mode.value
                        ? 'themed-btn-primary'
                        : 'themed-btn-secondary'
                    }`}
                  >
                    {t(mode.labelKey)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm themed-text-secondary">{t('settings.theme.drive')}</label>
              <div className="mt-2 flex gap-2">
                {themeModes.map((mode) => (
                  <button
                    key={mode.value}
                    onClick={() => handleThemeChange('drive', mode.value)}
                    className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      settings.theme.drive === mode.value
                        ? 'themed-btn-primary'
                        : 'themed-btn-secondary'
                    }`}
                  >
                    {t(mode.labelKey)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm themed-text-secondary">{t('settings.theme.chat')}</label>
              <div className="mt-2 flex gap-2">
                {themeModes.map((mode) => (
                  <button
                    key={mode.value}
                    onClick={() => handleThemeChange('chat', mode.value)}
                    className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      settings.theme.chat === mode.value
                        ? 'themed-btn-primary'
                        : 'themed-btn-secondary'
                    }`}
                  >
                    {t(mode.labelKey)}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// `LanguagePage` was here. REMOVED, TEMPORARY — every user and device is
// pinned to English for now (Lane A A2.3 does the other half: the i18next
// `lng: 'en'` pin, dropping `LanguageDetector`, and overwriting the cached
// `papan-language` key).
//
// Restoring language is a DELIBERATE act of restoring detection AND a UI —
// never merely deleting that pin, which would silently revive whatever stale
// per-device preference is still in localStorage. The locale files and the
// i18n machinery are untouched, so this page is the smaller half of the
// restoration, not the whole of it.

// (StoragePage — the static three-row tab — was retired 2026-08-05. The page
// key 'storage' now renders the injected drive-usage breakdown; the one
// genuinely useful row (settings-file path) moved into AboutPage below, and
// the bin-retention row was dropped because no sweeper enforces it —
// 2026-08-05_SETTINGS_STORAGE_TAB.md §2.5/§3.)

function AboutPage() {
  const { t } = useTranslation('core-ui')

  return (
    <div>
      <h2 className="text-lg font-medium themed-text mb-4">{t('settings.about.title')}</h2>
      <div className="themed-bg-secondary rounded-lg p-4 space-y-4">
        <div className="themed-text-secondary text-sm">
          <p className="font-medium themed-text mb-2">{t('settings.about.appName')}</p>
          <p>{t('settings.about.tagline')}</p>
          <p className="mt-2 text-xs">{t('settings.about.version', { version: __APP_VERSION__ })}</p>
        </div>
        {/* The "Settings file" row was here, naming
            `/papan/System/.config/settings.json`. Preferences live in
            `users.settings` now (Lane A A2.1) and that subsystem is deleted,
            with the folders swept — so the row named a file that no longer
            exists, on a path that no longer exists. A hardcoded location is
            only ever as true as the storage it describes; there is no
            replacement path to print, because the new home is a column. */}
      </div>
    </div>
  )
}

function DevToolsPage() {
  const toast = useToast()
  const [shouldThrow, setShouldThrow] = useState(false)

  if (shouldThrow) {
    throw new Error('Test error from Settings - Error Boundary is working!')
  }

  return (
    <div>
      <h2 className="text-lg font-medium themed-text mb-4 flex items-center gap-2">
        <Bug size={20} />
        Dev Tools
      </h2>
      <div className="themed-bg-secondary rounded-lg p-4 space-y-4">
        <p className="text-sm themed-text-muted">
          These tools are only visible in development mode.
        </p>
        <div>
          <p className="text-sm themed-text-secondary mb-2">Error Boundary</p>
          <button
            onClick={() => setShouldThrow(true)}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Test Error Boundary
          </button>
        </div>
        <div>
          <p className="text-sm themed-text-secondary mb-2">Toast Notifications</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => toast.success('Operation completed successfully!')}
              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-sm transition-colors"
            >
              Success
            </button>
            <button
              onClick={() => toast.error('Something went wrong!')}
              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
            >
              Error
            </button>
            <button
              onClick={() => toast.warning('Please review before continuing.')}
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-sm transition-colors"
            >
              Warning
            </button>
            <button
              onClick={() => toast.info('Here is some useful information.')}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
            >
              Info
            </button>
            {/* The four TYPE buttons above all fire SHORT strings, so none of them
                exercises the case toasts actually got wrong: a long message
                used to grow the bar to viewport width instead of wrapping.
                Kept beside them so that regression stays one click away. */}
            <button
              onClick={() => toast.error(
                'Could not move “Q3 Financial Reconciliation Working Draft (final, revised, v7).xlsx” '
                + 'into “Shared / Finance / 2026 / Quarterly Close / Archive” because the destination '
                + 'folder is read-only for your role, and the workspace has an active write freeze '
                + 'pending payment confirmation from the billing operator.',
              )}
              className="px-3 py-1.5 bg-zinc-600 hover:bg-zinc-700 text-white rounded text-sm transition-colors"
            >
              Long message
            </button>
            <button
              onClick={() => toast.success('Moved “Cardly” into Finance', 6000, {
                label: 'Undo',
                onClick: () => toast.info('Undone.'),
              })}
              className="px-3 py-1.5 bg-zinc-600 hover:bg-zinc-700 text-white rounded text-sm transition-colors"
            >
              With action
            </button>
            <button
              onClick={() => {
                const ctrl = toast.progress('Copying 8 items…', 8)
                let n = 0
                const tick = setInterval(() => {
                  n += 1
                  if (n >= 8) { clearInterval(tick); ctrl.complete('Copied 8 items') }
                  else ctrl.update(n)
                }, 450)
              }}
              className="px-3 py-1.5 bg-zinc-600 hover:bg-zinc-700 text-white rounded text-sm transition-colors"
            >
              Progress
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main SettingsView
// ---------------------------------------------------------------------------

interface SettingsViewProps {
  /** `platformRole` gates the Tier-1 platform budget page. Server remains
   *  the authority (`requirePlatformAdmin`); this only hides the nav item. */
  user?: { id: string; name: string; email: string; platformRole?: string | null }
  onSignOut?: () => void
  onSetPassword?: (newPassword: string) => Promise<{ error?: string }>
  /** `null` = still resolving. Never coerce — see `PasswordSection`. */
  hasPassword?: boolean | null
  onChangePassword?: (currentPassword: string, newPassword: string) => Promise<{ error?: string }>
  onForgotPassword?: () => void
  onRetryHasPassword?: () => void
  /** Update the signed-in user's display name. Returns `{}` on success
   *  or `{ error }` for the form to surface. Caller is responsible for
   *  refreshing the session so cached avatar/taskbar pick up the change. */
  onUpdateName?: (newName: string) => Promise<{ error?: string }>
  isOrg?: boolean
  tenantId?: string
  tenantName?: string
  userRole?: string
  onDeleteOrg?: () => void
  /** Org renamed — the shell owns `tenantName`, so it must update its list. */
  onRenameOrg?: (name: string) => void
  activePage?: string
  /** Is this deck on screen? Hidden shell windows stay MOUNTED, so without it
   *  Settings would name the tab from behind whatever you are looking at. */
  deckActive?: boolean
  activeParam?: string
  activeSubparam?: string
  onPageChange?: (page: string, param?: string, subparam?: string) => void
  /** Opens a drive file/folder picker popup; resolves with selected item or null if cancelled */
  onPickFile?: () => Promise<{ id: string; isFolder: boolean } | null>
  /** Bump to trigger members page refresh (e.g. from realtime org events) */
  refreshKey?: number
  /** Active org email identity (for determining "self" in members page) */
  orgEmail?: string
  /** Called when emails are added, removed, or primary changes — so App can refetch tenants/invites */
  onEmailsChanged?: () => void
  /** Map of email → org memberships for showing warnings on email removal */
  orgsByEmail?: Record<string, OrgEmailInfo[]>
  /** Fetch blocked list (org invitations + cross-org connection blocks) */
  onListBlocked?: () => Promise<Array<BlockedItem>>
  /** Unblock — `kind` routes to the invite vs connection unblock path */
  onUnblock?: (inviteId: string, kind?: 'invite' | 'connection') => Promise<void>
  /** Navigate to drive and reveal a folder/file */
  onRevealInDrive?: (folderId: string, parentFolderId?: string | null) => void
  /**
   * This tenant is identity-only (0175, §8) — it is the person's identity but
   * not a workspace, so the surfaces built on having a drive are dropped:
   * Secure Storage, Links, Network and Storage.
   *
   * Only ever true for a PERSONAL tenant (`resolveTenantModeForType`
   * short-circuits organizations), which is why three of the four already sit
   * inside the `!isOrg` block below — an org member's settings are unaffected.
   *
   * Hiding is disclosure, never enforcement: each page's data is refused
   * server-side by C2/C3 regardless.
   */
  identityOnly?: boolean
  /** Render prop for secure storage page (avoids circular dep with @papan/keybook) */
  secureStoragePage?: React.ReactNode
  /** Shown in the Leave confirm for the org being left (members page): what
   *  stays behind that the person should act on — the keybook's keys they own
   *  (`LeavingKeysNotice`). A render prop for the same boundary reason. */
  renderLeaveNotice?: (tenantId: string) => React.ReactNode
  /** Render prop for the Connections settings tab (step 9a). Render-prop
   *  pattern keeps the feature out of @papan/core-ui (the import would
   *  violate the shared/* → features/* boundary). */
  connectionsPage?: React.ReactNode
  /** Render prop for the Links settings page. Same boundary reason as
   *  `connectionsPage` — @papan/share-links lives in features/. */
  linksPage?: React.ReactNode
  /* NOTE: branding used to be a settings page here (`brandingPage` render
     prop + a Platform sidebar section). It is now its own taskbar destination
     at `${prefix}/branding` — see App.tsx's branding deck window. Old
     `/settings/branding` URLs redirect there. */
  /**
   * RETIRED — neither shell wires it, and nothing here requires one to.
   *
   * It fed an Account → Usage item that rendered the member's own spend, while
   * Organization → Spending rendered the same component with a different subset
   * of sections. One subject, two sidebar entries, each missing what the other
   * had. The pages are merged and the item is gone; `/settings/usage` resolves
   * to `budgetPage` (see `case 'usage'`).
   *
   * Kept in the type, and read as a FALLBACK, only so a host that still passes
   * it renders something sensible instead of the "not available" placeholder.
   * Delete once no caller remains.
   */
  usagePage?: React.ReactNode
  /** The org's own Spending destination (Tier-2). UNGATED: `SpendingPage`
   *  reads `manage_budget:read` itself and picks the VIEW — the org dashboard
   *  for a holder, the member's own self-scoped spend for everyone else. */
  budgetPage?: React.ReactNode
  /** The org's own budget (cap) editor, route `/settings/limits`. Gated on
   *  `manage_budget:write`. Separate from `budgetPage` because the two are
   *  different subjects behind different halves of `manage_budget`. */
  limitsPage?: React.ReactNode
  /** Plan, seats and allowance for THIS tenant. Shown to org and personal
   *  tenants alike (a personal drive is its own billing owner), which is why it
   *  renders in the Organization section AND the `!isOrg` arm of Account rather
   *  than only inside the `isOrg` block.
   *
   *  **Null when the caller lacks `manage_billing:read`** — an org's plan,
   *  seats, allowance and amount due are facts about the organization's
   *  commercial relationship, and a plain member has no part in it. Both shells
   *  gate it, and `GET /subscription` + `/manual-statement` refuse the same
   *  caller, so this is a real absence rather than a hidden nav item with the
   *  data one `fetch` away. READ, not write: the controls inside still take
   *  write, and someone granted read-only billing access is exactly who should
   *  keep the screen.
   *
   *  This is NOT the gate the docblock used to describe, and the distinction
   *  cost a round trip. It once claimed to be "null when billing is disabled
   *  for the install — the server 404s those routes, so a dead entry point is
   *  worse than none." Both halves of that were false: only the WRITE routes
   *  404 when `BILLING_ENABLED` is off, so the page still renders the plan, the
   *  meter and the seat card, and the entry point was never dead. That claim
   *  was deleted rather than implemented. The gate that exists now is about
   *  WHO IS ASKING, not about how the install is configured — a different
   *  question with a different answer, and the reason a `BILLING_ENABLED` check
   *  must not be reintroduced here. */
  billingPage?: React.ReactNode
  /**
   * True while the host is still RESOLVING whether this caller may see
   * `billingPage` — not while the page's own data loads.
   *
   * `useMyCapabilities().can()` answers `false` for "denied" and for "still
   * fetching" alike, so a host that gates on it alone passes `billingPage:
   * null` during the in-flight window and this screen confidently tells an
   * owner their billing "is not available to you". It is the bug class
   * `useMyCapabilities`' own docblock warns about, observed on a cold load
   * where the capabilities request took 5s: the refusal is indistinguishable
   * from the real one, and a user who believes it goes and asks an admin for
   * access they already have.
   *
   * Carried as a separate flag rather than folded into `billingPage` so the
   * prop keeps ONE meaning — `null` is a resolved denial, never an unresolved
   * one — and so the refusal copy stays here beside the grant that produces
   * it instead of being duplicated into both shells.
   */
  billingPageLoading?: boolean
  /** Storage composition for THIS tenant (drive-usage breakdown). Same
   *  org-AND-personal mirroring rationale as `billingPage` — a byte total is
   *  a per-tenant fact, not a member allocation
   *  (docs/plans/2026-08-05_SETTINGS_STORAGE_TAB.md §3A). Reuses the page
   *  key `'storage'` freed by retiring the old static App → Storage tab. */
  storagePage?: React.ReactNode
  /** Dedicated version-retention settings page (#590 Task 9) — split out of
   *  the flat control that used to live on `storagePage` (#452 Task A8).
   *  Same org-AND-personal mirror, same "injected, unconditional" shape —
   *  mirror `storagePage` exactly when wiring a new host. */
  versionRetentionPage?: React.ReactNode
  /** Settings → Organization → File activity (#118, Task B8) — who created /
   *  updated / moved / deleted / restored what, incl. scripts, AI agents and
   *  system sweeps. Org-only (no roles/teams-shaped audience on a personal
   *  drive's own actions the way `view_file_audit` reasons about them), so
   *  unlike `storagePage`/`billingPage` this has no `!isOrg` mirror. The page
   *  self-gates on `view_file_audit:read` exactly like `BudgetsEditor`
   *  self-gates on `manage_budget:write` — the nav item below additionally
   *  hides for a caller who does not hold it, same as `canWriteBudget`. */
  fileActivityPage?: React.ReactNode
  /** Optional keybook-aware team creator. When provided, the teams
   *  page uses it instead of a direct POST so keybook-on tenants get
   *  the keypair payload. Avoids a static dep from core-ui →
   *  @papan/keybook. App.tsx wires this. */
  createTeam?: (input: { tenantId: string; name: string; parentId?: string }) => Promise<{ ok: boolean; team: TeamInfo }>
  /** Phone viewport: the sidebar becomes a horizontal scrolling strip above a
   *  full-width content area instead of a fixed 180px side column. */
  mobile?: boolean
  /** Mobile close (desktop closes via its deck window). Shows an × in the
   *  header when provided. */
  onClose?: () => void
}

export function SettingsView({
  user,
  onSignOut,
  onSetPassword,
  hasPassword,
  onChangePassword,
  onForgotPassword,
  onRetryHasPassword,
  onUpdateName,
  isOrg,
  tenantId,
  tenantName,
  userRole,
  onDeleteOrg,
  onRenameOrg,
  activePage,
  deckActive = true,
  activeParam,
  activeSubparam,
  onPageChange,
  onPickFile,
  refreshKey,
  orgEmail,
  onEmailsChanged,
  orgsByEmail,
  onListBlocked,
  onUnblock,
  onRevealInDrive,
  identityOnly,
  secureStoragePage,
  renderLeaveNotice,
  connectionsPage,
  linksPage,
  usagePage,
  budgetPage,
  limitsPage,
  storagePage,
  versionRetentionPage,
  fileActivityPage,
  billingPage,
  billingPageLoading,
  createTeam,
  mobile,
  onClose,
}: SettingsViewProps) {
  const { t } = useTranslation('core-ui')
  // Ambient, not a prop (0179): the keybook switch is deployment-global, and a
  // default-open prop was forgotten by one of this component's two shells
  // within a day of existing. Hides the Secure Storage item and swaps the
  // page's content for the disabled notice — disclosure only, the
  // /api/keybook gate enforces.
  const keybookAvailable = useKeybookAvailable()
  const { loading, saving } = useTheme()

  // `canSeePermissions` (manage_member:write) gated the Permissions nav item and
  // was removed with it — the grid is platform-administered now. `canOrg` stays:
  // it still answers for the Budget item below.
  const { can: canOrg, loading: capsLoading, unavailable: capsUnavailable } = useMyCapabilities(tenantId, false, orgEmail)

  // Only the WRITE half gates a nav item now. `manage_budget:read` still
  // decides what Spending shows — org dashboard vs the member's own spend —
  // but that choice moved into `SpendingPage`, because the read capability
  // selects a VIEW rather than granting or refusing the page (2026-08-07).
  // Budget (the cap editor) stays gated and stays inside the `isOrg` block:
  // a personal drive has no roles, teams or members to allocate across.
  const canWriteBudget = canOrg('manage_budget', 'write')

  // File activity nav item (#118, Task B8) — same shape as `canWriteBudget`
  // above: `can()` reads false for both "denied" and "still loading", so the
  // item simply stays hidden until the grant resolves true rather than
  // flashing a denial. `view_file_audit` is owner/admin-only by default (see
  // `shared/types/src/capabilities.ts`), so a plain member never sees it —
  // the PAGE itself (`fileActivityPage`) carries the real, resolved-only
  // refusal copy for anyone who navigates to the URL directly.
  const canViewFileAudit = canOrg('view_file_audit', 'read')

  // Version retention (#590) — settings page + nav gated to org admins only.
  // Same shape as `canWriteBudget`/`canViewFileAudit`: `can()` reads false for
  // both "denied" and "still loading", so the item stays hidden until the
  // grant resolves true rather than flashing for a member who will be
  // refused a moment later. On a personal drive `canOrg` resolves the
  // caller's own owner membership, so this stays true there.
  const canManageVersionRetention = canOrg('manage_org', 'write')

  const [currentPage, setCurrentPage] = useState<SettingsPage>(
    (activePage as SettingsPage) || (isOrg ? 'org-general' : 'profile')
  )

  // The section is deliberately NOT in the title. Naming it would need a
  // label per SettingsPage — a second table beside the nav's own, free to
  // drift from it — and "Settings" is what the taskbar calls this surface.
  usePageTitle(t('taskBar.settings'), deckActive)

  // Sync from external activePage prop
  useEffect(() => {
    if (activePage) {
      setCurrentPage(activePage as SettingsPage)
    }
  }, [activePage])


  const navigateTo = useCallback((page: SettingsPage, param?: string, subparam?: string) => {
    setCurrentPage(page)
    onPageChange?.(page, param, subparam)
  }, [onPageChange])

  /**
   * A page this caller does not have, reached by URL, falls back to Profile.
   *
   * Hiding a sidebar item is not enough on its own: `activePage` comes from the
   * URL, so a bookmark or a pasted `?page=links` renders the page with no nav
   * item pointing at it — a surface the operator has switched off, shown anyway,
   * and reachable by anyone who was there before the switch.
   *
   * Three lists, one mechanism, because the three ask different questions —
   * "does this tenant have the feature", "is this tenant an organization", and
   * "does this page still exist at all" — and a page can be caught by any of
   * them. Keeping one effect is what stops the three from drifting into three
   * behaviours (a redirect here, a placeholder there, a silent `default:`
   * elsewhere) for what is, to the person holding the link, the same event.
   *
   * This is a DISCLOSURE tidy-up, not a gate. Each of these pages reads
   * tenant-scoped data the server refuses regardless, so the failure mode
   * without it is an empty or erroring page rather than a leak.
   *
   * Profile, because it is the one page every signed-in user always has and is
   * already the non-org default. Landing on it is self-explanatory in a way an
   * error state would not be.
   */
  const pageIsGone =
    RETIRED_SETTINGS.includes(currentPage)
    || (identityOnly && IDENTITY_ONLY_HIDDEN_SETTINGS.includes(currentPage))
    || (!isOrg && ORG_ONLY_SETTINGS.includes(currentPage))

  useEffect(() => {
    if (!pageIsGone) return
    navigateTo('profile')
    // `navigateTo` is stable (useCallback on `onPageChange`), so this settles in
    // one pass rather than looping.
    //
    // `currentPage` is in the deps even though the guard reads `pageIsGone`,
    // and that is not redundancy: `pageIsGone` is a BOOLEAN, so moving from one
    // gone page to a DIFFERENT gone page — /settings/permissions to
    // /settings/language via back/forward, or two pasted links in a tick —
    // leaves it `true` throughout and the effect never re-fires. The URL then
    // keeps naming the second removed page while Profile is on screen, which is
    // the exact "loads as something else" state these lists exist to prevent.
  }, [pageIsGone, currentPage, navigateTo])

  // The body scroll container — the WHOLE centered row scrolls (so the
  // scrollbar sits at the far right edge of the view, not between columns);
  // the sidebar and scrollspy are sticky columns inside it. The ScrollSpy
  // indexes and tracks this container.
  const contentRef = useRef<HTMLDivElement>(null)

  // The scroll container persists across page switches — reset it so a new
  // page never opens mid-scroll. Not on mount: it already starts at the top,
  // and a layout swap puts it back where the user was (#897).
  const scrolledPage = useRef(currentPage)
  useEffect(() => {
    if (scrolledPage.current === currentPage) return
    scrolledPage.current = currentPage
    contentRef.current?.scrollTo({ top: 0 })
  }, [currentPage])
  useSwapScroll(`settings:${currentPage}`, contentRef)

  // Mouse / pen drag-to-scroll + wheel → horizontal for the mobile nav strip
  // (overflow-x-auto is touch/trackpad only otherwise). A moved drag suppresses
  // the click it synthesizes so dragging never switches page. Same pattern as
  // the Recent carousel.
  const navRef = useRef<HTMLElement>(null)
  const navDrag = useRef<{ x: number; scroll: number; moved: boolean; id: number } | null>(null)
  const navSuppressClick = useRef(false)
  const onNavPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'touch' || !navRef.current) return
    navSuppressClick.current = false
    navDrag.current = { x: e.clientX, scroll: navRef.current.scrollLeft, moved: false, id: e.pointerId }
  }
  const onNavPointerMove = (e: React.PointerEvent) => {
    const d = navDrag.current
    const el = navRef.current
    if (!d || !el) return
    const dx = e.clientX - d.x
    if (!d.moved) {
      if (Math.abs(dx) <= 4) return
      d.moved = true
      try { el.setPointerCapture(d.id) } catch { /* not capturable */ }
    }
    el.scrollLeft = d.scroll - dx
  }
  const onNavPointerUp = () => {
    const d = navDrag.current
    const el = navRef.current
    if (!d || !el) return
    navDrag.current = null
    try { el.releasePointerCapture(d.id) } catch { /* already released */ }
    if (d.moved) navSuppressClick.current = true
  }
  const onNavWheel = (e: React.WheelEvent) => {
    const el = navRef.current
    if (!el) return
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) el.scrollLeft += e.deltaY
  }

  const renderPage = () => {
    if (loading) {
      return (
        <div className="flex items-center gap-2 themed-text-muted">
          <Loader2 size={20} className="animate-spin" />
          <span>{t('common:loading')}</span>
        </div>
      )
    }

    // The redirect above lands on the NEXT tick, so without this the page it is
    // redirecting away from renders once first — and these pages fetch on
    // mount. `/~/settings/members` would fire a members request for a personal
    // tenant every time someone opened that stale link. Render Profile directly;
    // the effect then corrects the URL to match what is already on screen.
    //
    // This closes the window completely for `RETIRED_SETTINGS` (a constant) and
    // `ORG_ONLY_SETTINGS` (`isOrg` is derived synchronously from the URL path —
    // see `useTenant`). It does NOT close it for the identity-only list, which
    // is the one case where the flag itself arrives late: `identityOnly` is
    // `false` until `my-tenants` resolves, so a stale `/settings/subscription`
    // still renders and fetches once before flipping. That window is accepted
    // and pre-existing — the parent plan's trap #1 says the safe direction is
    // to SHOW during the unresolved window rather than hide from everyone.
    if (pageIsGone) return <ProfilePage user={user} onSignOut={onSignOut} onUpdateName={onUpdateName} />

    switch (currentPage) {
      case 'profile':
        return <ProfilePage user={user} onSignOut={onSignOut} onUpdateName={onUpdateName} />
      case 'emails':
        return <EmailsPage onEmailsChanged={onEmailsChanged} orgsByEmail={orgsByEmail} />
      case 'password':
        return (
          <PasswordPage
            hasPassword={hasPassword}
            onSetPassword={onSetPassword}
            onChangePassword={onChangePassword}
            onForgotPassword={onForgotPassword}
            onRetryHasPassword={onRetryHasPassword}
          />
        )
      case 'secure-storage':
        // 0179: a stale deep link on a keybook-disabled deployment is refused
        // in words — the same answer the /keybook deck and mobile route give —
        // rather than the bare-English placeholder below, which exists only
        // for a shell that never wired the page in.
        if (!keybookAvailable) {
          return (
            <div className="p-8 max-w-md">
              <h2 className="themed-text text-base font-medium mb-2">
                {t('keybookDisabled.title', { defaultValue: 'Keybook is not available' })}
              </h2>
              <p className="themed-text-secondary text-sm leading-relaxed">
                {t('keybookDisabled.body', {
                  defaultValue:
                    'Credential storage is switched off on this deployment, so there is no keybook page here. Everything else — your files, organizations and apps — works as usual.',
                })}
              </p>
            </div>
          )
        }
        return secureStoragePage || <div className="p-4 themed-text-muted text-sm">Secure storage not available</div>
      case 'blocked':
        return <BlockedInvitesPage onListBlocked={onListBlocked} onUnblock={onUnblock} />
      case 'connected-apps':
        return <ConnectedAppsPage connectionId={activeParam} onNavigate={(id) => navigateTo('connected-apps', id)} />
      case 'network':
        return connectionsPage || <div className="p-4 themed-text-muted text-sm">Connections page not available</div>
      case 'org-general':
        return <OrgGeneralPage tenantName={tenantName} tenantId={tenantId} userRole={userRole} orgEmail={orgEmail} onDeleteOrg={onDeleteOrg} onRenameOrg={onRenameOrg} />
      // `case 'permissions'` is gone — TEMPORARY, see `RETIRED_SETTINGS`. The
      // grid moved to the platform console (Customers → Configuration →
      // Permission defaults) and its routes moved with it.
      case 'members':
        return <MembersPage tenantId={tenantId} userEmail={orgEmail || user?.email} onPickFile={onPickFile} refreshKey={refreshKey} renderLeaveNotice={renderLeaveNotice} />
      case 'teams':
        return <TeamsPage tenantId={tenantId} userEmail={orgEmail || user?.email} onRevealInDrive={onRevealInDrive} activeTeamId={activeParam} activeTab={activeSubparam as 'members' | 'access' | undefined} onNavigate={(teamId, tab) => navigateTo('teams', teamId, tab)} createTeam={createTeam} />
      case 'links':
        return linksPage || <div className="p-4 themed-text-muted text-sm">Links page not available</div>
      // `/settings/usage` resolves to the SAME page as `/settings/budget`.
      //
      // The two nav items were one subject filed twice — Organization →
      // Spending and Account → Usage both rendered the member's own figures,
      // each a strict subset of the other. The Account item is gone; the ROUTE
      // is not, and must not be: it is bookmarked, it is what
      // `settings.billing.noAccessBody` names in all four locales, and it is
      // the else-branch of `onOpenBudgets` on both shells — so a member
      // clicking "View detailed usage" on the Billing page lands here.
      //
      // Falls back to `usagePage` for any host that still wires only that one.
      case 'usage':
        return budgetPage || usagePage || <div className="p-4 themed-text-muted text-sm">Usage page not available</div>
      // Two paths, two pages, gated by the two halves of `manage_budget`.
      // `/settings/limits` is linkable, bookmarked and what `/billing/limits`
      // rewrites onto, so it stays live and renders the cap editor.
      case 'budget':
        return budgetPage || <div className="p-4 themed-text-muted text-sm">Budget dashboard not available</div>
      case 'limits':
        return limitsPage || <div className="p-4 themed-text-muted text-sm">Budgets editor not available</div>
      // Passed unconditionally by both shells (same as `budgetPage`/
      // `limitsPage`), so an absent value is a "host didn't wire it" state —
      // the real, resolved-only refusal for a caller without
      // `view_file_audit:read` renders INSIDE the page itself.
      case 'file-audit':
        return fileActivityPage || <div className="p-4 themed-text-muted text-sm">File activity not available</div>
      // The one fallback here that a real user can reach. `budgetPage` and
      // `limitsPage` are passed unconditionally by both shells, so theirs are
      // structural "host didn't wire it" states; `billingPage` is null for any
      // caller without `manage_billing:read`, and `/settings/subscription` is
      // bookmarked and pasted into tickets. So it gets refusal copy in the
      // user's own language rather than a developer's English placeholder —
      // and the URL keeps telling the truth instead of silently redirecting,
      // the same stance `OrgBudgetDashboard` and `BudgetsEditor` take.
      // A denial is only shown once the grant is RESOLVED. While it is in
      // flight the host passes `billingPageLoading`, because `can()` reports
      // "still fetching" and "denied" as the same `false` — see the prop.
      case 'subscription':
        return billingPage || (billingPageLoading
          ? <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>
          : (
            <div className="p-4 space-y-1">
              <p className="text-sm themed-text font-medium">{t('settings.billing.noAccessTitle')}</p>
              <p className="text-xs themed-text-muted">{t('settings.billing.noAccessBody')}</p>
            </div>
          ))
      case 'theme':
        return <ThemePage />
      case 'editor':
        return <EditorPage />
      // `case 'language'` is gone — TEMPORARY, see `RETIRED_SETTINGS`.
      case 'storage':
        // Injected drive-usage breakdown (key reused from the retired static
        // App → Storage tab — 2026-08-05_SETTINGS_STORAGE_TAB.md §3).
        return storagePage || <div className="p-4 themed-text-muted text-sm">Storage page not available</div>
      // Dedicated version-retention settings page (#590 Task 9) — injected,
      // both scopes, but gated on `canManageVersionRetention` unlike
      // `storagePage`: a deep link from a non-admin must not render the page
      // (the page itself does not self-gate — the retention controls would
      // paint and only fail once a write is attempted).
      //
      // `can()` reports "still fetching" and "denied" as the SAME `false`, so
      // gating on `canManageVersionRetention` alone would show an ORG ADMIN the
      // refusal on every cold capabilities cache — the documented
      // can()-while-loading trap, reachable in the primary flow (the
      // VersionPanel "Manage" button deep-links straight here). Distinguish the
      // two exactly as the `subscription` case above does: show the page once
      // the grant RESOLVES true, a loading placeholder while it is in flight or
      // the answer is unavailable (a failed fetch is not a denial), and the
      // refusal ONLY once resolved-and-denied.
      case 'version-retention':
        if (canManageVersionRetention)
          return versionRetentionPage || <div className="p-4 themed-text-muted text-sm">Version retention page not available</div>
        if (capsLoading || capsUnavailable)
          return <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>
        return <div className="p-4 themed-text-muted text-sm">{t('settings.versionRetention.noAccess')}</div>
      case 'about':
        return <AboutPage />
      case 'devtools':
        return <DevToolsPage />
      default:
        return <ProfilePage user={user} onSignOut={onSignOut} />
    }
  }

  return (
    <div className="flex-1 min-h-0 min-w-0 themed-bg-primary flex flex-col">
      {/* Header */}
      <div className="h-12 border-b themed-border flex items-center px-4">
        <Settings size={20} className="themed-icon mr-2" />
        <span className="themed-text font-medium">{t('settings.title')}</span>
        {saving && (
          <Loader2 size={16} className="ml-2 themed-text-muted animate-spin" />
        )}
        {mobile && onClose && (
          <>
            <div className="flex-1" />
            <button
              onClick={onClose}
              aria-label="Close"
              title="Close"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors themed-text-muted hover:themed-bg-tertiary hover:themed-text"
            >
              <X size={14} />
            </button>
          </>
        )}
      </div>

      {/* Body: centered container with sidebar + content (+ scrollspy on
          desktop). The OUTER div is the one scroll container, so its
          scrollbar sits at the far right edge of the view; the sidebar and
          scrollspy are sticky self-start columns that stay put while the
          content scrolls past. It's also a size container: 100cqh inside it
          is exactly the scrollport height, letting the sticky columns cap
          themselves in pure CSS and scroll internally when taller. On mobile
          the nav becomes a sticky horizontal scrolling chip strip. */}
      <div
        ref={contentRef}
        className="flex-1 min-h-0 overflow-y-auto [container-type:size]"
      >
        {/* mx-auto (NOT a flex parent with justify-center): a flex row would
            stretch this wrapper to the scrollport height instead of the
            content height, capping how far the sticky columns can follow. */}
        <div className={`flex w-full mx-auto ${mobile ? 'max-w-5xl flex-col px-3 py-3 gap-3' : 'max-w-7xl px-6 gap-8'}`}>
        {/* Sidebar — no bg, no border */}
        <nav
          ref={mobile ? navRef : undefined}
          // #897: chrome, not content — and on the phone a sticky header the
          // restored position sits below.
          data-scroll-skip
          data-scroll-sticky={mobile ? '' : undefined}
          onPointerDown={mobile ? onNavPointerDown : undefined}
          onPointerMove={mobile ? onNavPointerMove : undefined}
          onPointerUp={mobile ? onNavPointerUp : undefined}
          onPointerCancel={mobile ? onNavPointerUp : undefined}
          onWheel={mobile ? onNavWheel : undefined}
          onClickCapture={mobile ? (e) => { if (navSuppressClick.current) { e.stopPropagation(); navSuppressClick.current = false } } : undefined}
          className={mobile
            ? 'flex shrink-0 items-center gap-1.5 overflow-x-auto pb-1 cursor-grab active:cursor-grabbing [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sticky top-0 z-10 themed-bg-primary'
            : 'w-[180px] shrink-0 py-7 sticky top-0 self-start max-h-[100cqh] overflow-y-auto'}>
          {/* ORGANIZATION section — only if isOrg, shown first */}
          {isOrg && (
            <>
              <SidebarSection label="Organization" mobile={mobile} />
              <SidebarItem icon={Building2} label="General" active={currentPage === 'org-general'} onClick={() => navigateTo('org-general')} mobile={mobile} />
              {/* The Permissions item was here, gated on `manage_member:write`.
                  Both it and the grid it opened moved to the platform console —
                  TEMPORARY, see the pointer where `PermissionsPage` used to be. */}
              <SidebarItem icon={UserSearch} label="Members" active={currentPage === 'members'} onClick={() => navigateTo('members')} mobile={mobile} />
              <SidebarItem icon={Users} label="Teams" active={currentPage === 'teams'} onClick={() => navigateTo('teams')} mobile={mobile} />
              <SidebarItem icon={Link2} label="Links" active={currentPage === 'links'} onClick={() => navigateTo('links')} mobile={mobile} />
              <SidebarItem icon={Network} label="Network" active={currentPage === 'network'} onClick={() => navigateTo('network')} mobile={mobile} />
              {/* This org's OWN spend. Back from the Usage & Billing shelf,
                  which is now platform-only: a tenant's budget is org
                  configuration and belongs with members, teams and permissions,
                  reachable by whoever holds the capability — exactly how every
                  other org setting already works.

                  UNGATED since 2026-08-07: `manage_budget:read` now picks the
                  VIEW rather than pass/fail. `SpendingPage` renders the org
                  dashboard for a holder and the member's own self-scoped spend
                  (`GET /orgs/:id/usage/me`, membership-only, no email param)
                  for everyone else. Gating the ITEM hid a page every member has
                  something to see on, and handed them refusal copy about a
                  question they had not asked. */}
              <SidebarItem icon={CircleDollarSign} label={t('settings.nav.spending')} active={currentPage === 'budget'} onClick={() => navigateTo('budget')} mobile={mobile} />
              {/* Storage composition — directly below Spending, but NOT behind
                  its capability: a byte total is a per-tenant fact every
                  member may see (the server gate is membership only), not
                  spend data (2026-08-05_SETTINGS_STORAGE_TAB.md §2.6/§4.1). */}
              {storagePage && (
                <SidebarItem icon={HardDrive} label={t('settings.nav.storage')} active={currentPage === 'storage'} onClick={() => navigateTo('storage')} mobile={mobile} />
              )}
              {/* Version retention (#590 Task 9) — split out of Storage. Unlike
                  Storage/Spending this IS behind a capability: the page lets an
                  admin change how long OTHER members' versions are kept, which
                  is `manage_org:write`, not a membership fact everyone may see.
                  `canManageVersionRetention` stays false while the grant is in
                  flight (same as `canWriteBudget`/`canViewFileAudit`), so the
                  item simply appears once resolved true. */}
              {versionRetentionPage && canManageVersionRetention && (
                <SidebarItem icon={Clock} label={t('settings.nav.versionRetention')} active={currentPage === 'version-retention'} onClick={() => navigateTo('version-retention')} mobile={mobile} />
              )}
              {canWriteBudget && (
                <SidebarItem icon={SlidersHorizontal} label={t('settings.nav.budget')} active={currentPage === 'limits'} onClick={() => navigateTo('limits')} mobile={mobile} />
              )}
              {/* File activity (#118, Task B8) — owner/admin only by default
                  (`view_file_audit`), so unlike Spending/Storage this item IS
                  behind its capability. `canViewFileAudit` stays false while
                  the grant is in flight (same as `canWriteBudget`), so the
                  item simply appears once resolved true rather than flashing
                  for a member who will be refused a moment later. */}
              {canViewFileAudit && (
                <SidebarItem icon={History} label={t('settings.nav.fileActivity')} active={currentPage === 'file-audit'} onClick={() => navigateTo('file-audit')} mobile={mobile} />
              )}
              {billingPage && (
                <SidebarItem icon={CreditCard} label={t('settings.nav.billing')} active={currentPage === 'subscription'} onClick={() => navigateTo('subscription')} mobile={mobile} />
              )}
            </>
          )}

          {/* ACCOUNT section — "Personal" inside an organization, where
              "Account" reads as the ORG's account rather than the person's.
              These four section headers are hardcoded English while every item
              beneath them goes through `t()`; that inconsistency predates this
              line and is deliberately left alone here. */}
          <SidebarSection label={isOrg ? 'Personal' : 'Account'} mobile={mobile} />
          <SidebarItem icon={UserIcon} label="Profile" active={currentPage === 'profile'} onClick={() => navigateTo('profile')} mobile={mobile} />
          <SidebarItem icon={Mail} label="Emails" active={currentPage === 'emails'} onClick={() => navigateTo('emails')} mobile={mobile} />
          <SidebarItem icon={Key} label="Password" active={currentPage === 'password'} onClick={() => navigateTo('password')} mobile={mobile} />
          {/* §8 hides Secure Storage in an identity-only tenant: the keybook
              is tenant-scoped storage, and this tenant has none. */}
          {/* 0179 hides it when the deployment has the keybook off. */}
          {!identityOnly && keybookAvailable && (
            <SidebarItem icon={Lock} label="Secure Storage" active={currentPage === 'secure-storage'} onClick={() => navigateTo('secure-storage')} mobile={mobile} />
          )}
          <SidebarItem icon={ShieldBan} label="Blocked" active={currentPage === 'blocked'} onClick={() => navigateTo('blocked')} mobile={mobile} />
          {/* The apps a person connected over MCP (Claude…) — replaced Developer tokens before either shipped (2026-10-16). */}
          <SidebarItem icon={Plug} label={t('settings.connectedApps.nav')} active={currentPage === 'connected-apps'} onClick={() => navigateTo('connected-apps')} mobile={mobile} />
          {/* The Account → Usage item lived here and is gone.
              It rendered the same component as Organization → Spending, and a
              member therefore had two sidebar entries for one subject. The
              pages have been merged (see the `case 'usage'` note above), so the
              second door was removed rather than the content. The ROUTE still
              resolves. */}
          {!isOrg && (
            <>
              {/* §8 hides Links and Network in an identity-only tenant. Both are
                  about sharing this tenant's OWN content — there is none, and a
                  link list that can only ever be empty reads as broken rather
                  than as configured. The ORGANIZATION block above keeps its
                  copies unconditionally: an org is never identity-only. */}
              {!identityOnly && (
                <>
                  <SidebarItem icon={Link2} label="Links" active={currentPage === 'links'} onClick={() => navigateTo('links')} mobile={mobile} />
                  <SidebarItem icon={Network} label="Network" active={currentPage === 'network'} onClick={() => navigateTo('network')} mobile={mobile} />
                </>
              )}
              {/* Spending renders here too (2026-08-07). The old note said it
                  deliberately did NOT, because a personal drive has no roles,
                  teams or members to allocate a budget across — that reasoning
                  was about the per-member and per-team TABLES and still holds,
                  which is why **Budget** (the cap editor) is still absent below.
                  It says nothing against a self-scoped spend view.

                  `SpendingPage` resolves here to `OrgBudgetDashboard` in
                  `personal` mode — NOT `MemberUsageView`, which is what this
                  note claimed until 2026-08-10. The distinction matters to
                  anyone reasoning from here: the two render different sections,
                  read different endpoints (`/usage` vs `/usage/me`) and
                  therefore report different totals. Check `SpendingPage`
                  itself before assuming which one is on screen.

                  Hidden in an identity-only tenant: there is no drive, no
                  workspace and no agent to spend on, so this reports zero
                  forever. The `!identityOnly` here is what hides the ITEM; the
                  URL is closed by `IDENTITY_ONLY_HIDDEN_SETTINGS`, which the
                  `'usage'` alias and `'limits'` need anyway. */}
              {!identityOnly && (
                <SidebarItem icon={CircleDollarSign} label={t('settings.nav.spending')} active={currentPage === 'budget'} onClick={() => navigateTo('budget')} mobile={mobile} />
              )}
              {/* A personal drive is its own billing owner, so Billing renders
                  here as well as in the Organization block above — except in an
                  identity-only tenant, where the billing WRITES are gated
                  server-side and there is nothing to buy. See the long note on
                  `IDENTITY_ONLY_HIDDEN_SETTINGS` for why hiding a billing page
                  is safe HERE and would not be in general. */}
              {billingPage && !identityOnly && (
                <SidebarItem icon={CreditCard} label={t('settings.nav.billing')} active={currentPage === 'subscription'} onClick={() => navigateTo('subscription')} mobile={mobile} />
              )}
              {/* Personal-drive mirror of the Organization block's Storage item
                  (§3A) — same page key, same component. This mirror is what
                  keeps retiring the old static App → Storage tab from being a
                  regression for personal drives. */}
              {/* And Storage: a byte breakdown of a drive this tenant does
                  not have. */}
              {storagePage && !identityOnly && (
                <SidebarItem icon={HardDrive} label={t('settings.nav.storage')} active={currentPage === 'storage'} onClick={() => navigateTo('storage')} mobile={mobile} />
              )}
              {/* Personal-drive mirror of the Organization block's Version
                  retention item — hidden in identity-only mode (§8/0175, this
                  tenant has no drive to retain versions on) AND behind the
                  same `manage_org:write` gate as the org block (the owner's
                  own personal drive resolves true; see the flag's comment). */}
              {versionRetentionPage && !identityOnly && canManageVersionRetention && (
                <SidebarItem icon={Clock} label={t('settings.nav.versionRetention')} active={currentPage === 'version-retention'} onClick={() => navigateTo('version-retention')} mobile={mobile} />
              )}
            </>
          )}

          {/* APP section — its old static Storage tab was retired 2026-08-05;
              "Storage" now lives in the Organization / personal-Account blocks
              above as the injected drive-usage breakdown. */}
          <SidebarSection label="App" mobile={mobile} />
          <SidebarItem icon={Palette} label="Theme" active={currentPage === 'theme'} onClick={() => navigateTo('theme')} mobile={mobile} />
          <SidebarItem icon={Code2} label={t('settings.editor.title')} active={currentPage === 'editor'} onClick={() => navigateTo('editor')} mobile={mobile} />
          {/* The Language item was here — removed with its page. TEMPORARY; see
              the pointer where `LanguagePage` used to be. */}
          <SidebarItem icon={Info} label="About" active={currentPage === 'about'} onClick={() => navigateTo('about')} mobile={mobile} />

          {/* Platform budget pages (Tenant Budgets, Feature Pricing) moved to
              the "Usage & Billing" taskbar surface (BudgetBillingView). */}

          {/* DEV section — only in dev mode */}
          {isDev && (
            <>
              <SidebarSection label="Dev" mobile={mobile} />
              <SidebarItem icon={Bug} label="Dev Tools" active={currentPage === 'devtools'} onClick={() => navigateTo('devtools')} mobile={mobile} />
            </>
          )}
        </nav>

        {/* Content area — wide, no extra container. Scrolls with the outer
            body container; the flanking sticky columns stay put. No overflow
            of its own, so borders/rings at its edges never get clipped. */}
        <div className={`flex-1 min-w-0 ${mobile ? '' : 'py-6'}`}>
          {renderPage()}
        </div>

        {/* Right-hand outline of the current page's headings. */}
        {!mobile && <ScrollSpy containerRef={contentRef} pageKey={currentPage} />}
        </div>
      </div>
    </div>
  )
}
